import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { StockTableLayout, Statistic } from '../../components/StockTable';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { useAuth } from '../../contexts/AuthContext';
import { TickerDisplayData } from '../../types/ticker';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import TabBar, { TabType } from '../../components/Common/TabBar';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getSmallTextSizeClass, getStatisticLabelSizeClass, getDisplayTextSizeClass } from '../../utils/fontSizeUtils';
import { useTickerSnapshot } from '../../hooks/useTickerSnapshot';
import { searchApiService } from '../../services/searchApi';
import { GoldenCrossIcon, DeathCrossIcon, SupportIcon, ResistanceIcon, UpArrowIcon, DownArrowIcon } from '../../components/Icons';
import { transformAnalystViewsData } from '../../hooks/useAnalystViews';
import { AnalystViewDisplayData } from '../../types/analystViews';
import { useDailyTabStatistics, formatDailyStatisticValue, getDailyStatisticColor } from '../../hooks/useDailyTabStatistics';
import { DAILY_STATISTICS, getCachedLabel, getLatestLabel, getOpenLabel } from '../../utils/dailyTabUtils';
import { TECHNICAL_STATISTICS, CATEGORY_HEADERS, TickerTechnicalData, MovingAverageData } from '../../utils/technicalsTabUtils';
import { formatTechnicalsStatisticValue, getTechnicalsStatisticColor } from '../../hooks/useTechnicalsTabStatistics';
import { FUNDAMENTAL_STATISTICS, ANNUAL_FUNDAMENTAL_STATISTICS, FUNDAMENTAL_CATEGORY_HEADERS, ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS } from '../../utils/fundamentalsTabUtils';
import { formatFundamentalsStatisticValue, getFundamentalsStatisticColor } from '../../hooks/useFundamentalsTabStatistics';
import { ANALYST_VIEWS_STATISTICS, ANALYST_VIEWS_CATEGORY_HEADERS } from '../../utils/analystViewsTabUtils';
import { formatAnalystViewsStatisticValue, getAnalystViewsStatisticColor } from '../../hooks/useAnalystViewsTabStatistics';
import { formatPerformanceStatisticValue, getPerformanceStatisticColor } from '../../hooks/usePerformanceTabStatistics';
import {
  PERFORMANCE_STATISTICS,
  CATEGORY_HEADERS as PERFORMANCE_CATEGORY_HEADERS,
  TickerPerformanceData,
  PerformanceRow,
} from '../../utils/performanceTabUtils';
import { INSIDER_TRANSACTIONS_STATISTICS, INSIDER_TRANSACTIONS_CATEGORY_HEADERS } from '../../utils/insiderTransactionsTabUtils';
import {
  PROS_CONS_STATISTICS,
  prosConsLineArrays,
  ProsConsNumberedList,
  getProsConsIndicatorsColumnWidthPx,
  getProsConsHistoryMonthColumnWidthPx,
} from '../../utils/prosConsTabUtils';
import { TickerPast4QuartersAnalystViewsData, QuarterData, TickerAnalystView } from '../../types/analystViews';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import CategoryInfoButton from '../../components/CategoryInfoButton';
import IndicatorInfoButton from '../../components/IndicatorInfoButton';
import {
  formatQuarterHeader,
  formatAnalystViewsDate,
  transformAnalystViewsData as transformAnalystViewsDataForTicker,
  formatAnalystViewsValue,
  getAnalystViewsStatisticColor as getAnalystViewsTickerStatisticColor,
  getAnalystViewsQuarterData,
} from '../../utils/tickerAnalystViewsUtils';
import { DailyPriceTable, DailyPriceTableFullScreen, TimeframeDropdown, TickerDailyData } from '../../components/DailyPriceTable';
import { useFullScreen } from '../../hooks/useFullScreen';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { Timeframe } from '../../utils/dailyPriceUtils';
import { formatNumber, getBollingerBandIcon, getFibonacciIcon } from '../../utils/technicalsTabUtils';
import {
  TECHNICALS_DATE_COLUMNS,
  createIndicatorAccessors,
  hasGoldenCross,
  hasDeathCross,
  getTechnicalsValueColor,
  buildTickerTechnicalDataForIcons,
  formatTechnicalsStatisticValue as formatTechnicalsStatisticValueFromUtils,
  getCrossIcon,
  type TickerTechnicalDataFromAPI,
  type DailyPriceData,
} from '../../utils/tickerTechnicalsUtils';
import {
  generateQuarterlyColumns as generateQuarterlyColumnsUtil,
  generateAnnualColumns as generateAnnualColumnsUtil,
  getFundamentalsValue as getFundamentalsValueUtil,
  formatFundamentalsValue as formatFundamentalsValueUtil,
  getFundamentalsArrowInfo as getFundamentalsArrowInfoUtil,
  getFundamentalsArrowTooltip as getFundamentalsArrowTooltipUtil,
} from '../../utils/tickerFundamentalsUtils';

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

const MONTH_ABBR_DOT_PROS = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

function formatLandingProsConsMonthYear(year: number | null, month: number | null): string {
  if (!year || !month || month < 1 || month > 12) return '-';
  return `${MONTH_ABBR_DOT_PROS[month - 1]} ${year}`;
}

type PerformanceHistoryRow = {
  tickerSymbol: string;
  tickerBasicsId: number;
  date: string | Date | null;
  ytdReturn: number | null;
  oneMReturn: number | null;
  threeMReturn: number | null;
  sixMReturn: number | null;
  nineMReturn: number | null;
  oneYReturn: number | null;
  twoYReturn: number | null;
  threeYReturn: number | null;
  fourYReturn: number | null;
  fiveYReturn: number | null;
};

type LandingProsConsHistoryItem = {
  year: number | null;
  month: number | null;
  prosCons: {
    pro_1: string | null;
    pro_2: string | null;
    pro_3: string | null;
    pro_4: string | null;
    pro_5: string | null;
    con_1: string | null;
    con_2: string | null;
    con_3: string | null;
    con_4: string | null;
    con_5: string | null;
  } | null;
};



interface TickerFundamentalsData {
  [tickerSymbol: string]: TickerDisplayData;
}

const Landing: React.FC = () => {
  const { fontSize } = useFontSize();
  const { isAuthenticated } = useAuth();
  const prosConsHistoryApi = useMemo(
    () => (isAuthenticated ? userTickerApi : tickerApi),
    [isAuthenticated],
  );

  const performanceApi = useMemo(
    () => (isAuthenticated ? userTickerApi : tickerApi),
    [isAuthenticated],
  );
  
  useEffect(() => {
    document.title = 'Stock Table - Instant Stock Evaluation';
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const [tickerList] = useState<string[]>(DEFAULT_TICKERS);
  const [activeTab, setActiveTab] = useState<TabType>('daily');
  const tableSectionRef = useRef<HTMLElement>(null);
  const [tickerTechnicalData, setTickerTechnicalData] = useState<TickerTechnicalData>({});
  const [tickerFundamentalsData, setTickerFundamentalsData] = useState<TickerFundamentalsData>({});
  const [tickerNames, setTickerNames] = useState<{ [symbol: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false); // Start with false, will be set to true when fetching
  const [error, setError] = useState<string | null>(null);
  const [hiddenStatistics] = useState<string[]>([]);
  
  // Market status state (same as ListDaily)
  const [marketStatus, setMarketStatus] = useState<'open' | 'pre-market' | 'after-hours' | 'closed' | null>(null);
  const [marketOpenTimestamp, setMarketOpenTimestamp] = useState<string | null>(null);
  const [marketCloseTimestamp, setMarketCloseTimestamp] = useState<string | null>(null);
  const [marketPreMarketTimestamp, setMarketPreMarketTimestamp] = useState<string | null>(null);
  const [cachedLatestLabel, setCachedLatestLabel] = useState<string>(getCachedLabel());
  const [cachedOpenLabel, setCachedOpenLabel] = useState<string>(() => {
    const cached = localStorage.getItem('stocktable-open-label');
    return cached || 'Open';
  });
  const [previousMarketStatus, setPreviousMarketStatus] = useState<'open' | 'pre-market' | 'after-hours' | 'closed' | null>(null);
  const [marketStatusLoading, setMarketStatusLoading] = useState(false);
  
  // Timeframe for fundamentals (default to quarterly like ListFundamentals)
  const [timeframe, setTimeframe] = useState<'quarterly' | 'annual'>('quarterly');
  
  // Data for different tabs
  const [analystViewsData, setAnalystViewsData] = useState<AnalystViewDisplayData[]>([]);
  const [analystViewsPriceData, setAnalystViewsPriceData] = useState<{ [symbol: string]: any }>({});
  const [nextEarningsDates, setNextEarningsDates] = useState<Record<string, Date | null>>({});
  const [insiderData, setInsiderData] = useState<Record<string, any>>({});
  const [prosConsRows, setProsConsRows] = useState<Record<string, any>>({});
  const [prosConsSyncingTickers, setProsConsSyncingTickers] = useState<Set<string>>(new Set());

  const [tickerPerformanceData, setTickerPerformanceData] = useState<TickerPerformanceData>({});
  const [tickerPerformanceBasicsMap, setTickerPerformanceBasicsMap] = useState<Record<string, { id: number; ticker_name: string }>>({});
  const [performanceSyncingTickers, setPerformanceSyncingTickers] = useState<Set<string>>(new Set());
  const [performanceUnsyncedTickers, setPerformanceUnsyncedTickers] = useState<Set<string>>(new Set());
  const [performancePollingLimitReached, setPerformancePollingLimitReached] = useState<Set<string>>(new Set());
  
  // Refetch trigger state
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  
  // Shared state for current embedded ticker info (persists across tab switches)
  const [currentEmbeddedTickerName, setCurrentEmbeddedTickerName] = useState<string>('');
  const [currentEmbeddedTickerSymbol, setCurrentEmbeddedTickerSymbol] = useState<string>('');
  
  // State for embedded daily ticker view
  const [selectedTickerForDailyView, setSelectedTickerForDailyView] = useState<string | null>(null);
  const [dailyTickerData, setDailyTickerData] = useState<any>(null);
  const [dailyTickerLoading, setDailyTickerLoading] = useState(false);
  const [dailyTickerError, setDailyTickerError] = useState<string | null>(null);
  const [dailyTickerLastUpdated, setDailyTickerLastUpdated] = useState<Date | null>(null);
  const [dailyTimeframe, setDailyTimeframe] = useState<Timeframe>('1year');
  const [dailySortColumn, setDailySortColumn] = useState<'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted'>('date');
  const [dailySortDirection, setDailySortDirection] = useState<'asc' | 'desc'>('desc');
  const [allDailyPrices, setAllDailyPrices] = useState<any[]>([]);
  const [hasMoreDailyData, setHasMoreDailyData] = useState(true);
  const [loadingMoreDailyData, setLoadingMoreDailyData] = useState(false);
  const dailyTableRef = useRef<HTMLDivElement>(null);
  const dailyCurrentPageRef = useRef(0);
  const [isDailyDropdownOpen, setIsDailyDropdownOpen] = useState(false);
  const [isDailyFullScreen, setIsDailyFullScreen] = useState(false);
  const dailyFullScreenTableRef = useRef<HTMLDivElement>(null);
  
  // State for embedded technicals ticker view
  const [selectedTickerForTechnicalsView, setSelectedTickerForTechnicalsView] = useState<string | null>(null);
  const [technicalsTickerData, setTechnicalsTickerData] = useState<any>(null);
  const [technicalsTickerLoading, setTechnicalsTickerLoading] = useState(false);
  const [technicalsTickerError, setTechnicalsTickerError] = useState<string | null>(null);
  const [technicalsTickerLastUpdated, setTechnicalsTickerLastUpdated] = useState<Date | null>(null);
  const [technicalsDates, setTechnicalsDates] = useState<{ [daysAgo: number]: string }>({});
  const [technicalsDailyPrices, setTechnicalsDailyPrices] = useState<{ [daysAgo: number]: any }>({});
  const [technicalsTickerName, setTechnicalsTickerName] = useState<string>('');
  const [technicalsTickerSymbol, setTechnicalsTickerSymbol] = useState<string>('');
  const [isTechnicalsFullScreen, setIsTechnicalsFullScreen] = useState(false);
  const technicalsFullScreenTableRef = useRef<HTMLDivElement>(null);

  const [selectedTickerForPerformanceView, setSelectedTickerForPerformanceView] = useState<string | null>(null);
  const [performanceEmbeddedHistory, setPerformanceEmbeddedHistory] = useState<PerformanceHistoryRow[]>([]);
  const [performanceEmbeddedLoading, setPerformanceEmbeddedLoading] = useState(false);
  const [performanceEmbeddedError, setPerformanceEmbeddedError] = useState<string | null>(null);
  const [performanceEmbeddedLastUpdated, setPerformanceEmbeddedLastUpdated] = useState<Date | null>(null);
  const [isPerformanceFullScreen, setIsPerformanceFullScreen] = useState(false);
  const [performanceEmbeddedIsSyncing, setPerformanceEmbeddedIsSyncing] = useState(false);
  const [performanceEmbeddedPollingLimit, setPerformanceEmbeddedPollingLimit] = useState(false);
  const performanceEmbeddedPollingRef = useRef<NodeJS.Timeout | null>(null);
  const performanceEmbeddedCheckingRef = useRef(false);
  const performanceEmbeddedAttemptRef = useRef(1);
  const performanceEmbeddedStoppedRef = useRef(false);
  const performanceFullScreenTableRef = useRef<HTMLDivElement>(null);
  const openPerformanceEmbeddedViewRef = useRef<((tickerSymbol: string) => void) | null>(null);
  
  // State for embedded fundamentals ticker view
  const [selectedTickerForFundamentalsView, setSelectedTickerForFundamentalsView] = useState<string | null>(null);
  const [fundamentalsTickerLoading, setFundamentalsTickerLoading] = useState(false);
  const [fundamentalsTickerError, setFundamentalsTickerError] = useState<string | null>(null);
  const [fundamentalsTickerLastUpdated, setFundamentalsTickerLastUpdated] = useState<Date | null>(null);
  const [fundamentalsTickerName, setFundamentalsTickerName] = useState<string>('');
  const [fundamentalsTickerSymbol, setFundamentalsTickerSymbol] = useState<string>('');
  const [fundamentalsTickerBasicsId, setFundamentalsTickerBasicsId] = useState<number | null>(null);
  const [fundamentalsTimeframe, setFundamentalsTimeframe] = useState<'quarterly' | 'annual'>('quarterly');
  const [fundamentalsQuarterlyFinancials, setFundamentalsQuarterlyFinancials] = useState<any[]>([]);
  const [fundamentalsQuarterlyRatios, setFundamentalsQuarterlyRatios] = useState<any[]>([]);
  const [fundamentalsAnnualFinancials, setFundamentalsAnnualFinancials] = useState<any[]>([]);
  const [fundamentalsAnnualRatios, setFundamentalsAnnualRatios] = useState<any[]>([]);
  const [fundamentalsQuarterlyColumns, setFundamentalsQuarterlyColumns] = useState<Array<{ label: string; date: string; year: number; quarter: number }>>([]);
  const [fundamentalsAnnualColumns, setFundamentalsAnnualColumns] = useState<Array<{ label: string; date: string; year: number }>>([]);
  const [fundamentalsIsSyncing, setFundamentalsIsSyncing] = useState(false);
  const [fundamentalsSyncPollingAttempts, setFundamentalsSyncPollingAttempts] = useState<number>(0);
  const [fundamentalsPollingLimitReached, setFundamentalsPollingLimitReached] = useState(false);
  const fundamentalsPollingStoppedRef = useRef(false);
  const fundamentalsSyncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fundamentalsIsCheckingSyncRef = useRef(false);
  const fundamentalsCurrentAttemptRef = useRef(1);
  const [isFundamentalsFullScreen, setIsFundamentalsFullScreen] = useState(false);
  const fundamentalsFullScreenTableRef = useRef<HTMLDivElement>(null);
  
  // State for embedded analyst views ticker view
  const [selectedTickerForAnalystViewsView, setSelectedTickerForAnalystViewsView] = useState<string | null>(null);
  const [analystViewsTickerLoading, setAnalystViewsTickerLoading] = useState(false);
  const [analystViewsTickerError, setAnalystViewsTickerError] = useState<string | null>(null);
  const [analystViewsTickerLastUpdated, setAnalystViewsTickerLastUpdated] = useState<Date | null>(null);
  const [analystViewsTickerName, setAnalystViewsTickerName] = useState<string>('');
  const [analystViewsTickerSymbol, setAnalystViewsTickerSymbol] = useState<string>('');
  const [analystViewsTickerBasicsId, setAnalystViewsTickerBasicsId] = useState<number | null>(null);
  const [analystViewsTickerData, setAnalystViewsTickerData] = useState<TickerPast4QuartersAnalystViewsData | null>(null);
  const [analystViewsTickerPriceData, setAnalystViewsTickerPriceData] = useState<any>(null);
  const [analystViewsLatestDate, setAnalystViewsLatestDate] = useState<string | null>(null);
  const [analystViewsNextEarningsDate, setAnalystViewsNextEarningsDate] = useState<Date | null>(null);
  const [analystViewsIsSyncing, setAnalystViewsIsSyncing] = useState(false);
  const [analystViewsSyncPollingAttempts, setAnalystViewsSyncPollingAttempts] = useState<number>(0);
  const [analystViewsPollingLimitReached, setAnalystViewsPollingLimitReached] = useState(false);
  const analystViewsPollingStoppedRef = useRef(false);
  const analystViewsSyncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const analystViewsIsCheckingSyncRef = useRef(false);
  const analystViewsCurrentAttemptRef = useRef(1);
  const [analystViewsMarketType, setAnalystViewsMarketType] = useState<string | null>(null);
  const [isAnalystViewsFullScreen, setIsAnalystViewsFullScreen] = useState(false);
  const analystViewsFullScreenTableRef = useRef<HTMLDivElement>(null);
  const [isAnalystViewsModalOpen, setIsAnalystViewsModalOpen] = useState(false);
  const [selectedAnalystViewsCategory, setSelectedAnalystViewsCategory] = useState<string>('');
  const [isAnalystViewsIndicatorModalOpen, setIsAnalystViewsIndicatorModalOpen] = useState(false);
  const [selectedAnalystViewsIndicatorKey, setSelectedAnalystViewsIndicatorKey] = useState<string>('');
  
  // State for embedded insiders ticker view
  const [selectedTickerForInsidersView, setSelectedTickerForInsidersView] = useState<string | null>(null);
  const [insidersTickerLoading, setInsidersTickerLoading] = useState(false);
  const [insidersTickerError, setInsidersTickerError] = useState<string | null>(null);
  const [insidersTickerLastUpdated, setInsidersTickerLastUpdated] = useState<Date | null>(null);
  const [insidersTickerName, setInsidersTickerName] = useState<string>('');
  const [insidersTickerSymbol, setInsidersTickerSymbol] = useState<string>('');
  const [insidersTickerBasicsId, setInsidersTickerBasicsId] = useState<number | null>(null);
  const [insidersTickerData, setInsidersTickerData] = useState<any>(null);
  const [insidersIsSyncing, setInsidersIsSyncing] = useState(false);
  const [insidersSyncPollingAttempts, setInsidersSyncPollingAttempts] = useState<number>(0);
  const [insidersPollingLimitReached, setInsidersPollingLimitReached] = useState(false);
  const insidersPollingStoppedRef = useRef(false);
  const insidersSyncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const insidersIsCheckingSyncRef = useRef(false);
  const insidersCurrentAttemptRef = useRef(1);
  const [isInsidersFullScreen, setIsInsidersFullScreen] = useState(false);
  const insidersFullScreenTableRef = useRef<HTMLDivElement>(null);
  const [isProsConsFullScreen, setIsProsConsFullScreen] = useState(false);
  const prosConsFullScreenTableRef = useRef<HTMLDivElement>(null);
  const [isInsidersCategoryModalOpen, setIsInsidersCategoryModalOpen] = useState(false);
  const [selectedInsidersCategory, setSelectedInsidersCategory] = useState<string>('');

  // Embedded Pros & Cons (Featured Stocks — full pros/cons text; API uses ownerHasProSubscription for history)
  const [selectedTickerForProsConsView, setSelectedTickerForProsConsView] = useState<string | null>(null);
  const [prosConsTickerLoading, setProsConsTickerLoading] = useState(false);
  const [prosConsTickerError, setProsConsTickerError] = useState<string | null>(null);
  const [prosConsTickerLastUpdated, setProsConsTickerLastUpdated] = useState<Date | null>(null);
  const [prosConsTickerName, setProsConsTickerName] = useState('');
  const [prosConsTickerSymbol, setProsConsTickerSymbol] = useState('');
  const [prosConsTickerBasicsId, setProsConsTickerBasicsId] = useState<number | null>(null);
  const [prosConsHistory, setProsConsHistory] = useState<LandingProsConsHistoryItem[]>([]);
  const [prosConsIsSyncing, setProsConsIsSyncing] = useState(false);
  const prosConsPollingStoppedRef = useRef(false);
  const prosConsSyncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const prosConsCurrentAttemptRef = useRef(1);
  const prosConsIsCheckingSyncRef = useRef(false);
  const prosConsResolvedIdRef = useRef<number | null>(null);
  const prosConsTableScrollRef = useRef<HTMLDivElement>(null);
  
  // Fetch market status when component loads or refreshes (same as ListDaily)
  const fetchMarketStatus = async () => {
    try {
      setMarketStatusLoading(true);
      const response = await searchApiService.getMarketStatus();
      const newMarketStatus = response.data.market_status;
      const serverMarketOpenTimestamp = response.data.market_open_timestamp;
      const serverMarketCloseTimestamp = response.data.market_close_timestamp;
      const serverMarketPreMarketTimestamp = response.data.market_pre_market_timestamp;
      
      // Use server-provided timestamps if available, otherwise track client-side as fallback
      if (serverMarketOpenTimestamp) {
        setMarketOpenTimestamp(serverMarketOpenTimestamp);
        // Also store in localStorage as fallback
        const timestamp = new Date(serverMarketOpenTimestamp).getTime();
        if (!isNaN(timestamp)) {
          localStorage.setItem('stocktable-market-open-timestamp', timestamp.toString());
        }
      } else if (previousMarketStatus === 'closed' && newMarketStatus === 'open') {
        // Fallback: Track when market status changes from 'closed' to 'open' on client
        const clientTimestamp = Date.now();
        setMarketOpenTimestamp(new Date(clientTimestamp).toISOString());
        localStorage.setItem('stocktable-market-open-timestamp', clientTimestamp.toString());
      }
      
      // Set close timestamp from server
      if (serverMarketCloseTimestamp) {
        setMarketCloseTimestamp(serverMarketCloseTimestamp);
      } else if (previousMarketStatus === 'open' && (newMarketStatus === 'closed' || newMarketStatus === 'after-hours')) {
        // Fallback: Track when market status changes from 'open' to 'closed'/'after-hours' on client
        const clientTimestamp = Date.now();
        setMarketCloseTimestamp(new Date(clientTimestamp).toISOString());
      }
      
      // Set pre-market timestamp from server
      if (serverMarketPreMarketTimestamp) {
        setMarketPreMarketTimestamp(serverMarketPreMarketTimestamp);
      } else if (previousMarketStatus !== 'pre-market' && newMarketStatus === 'pre-market') {
        // Fallback: Track when market status changes to 'pre-market' on client
        const clientTimestamp = Date.now();
        setMarketPreMarketTimestamp(new Date(clientTimestamp).toISOString());
      }
      
      setPreviousMarketStatus(newMarketStatus);
      setMarketStatus(newMarketStatus);
    } catch (error) {
      console.error('Failed to fetch market status:', error);
      setMarketStatus(null);
    } finally {
      setMarketStatusLoading(false);
    }
  };

  // Fetch market status on component mount
  useEffect(() => {
    fetchMarketStatus();
  }, []);

  // Use ticker snapshot hook for Daily tab (same as ListDaily)
  // Always pass tickerList - hook will handle empty array gracefully
  const { data: snapshotData, loading: snapshotLoading, error: snapshotError, lastUpdated: snapshotLastUpdated, refetch: refetchSnapshot } = useTickerSnapshot(
    tickerList,
    false, // isAuthenticated - false for landing page
    marketStatus || undefined
  );
  
  // State for last updated time (for Technicals, Fundamentals, Analysts tabs)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Handle refetch
  const handleRefetch = useCallback(() => {
    if (activeTab === 'daily') {
      // For Daily tab, use the snapshot refetch
      refetchSnapshot();
    } else {
      // For other tabs, trigger refetch by incrementing trigger
      setRefetchTrigger(prev => prev + 1);
    }
  }, [activeTab, refetchSnapshot]);

  // Check ticker sync status to get ticker names (same sequence as ListDaily)
  const checkSyncStatus = useCallback(async (tickersToCheck: string[]) => {
    if (tickersToCheck.length === 0) return;

    try {
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: tickersToCheck,
      });
      
      // Store ticker names from sync response (same as ListDaily)
      const newTickerNames: { [symbol: string]: string } = {};
      syncResponse.data.tickers.forEach((ticker: any) => {
        if (ticker.ticker_name) {
          newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
          sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
        }
        if (ticker.ticker_symbol) {
          sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
        }
      });
      setTickerNames(prev => ({ ...prev, ...newTickerNames }));
    } catch (error) {
      console.error('Error checking sync status:', error);
    }
  }, []);

  // Check sync status when tickerList changes (for Daily tab, same as ListDaily)
  useEffect(() => {
    if (tickerList.length === 0) {
      return;
    }
    
    // Check sync status for all tickers in the list (same as ListDaily)
    checkSyncStatus(tickerList);
  }, [tickerList, checkSyncStatus]);

  // Fetch fundamental data following the same API sequence as ListFundamentals
  const fetchFundamentalData = useCallback(async (tickerSymbols: string[], syncResponseData?: any) => {
    if (tickerSymbols.length === 0) return;

    try {
      console.log(`Landing: Starting fundamental data fetch for: ${tickerSymbols.join(', ')}`);

      // Check if using default tickers - use cached endpoint
      const isDefaultTickers = JSON.stringify([...tickerSymbols].sort()) === JSON.stringify([...DEFAULT_TICKERS].sort());
      
      if (isDefaultTickers) {
        console.log('Landing: Using cached fundamentals endpoint for default tickers');
        try {
          const cachedResponse = await tickerApi.landingCache.getCachedFundamentals(timeframe);
          
          if (cachedResponse.success && cachedResponse.data) {
            const { currentQuarterRatios, ratiosComparison, financialsComparison, syncResponse: cachedSyncResponse } = cachedResponse.data;
            syncResponseData = cachedSyncResponse || syncResponseData;
            
            // Process cached data (same logic as below)
            const newFundamentalsData: TickerFundamentalsData = {};
            const tickerStatuses = syncResponseData 
              ? syncResponseData.tickers.filter((ticker: any) => 
                  ticker.is_synced === true && tickerSymbols.includes(ticker.ticker_symbol)
                )
              : [];
            
            if (tickerStatuses.length === 0) {
              console.log(`Landing: No synced tickers found in cached syncResponseData`);
              return;
            }
            
            // Process cached data similar to the original logic
            tickerStatuses.forEach((tickerStatus: any) => {
              const symbol = tickerStatus.ticker_symbol;
              const tickerId = tickerStatus.ticker_basics_id;
              const currentRatiosData = currentQuarterRatios.find((r: any) => r.tickerBasicsId === tickerId);
              const ratiosData = ratiosComparison.find((r: any) => r.tickerBasicsId === tickerId);
              const financialsData = financialsComparison.find((f: any) => f.tickerBasicsId === tickerId);
              
              // Create a map of all fundamental values (same processing logic as original)
              const fundamentalValues: { [key: string]: number } = {};
              const comparisonData: { [key: string]: any } = {};
              
              if (timeframe === 'quarterly') {
                if (currentRatiosData) {
                  currentRatiosData.ratios?.forEach((ratio: any) => {
                    fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
                  });
                }
                
                if (ratiosData) {
                  ratiosData.latestQuarter?.forEach((ratio: any) => {
                    const currentValue = currentRatiosData?.ratios?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue;
                    if (currentValue !== undefined) {
                      comparisonData[ratio.ratioKey] = {
                        latest: currentValue,
                        previous: ratio.ratioValue,
                        sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                      };
                    } else {
                      fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
                      comparisonData[ratio.ratioKey] = {
                        latest: ratio.ratioValue,
                        previous: ratiosData.previousQuarter?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                        sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                      };
                    }
                  });
                }
                
                if (financialsData) {
                  financialsData.latestQuarter?.forEach((financial: any) => {
                    fundamentalValues[financial.statsKey] = financial.statsValue;
                    comparisonData[financial.statsKey] = {
                      latest: financial.statsValue,
                      previous: financialsData.previousQuarter?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                      sameQuarterLastYear: financialsData.sameQuarterLastYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                    };
                  });
                }
              } else {
                // Annual timeframe processing
                if (ratiosData && ratiosData.latestYear) {
                  ratiosData.latestYear.forEach((ratio: any) => {
                    fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
                    comparisonData[ratio.ratioKey] = {
                      latest: ratio.ratioValue,
                      previous: ratiosData.previousYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                      sameQuarterLastYear: undefined, // Not used for annual data
                    };
                  });
                }
                
                if (financialsData && financialsData.latestYear) {
                  financialsData.latestYear.forEach((financial: any) => {
                    fundamentalValues[financial.statsKey] = financial.statsValue;
                    comparisonData[financial.statsKey] = {
                      latest: financial.statsValue,
                      previous: financialsData.previousYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                      sameQuarterLastYear: undefined, // Not used for annual data
                    };
                  });
                }
              }
              
              const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
              const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
              const tickerName = cachedName || symbol;
              const tickerSymbol = cachedSymbol || symbol;

              newFundamentalsData[symbol] = {
                ticker: symbol,
                name: symbol,
                ticker_name: tickerName,
                ticker_symbol: tickerSymbol,
                latest: 0,
                change: 0,
                changePercent: 0,
                volume: 0,
                high: 0,
                low: 0,
                open: 0,
                prevClose: 0,
                updated: Date.now(),
                isSyncing: false,
                _comparisonData: comparisonData,
                _timeframe: timeframe,
                ...fundamentalValues,
              };
            });

            setTickerFundamentalsData(prev => ({
              ...prev,
              ...newFundamentalsData,
            }));

            console.log(`Landing: Successfully fetched cached fundamental data`);
            return;
          }
        } catch (cacheError) {
          console.warn('Landing: Failed to fetch from cache, falling back to regular API:', cacheError);
          // Fall through to regular API call
        }
      }

      let tickerBasicsIds: number[] = [];
      
      if (syncResponseData) {
        // Use provided sync response data to avoid duplicate API call (same as ListFundamentals)
        console.log('Landing: Using provided sync response data');
        tickerBasicsIds = syncResponseData.tickers
          .filter((ticker: any) => ticker.is_synced === true && tickerSymbols.includes(ticker.ticker_symbol))
          .map((ticker: any) => ticker.ticker_basics_id);
      } else {
        // Fallback: Get ticker basics IDs from sync status (only if no sync data provided)
        console.log('Landing: No sync data provided, making API call');
        const syncResponse = await tickerApi.checkTickerSyncStatus({
          ticker_symbols: tickerSymbols,
        });
        
        if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers || syncResponse.data.tickers.length === 0) {
          console.error(`Failed to get sync status for ${tickerSymbols.join(', ')}`);
          return;
        }

        tickerBasicsIds = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_basics_id);
        
        // Use syncResponse.data for processing
        syncResponseData = syncResponse.data;
      }
      
      if (tickerBasicsIds.length === 0) {
        console.log(`No synced tickers found for ${tickerSymbols.join(', ')}`);
        return;
      }
      
      console.log(`Landing: Got ticker basics IDs: ${tickerBasicsIds.join(', ')}`);

      let currentQuarterRatios: { [tickerId: number]: any } = {};
      let ratiosComparisonData: { [tickerId: number]: any } = {};
      let financialsComparisonData: { [tickerId: number]: any } = {};

      if (timeframe === 'quarterly') {
        // Step 2: For synced tickers, call "current" API once with multiple tickers
        console.log('Landing: Fetching current quarterly ratios...');
        const currentRatiosResponse = await tickerApi.getCurrentQuarterlyRatios(tickerBasicsIds);
        
        if (currentRatiosResponse.success && currentRatiosResponse.data) {
          currentRatiosResponse.data.forEach((tickerData: any) => {
            currentQuarterRatios[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 3: For synced tickers, call "latest-ended-and-comparisons" once with multiple tickers
        console.log('Landing: Fetching latest ended quarterly ratios with comparisons...');
        const ratiosResponse = await tickerApi.getLatestEndedQuarterlyRatiosWithComparisons(tickerBasicsIds);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 4: For synced tickers, call "latest-and-comparisons" once with multiple tickers
        console.log('Landing: Fetching latest quarterly financials with comparisons...');
        const financialsResponse = await tickerApi.getLatestEndedQuarterlyFinancialsWithComparisons(tickerBasicsIds);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      } else {
        // Annual timeframe - fetch annual data with comparisons
        console.log('Landing: Fetching annual ratios with comparisons...');
        const ratiosResponse = await tickerApi.getLatestAnnualRatiosWithComparisons(tickerBasicsIds);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        console.log('Landing: Fetching annual financials with comparisons...');
        const financialsResponse = await tickerApi.getLatestAnnualFinancialsWithComparisons(tickerBasicsIds);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      }

      // Process data for each ticker (same as ListFundamentals)
      const newFundamentalsData: TickerFundamentalsData = {};
      
      // Use syncResponseData if available, otherwise we need to get ticker data differently (same as ListFundamentals)
      // Filter to only process synced tickers that are in the tickerSymbols parameter
      const tickerStatuses = syncResponseData 
        ? syncResponseData.tickers.filter((ticker: any) => 
            ticker.is_synced === true && tickerSymbols.includes(ticker.ticker_symbol)
          )
        : [];
      
      if (tickerStatuses.length === 0) {
        console.log(`Landing: No synced tickers found in syncResponseData for ${tickerSymbols.join(', ')}`);
        return;
      }
      
      tickerStatuses.forEach((tickerStatus: any) => {
        const symbol = tickerStatus.ticker_symbol;
        const tickerId = tickerStatus.ticker_basics_id;
        const currentRatiosData = currentQuarterRatios[tickerId];
        const ratiosData = ratiosComparisonData[tickerId];
        const financialsData = financialsComparisonData[tickerId];
        
        // Create a map of all fundamental values
        const fundamentalValues: { [key: string]: number } = {};
        const comparisonData: { [key: string]: any } = {};
        
        if (timeframe === 'quarterly') {
          // Process current quarter ratios for Latest Valuation section
          if (currentRatiosData) {
            currentRatiosData.ratios?.forEach((ratio: any) => {
              fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
            });
          }
          
          // Process latest ended quarter ratios for comparison data
          if (ratiosData) {
            ratiosData.latestQuarter?.forEach((ratio: any) => {
              // For Latest Valuation section, compare current quarter vs latest ended quarter
              const currentValue = currentRatiosData?.ratios?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue;
              if (currentValue !== undefined) {
                comparisonData[ratio.ratioKey] = {
                  latest: currentValue,
                  previous: ratio.ratioValue, // latest ended quarter as comparison
                  sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                };
              } else {
                // For other sections, use latest ended quarter data
                fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
                comparisonData[ratio.ratioKey] = {
                  latest: ratio.ratioValue,
                  previous: ratiosData.previousQuarter?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                  sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                };
              }
            });
          }
          
          if (financialsData) {
            // Process latest quarter financials
            financialsData.latestQuarter?.forEach((financial: any) => {
              fundamentalValues[financial.statsKey] = financial.statsValue;
              comparisonData[financial.statsKey] = {
                latest: financial.statsValue,
                previous: financialsData.previousQuarter?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                sameQuarterLastYear: financialsData.sameQuarterLastYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
              };
            });
          }
        }

        if (timeframe === 'annual') {
          // Process annual ratios
          if (ratiosData && ratiosData.latestYear) {
            ratiosData.latestYear.forEach((ratio: any) => {
              fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
              comparisonData[ratio.ratioKey] = {
                latest: ratio.ratioValue,
                previous: ratiosData.previousYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                sameQuarterLastYear: undefined, // Not used for annual data
              };
            });
          }
          
          if (financialsData && financialsData.latestYear) {
            financialsData.latestYear.forEach((financial: any) => {
              fundamentalValues[financial.statsKey] = financial.statsValue;
              comparisonData[financial.statsKey] = {
                latest: financial.statsValue,
                previous: financialsData.previousYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                sameQuarterLastYear: undefined, // Not used for annual data
              };
            });
          }
        }

        // Get ticker name from sessionStorage cache (don't depend on tickerNames state to avoid loops)
        const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
        const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
        const tickerName = cachedName || symbol;
        const tickerSymbol = cachedSymbol || symbol;

        // Create the ticker display data
        newFundamentalsData[symbol] = {
          ticker: symbol,
          name: symbol,
          ticker_name: tickerName,
          ticker_symbol: tickerSymbol,
          latest: 0,
          change: 0,
          changePercent: 0,
          volume: 0,
          high: 0,
          low: 0,
          open: 0,
          prevClose: 0,
          updated: Date.now(),
          isSyncing: false,
          _comparisonData: comparisonData,
          _timeframe: timeframe,
          // Add all fundamental values
          ...fundamentalValues,
        };
      });

      // Update the fundamentals data state
      setTickerFundamentalsData(prev => ({
        ...prev,
        ...newFundamentalsData,
      }));

      console.log(`Landing: Successfully fetched fundamental data:`, newFundamentalsData);
    } catch (error) {
      console.error('Error fetching fundamental data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to fetch fundamental data: ${errorMessage}`);
    }
  }, [timeframe]); // Removed tickerNames to prevent infinite loops

  // Fetch analyst views data following the same API sequence as ListAnalystViews
  const fetchAnalystViewsData = useCallback(async (tickerSymbols: string[]) => {
    if (tickerSymbols.length === 0) {
      setAnalystViewsData([]);
      return;
    }

    try {
      console.log(`Landing: Starting analyst views data fetch for: ${tickerSymbols.join(', ')}`);

      // Check if using default tickers - use cached endpoint
      const isDefaultTickers = JSON.stringify([...tickerSymbols].sort()) === JSON.stringify([...DEFAULT_TICKERS].sort());
      
      if (isDefaultTickers) {
        console.log('Landing: Using cached analyst views endpoint for default tickers');
        try {
          const cachedResponse = await tickerApi.landingCache.getCachedCompleteAnalystViews();
          
          if (cachedResponse.success && cachedResponse.data) {
            const { analystViews: cachedAnalystViews, prices: cachedPrices } = cachedResponse.data;
            
            // Process cached analyst views
            if (cachedAnalystViews?.success && cachedAnalystViews.data) {
              const transformedData = transformAnalystViewsData(cachedAnalystViews.data);
              
              setAnalystViewsData(prevData => {
                const existingData = prevData || [];
                const newDataMap = new Map(transformedData.map(item => [item.tickerSymbol, item]));
                
                const mergedData = existingData.map(existingItem => {
                  const newItem = newDataMap.get(existingItem.tickerSymbol);
                  return newItem || existingItem;
                });
                
                transformedData.forEach(newItem => {
                  if (!existingData.some(existing => existing.tickerSymbol === newItem.tickerSymbol)) {
                    mergedData.push(newItem);
                  }
                });
                
                return mergedData;
              });
            }
            
            // Process cached prices
            if (cachedPrices?.data?.tickers) {
              const newPriceData: { [symbol: string]: any } = {};
              
              cachedPrices.data.tickers.forEach((priceData: any) => {
                const symbol = priceData.ticker_symbol;
                if (priceData.prices && priceData.prices.length > 0) {
                  const latestPrice = priceData.prices[0];
                  const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
                  
                  newPriceData[symbol] = {
                    ticker_symbol: priceData.ticker_symbol,
                    ticker_basics_id: priceData.ticker_basics_id,
                    close_price_adjusted: latestPrice.close_price_adjusted,
                    date: new Date(latestPrice.date),
                    previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
                    previous_date: previousPrice ? new Date(previousPrice.date) : null,
                    currentPrice: latestPrice.close_price_adjusted,
                    previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
                    change: latestPrice.change,
                    changePercent: latestPrice.changePercent,
                    volume: latestPrice.volume,
                    high: latestPrice.high,
                    low: latestPrice.low,
                    open: latestPrice.open,
                    prevClose: latestPrice.previousClose,
                  };
                }
              });
              
              setAnalystViewsPriceData(prev => ({ ...prev, ...newPriceData }));
            }
            
            // Fetch ticker basics to get nextEarningsDate even when using cache
            try {
              const tickerBasicsResponse = await tickerApi.getTickerBasics(tickerSymbols);
              if (tickerBasicsResponse.success && tickerBasicsResponse.data) {
                const earningsDates: Record<string, Date | null> = {};
                tickerBasicsResponse.data.forEach((tickerBasic: any) => {
                  if (tickerBasic.nextEarningsDate) {
                    const date = typeof tickerBasic.nextEarningsDate === 'string' 
                      ? new Date(tickerBasic.nextEarningsDate) 
                      : tickerBasic.nextEarningsDate;
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      earningsDates[tickerBasic.tickerSymbol] = date;
                    }
                  }
                });
                setNextEarningsDates(prev => ({ ...prev, ...earningsDates }));
              }
            } catch (err) {
              console.error('Error fetching ticker basics for next earnings date:', err);
            }
            
            console.log(`Landing: Successfully fetched cached analyst views data`);
            return;
          }
        } catch (cacheError) {
          console.warn('Landing: Failed to fetch from cache, falling back to regular API:', cacheError);
          // Fall through to regular API call
        }
      }

      const request = {
        ticker_symbols: tickerSymbols,
        category: 'all',
        key: 'all'
      };

      const response = await tickerApi.getLatestAnalystViews(request);
      
      // Fetch ticker basics to get nextEarningsDate
      try {
        const tickerBasicsResponse = await tickerApi.getTickerBasics(tickerSymbols);
        if (tickerBasicsResponse.success && tickerBasicsResponse.data) {
          const earningsDates: Record<string, Date | null> = {};
          tickerBasicsResponse.data.forEach((tickerBasic: any) => {
            if (tickerBasic.nextEarningsDate) {
              const date = typeof tickerBasic.nextEarningsDate === 'string' 
                ? new Date(tickerBasic.nextEarningsDate) 
                : tickerBasic.nextEarningsDate;
              if (date instanceof Date && !isNaN(date.getTime())) {
                earningsDates[tickerBasic.tickerSymbol] = date;
              }
            }
          });
          setNextEarningsDates(prev => ({ ...prev, ...earningsDates }));
        }
      } catch (err) {
        console.error('Error fetching ticker basics for next earnings date:', err);
      }
      
      if (response.success) {
        const transformedData = transformAnalystViewsData(response.data);
        
        // Merge new data with existing data instead of replacing it (same as ListAnalystViews)
        setAnalystViewsData(prevData => {
          const existingData = prevData || [];
          const newDataMap = new Map(transformedData.map(item => [item.tickerSymbol, item]));
          
          // Update existing data with new data, or add new data
          const mergedData = existingData.map(existingItem => {
            const newItem = newDataMap.get(existingItem.tickerSymbol);
            return newItem || existingItem;
          });
          
          // Add any completely new tickers
          transformedData.forEach(newItem => {
            if (!existingData.some(existing => existing.tickerSymbol === newItem.tickerSymbol)) {
              mergedData.push(newItem);
            }
          });
          
          return mergedData;
        });
        
        console.log(`Landing: Successfully fetched analyst views data for ${tickerSymbols.join(', ')}`);
      } else {
        console.error(`Landing: Failed to fetch analyst views: ${response.message}`);
        setError(response.message || 'Failed to fetch analyst views');
      }
    } catch (error) {
      console.error('Landing: Error fetching analyst views data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to fetch analyst views data: ${errorMessage}`);
    }
  }, []);

  // Fetch price data for analyst views (same as ListAnalystViews)
  const fetchAnalystViewsPriceData = useCallback(async (tickerSymbols: string[]) => {
    if (tickerSymbols.length === 0) return;

    try {
      console.log(`Landing: Fetching price data for analyst views: ${tickerSymbols.join(', ')}`);
      
      const response = await tickerApi.getTickerLatestPrice({
        ticker_symbols: tickerSymbols,
      });

      if (response.success && response.data && response.data.tickers) {
        const newPriceData: { [symbol: string]: any } = {};
        
        response.data.tickers.forEach((priceData: any) => {
          const symbol = priceData.ticker_symbol;
          if (priceData.prices && priceData.prices.length > 0) {
            const latestPrice = priceData.prices[0];
            const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
            
            newPriceData[symbol] = {
              ticker_symbol: priceData.ticker_symbol,
              ticker_basics_id: priceData.ticker_basics_id,
              close_price_adjusted: latestPrice.close_price_adjusted,
              date: new Date(latestPrice.date),
              previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
              previous_date: previousPrice ? new Date(previousPrice.date) : null,
              // Also add the format expected by transformDataForDisplay
              currentPrice: latestPrice.close_price_adjusted,
              previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
              change: latestPrice.change,
              changePercent: latestPrice.changePercent,
              volume: latestPrice.volume,
              high: latestPrice.high,
              low: latestPrice.low,
              open: latestPrice.open,
              prevClose: latestPrice.previousClose,
            };
          }
        });
        
        setAnalystViewsPriceData(prev => ({ ...prev, ...newPriceData }));
        console.log(`Landing: Successfully fetched price data for analyst views`);
      }
    } catch (error) {
      console.error('Landing: Error fetching price data for analyst views:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to fetch price data: ${errorMessage}`);
    }
  }, []);

  // Fetch ticker sync status to get ticker_basics_id (for Technicals tab)
  const fetchTickerBasics = useCallback(async () => {
    if (tickerList.length === 0) return [];

    try {
      // Use checkTickerSyncStatus to get ticker_basics_id (same as ListTechnicals)
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: tickerList,
      });
      
      if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers) {
        console.error('Invalid sync response structure:', syncResponse);
        return [];
      }
      
      const tickerNamesMap: { [symbol: string]: string } = {};
      
      syncResponse.data.tickers.forEach((ticker: any) => {
        const symbol = ticker.ticker_symbol;
        const tickerName = ticker.ticker_name || symbol;
        tickerNamesMap[symbol] = tickerName;
        // Store in sessionStorage for consistency (used by fetchFundamentalData)
        if (ticker.ticker_name) {
          sessionStorage.setItem(`ticker-name-${symbol}`, ticker.ticker_name);
        }
        if (ticker.ticker_symbol) {
          sessionStorage.setItem(`ticker-symbol-${symbol}`, ticker.ticker_symbol);
        }
      });
      
      setTickerNames(tickerNamesMap);
      
      // Return tickers with ticker_basics_id
      return syncResponse.data.tickers;
    } catch (error) {
      console.error('Error fetching ticker sync status:', error);
      return [];
    }
  }, [tickerList]);

  // Fetch technical data (returns data without setting state)
  const fetchTechnicalData = useCallback(async (syncedTickers: any[]): Promise<TickerTechnicalData> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      // Extract ticker_basics_id as numbers (syncedTickers is already filtered)
      const tickerBasicsIds = syncedTickers
        .map((ticker: any) => ticker.ticker_basics_id)
        .filter((id: any) => id !== undefined && id !== null && typeof id === 'number');
      
      if (tickerBasicsIds.length === 0) {
        console.log('No synced tickers with valid ticker_basics_id found');
        return {};
      }
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await tickerApi.getTickerTechnical({
        tickerBasicsIds: tickerBasicsIds,
        daysAgo: 0,
      });

      const newTechnicalData: TickerTechnicalData = {};
      
      response.data.forEach((tickerData: any) => {
        const symbol = tickerData.tickerSymbol;
        
        newTechnicalData[symbol] = {
          technicals: {},
          price: null,
          movingAverages: [],
        };

        if (tickerData.indicators) {
          tickerData.indicators.forEach((indicator: any) => {
            newTechnicalData[symbol].technicals[indicator.indicatorKey] = {
              ticker_basics_id: tickerData.tickerBasicsId,
              indicator_key: indicator.indicatorKey,
              value: indicator.indicatorValue,
              nonNumericValue: indicator.indicatorNonNumericValue || indicator.indicator_non_numeric_value || null,
              date: indicator.date,
            };
          });
        }
      });
      
      return newTechnicalData;
    } catch (error) {
      console.error('Error fetching technical data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to fetch technical data: ${errorMessage}`);
      return {};
    }
  }, []);

  // Fetch moving average data (returns data without setting state)
  const fetchMovingAverageData = useCallback(async (syncedTickers: any[]): Promise<{ [tickerSymbol: string]: MovingAverageData[] }> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      // Extract ticker_basics_id as numbers (syncedTickers is already filtered)
      const tickerBasicsIds = syncedTickers
        .map((ticker: any) => ticker.ticker_basics_id)
        .filter((id: any) => id !== undefined && id !== null && typeof id === 'number');
      
      if (tickerBasicsIds.length === 0) {
        console.log('No synced tickers with valid ticker_basics_id found for moving averages');
        return {};
      }
      
      const response = await tickerApi.getMovingAverageCrossData({
        ticker_basics_ids: tickerBasicsIds,
        start_days_ago: 1,
        end_days_ago: 6,
      });

      const movingAverageData: { [tickerSymbol: string]: MovingAverageData[] } = {};
      
      response.data.tickers.forEach((tickerData: any) => {
        const symbol = tickerData.ticker_symbol;
        movingAverageData[symbol] = tickerData.moving_averages.map((ma: any) => ({
          days_ago: ma.days_ago,
          date: new Date(ma.date),
          sma_20: ma.sma_20,
          sma_50: ma.sma_50,
          sma_200: ma.sma_200,
          sma_w50: ma.sma_w50,
          sma_w200: ma.sma_w200,
          ema_21: ma.ema_21,
          ema_50: ma.ema_50,
          ema_200: ma.ema_200,
        }));
      });

      return movingAverageData;
    } catch (error) {
      console.error('Error fetching moving average data:', error);
      return {};
    }
  }, []);

  // Fetch both technical and moving average data in parallel (like ListTechnicals)
  const fetchAllTechnicalData = useCallback(async (syncedTickers: any[]): Promise<TickerTechnicalData> => {
    if (syncedTickers.length === 0) {
      return {};
    }

    try {
      // Check if using default tickers - use cached endpoint
      const tickerSymbols = syncedTickers.map((t: any) => t.ticker_symbol);
      const isDefaultTickers = JSON.stringify([...tickerSymbols].sort()) === JSON.stringify([...DEFAULT_TICKERS].sort());
      
      if (isDefaultTickers) {
        console.log('Landing: Using cached technicals endpoint for default tickers');
        try {
          const cachedResponse = await tickerApi.landingCache.getCachedCompleteTechnicals();
          
          if (cachedResponse.success && cachedResponse.data) {
            const { technicalData: cachedTechnicalData, movingAverages: cachedMovingAverages, prices: cachedPrices } = cachedResponse.data;
            
            // Process cached technical data
            const newTechnicalData: TickerTechnicalData = {};
            
            if (cachedTechnicalData?.data) {
              cachedTechnicalData.data.forEach((tickerData: any) => {
                const symbol = tickerData.tickerSymbol;
                
                newTechnicalData[symbol] = {
                  technicals: {},
                  price: null,
                  movingAverages: [],
                };

                if (tickerData.indicators) {
                  tickerData.indicators.forEach((indicator: any) => {
                    newTechnicalData[symbol].technicals[indicator.indicatorKey] = {
                      ticker_basics_id: tickerData.tickerBasicsId,
                      indicator_key: indicator.indicatorKey,
                      value: indicator.indicatorValue,
                      nonNumericValue: indicator.indicatorNonNumericValue || indicator.indicator_non_numeric_value || null,
                      date: indicator.date,
                    };
                  });
                }
              });
            }
            
            // Process cached moving averages
            if (cachedMovingAverages?.data?.tickers) {
              cachedMovingAverages.data.tickers.forEach((tickerData: any) => {
                const symbol = tickerData.ticker_symbol;
                if (newTechnicalData[symbol]) {
                  newTechnicalData[symbol].movingAverages = tickerData.moving_averages.map((ma: any) => ({
                    days_ago: ma.days_ago,
                    date: new Date(ma.date),
                    sma_20: ma.sma_20,
                    sma_50: ma.sma_50,
                    sma_200: ma.sma_200,
                    sma_w50: ma.sma_w50,
                    sma_w200: ma.sma_w200,
                    ema_21: ma.ema_21,
                    ema_50: ma.ema_50,
                    ema_200: ma.ema_200,
                  }));
                }
              });
            }
            
            // Process cached prices
            if (cachedPrices?.data?.tickers) {
              cachedPrices.data.tickers.forEach((priceData: any) => {
                const symbol = priceData.ticker_symbol;
                if (newTechnicalData[symbol] && priceData.prices && priceData.prices.length > 0) {
                  const latestPrice = priceData.prices[0];
                  const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
                  
                  newTechnicalData[symbol].price = {
                    ticker_symbol: priceData.ticker_symbol,
                    ticker_basics_id: priceData.ticker_basics_id,
                    close_price_adjusted: latestPrice.close_price_adjusted,
                    date: new Date(latestPrice.date),
                    previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
                    previous_date: previousPrice ? new Date(previousPrice.date) : null,
                  };
                }
              });
            }
            
            // Set the merged technical data
            setTickerTechnicalData(prev => {
              const updated = { ...prev, ...newTechnicalData };
              return updated;
            });

            return newTechnicalData;
          }
        } catch (cacheError) {
          console.warn('Landing: Failed to fetch from cache, falling back to regular API:', cacheError);
          // Fall through to regular API call
        }
      }

      // Call both APIs in parallel (regular API call)
      const [technicalData, movingAverageData] = await Promise.all([
        fetchTechnicalData(syncedTickers),
        fetchMovingAverageData(syncedTickers)
      ]);

      // Merge moving average data into technical data
      Object.keys(technicalData).forEach(symbol => {
        if (movingAverageData[symbol]) {
          technicalData[symbol].movingAverages = movingAverageData[symbol];
        }
      });

      // Set the merged technical data
      setTickerTechnicalData(prev => {
        const updated = { ...prev, ...technicalData };
        return updated;
      });

      return technicalData;
    } catch (error) {
      console.error('Error fetching all technical data:', error);
      throw error;
    }
  }, [fetchTechnicalData, fetchMovingAverageData]);

  // Fetch price data (like ListTechnicals - takes ticker symbols and merges with existing data)
  const fetchPriceData = useCallback(async (tickers: string[], currentTechnicalData: TickerTechnicalData) => {
    if (tickers.length === 0) return;

    try {
      const response = await tickerApi.getTickerLatestPrice({
        ticker_symbols: tickers,
      });

      // Merge with existing data instead of replacing it (same as ListTechnicals)
      setTickerTechnicalData(prev => {
        const updatedTechnicalData = { ...prev };
        
        response.data.tickers.forEach((priceData: any) => {
          const symbol = priceData.ticker_symbol;
          if (updatedTechnicalData[symbol] && priceData.prices && priceData.prices.length > 0) {
            // Use the first (most recent) price from the array
            const latestPrice = priceData.prices[0];
            const previousPrice = priceData.prices.length > 1 ? priceData.prices[1] : null;
            
            updatedTechnicalData[symbol].price = {
              ticker_symbol: priceData.ticker_symbol,
              ticker_basics_id: priceData.ticker_basics_id,
              close_price_adjusted: latestPrice.close_price_adjusted,
              date: new Date(latestPrice.date),
              previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
              previous_date: previousPrice ? new Date(previousPrice.date) : null,
            };
          }
        });
        
        return updatedTechnicalData;
      });
    } catch (error) {
      console.error('Error fetching price data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to fetch price data: ${errorMessage}`);
    }
  }, []);

  const fetchLandingPerformanceData = useCallback(
    async (syncedTickers: any[], symbolsFallback: string[]): Promise<TickerPerformanceData> => {
      if (syncedTickers.length > 0) {
        const tickerBasicsIds = syncedTickers.map((t: any) => t.ticker_basics_id);
        const response = await performanceApi.getTickerPerformance({ tickerBasicsIds });
        const byId: Record<number, any> = {};
        (response.data || []).forEach((row: any) => {
          byId[row.tickerBasicsId] = row;
        });

        const next: TickerPerformanceData = {};
        for (const t of syncedTickers) {
          const row = byId[t.ticker_basics_id];
          const perf: PerformanceRow | null = row
            ? {
                ticker_basics_id: t.ticker_basics_id,
                date: row.date ? new Date(row.date).toISOString().slice(0, 10) : null,
                ytd_return: row.ytdReturn ?? null,
                one_m_return: row.oneMReturn ?? null,
                three_m_return: row.threeMReturn ?? null,
                six_m_return: row.sixMReturn ?? null,
                nine_m_return: row.nineMReturn ?? null,
                one_y_return: row.oneYReturn ?? null,
                two_y_return: row.twoYReturn ?? null,
                three_y_return: row.threeYReturn ?? null,
                four_y_return: row.fourYReturn ?? null,
                five_y_return: row.fiveYReturn ?? null,
              }
            : null;
          next[t.ticker_symbol] = { performance: perf };
        }
        return next;
      }

      const symbols = (symbolsFallback || []).map((s) => String(s).toUpperCase());
      if (symbols.length === 0) return {};

      const response = await performanceApi.getTickerPerformance({ tickerSymbols: symbols });
      const next: TickerPerformanceData = {};
      (response.data || []).forEach((row: any) => {
        const symbol = String(row.tickerSymbol || '').toUpperCase();
        if (!symbol) return;
        next[symbol] = {
          performance: {
            ticker_basics_id: row.tickerBasicsId,
            date: row.date ? new Date(row.date).toISOString().slice(0, 10) : null,
            ytd_return: row.ytdReturn ?? null,
            one_m_return: row.oneMReturn ?? null,
            three_m_return: row.threeMReturn ?? null,
            six_m_return: row.sixMReturn ?? null,
            nine_m_return: row.nineMReturn ?? null,
            one_y_return: row.oneYReturn ?? null,
            two_y_return: row.twoYReturn ?? null,
            three_y_return: row.threeYReturn ?? null,
            four_y_return: row.fourYReturn ?? null,
            five_y_return: row.fiveYReturn ?? null,
          },
        };
      });

      if (Object.keys(next).length > 0) {
        setTickerPerformanceBasicsMap((prev) => {
          const merged = { ...prev };
          (response.data || []).forEach((row: any) => {
            const symbol = String(row.tickerSymbol || '').toUpperCase();
            if (!symbol) return;
            merged[symbol] = { id: row.tickerBasicsId, ticker_name: row.tickerName || row.ticker_name || symbol };
          });
          return merged;
        });
      }

      return next;
    },
    [performanceApi],
  );

  const loadLandingPerformanceTabData = useCallback(async () => {
    const list = tickerList;
    if (list.length === 0) return;

    const syncResponse = await performanceApi.checkTickerSyncStatus({
      ticker_symbols: list,
    });
    const tickers = syncResponse?.data?.tickers || [];
    const synced = tickers.filter((t: any) => t.is_synced === true);
    const unsynced = tickers.filter((t: any) => !t.is_synced);
    setPerformanceUnsyncedTickers(new Set(unsynced.map((t: any) => t.ticker_symbol)));
    setPerformanceSyncingTickers(new Set());
    setPerformancePollingLimitReached(new Set());

    if (synced.length > 0) {
      setTickerPerformanceBasicsMap((prev) => {
        const next = { ...prev };
        synced.forEach((t: any) => {
          next[t.ticker_symbol] = {
            id: t.ticker_basics_id,
            ticker_name: t.ticker_name || t.ticker_symbol,
          };
        });
        return next;
      });
    }

    const perf = await fetchLandingPerformanceData(synced, list);
    setTickerPerformanceData((prev) => {
      const merged = { ...prev, ...perf };
      list.forEach((sym) => {
        if (merged[sym] === undefined) {
          merged[sym] = { performance: null };
        }
      });
      return merged;
    });

    const syncedWithDate = synced.filter((t: any) => t.last_sync_ended_at);
    if (syncedWithDate.length > 0) {
      const lastSyncDates = syncedWithDate
        .map((t: any) => new Date(t.last_sync_ended_at))
        .filter((d: Date) => !isNaN(d.getTime()));
      if (lastSyncDates.length > 0) {
        setLastUpdated(new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime()))));
      }
    }
  }, [tickerList, performanceApi, fetchLandingPerformanceData]);

  // Fetch data based on active tab
  useEffect(() => {
    const loadData = async () => {
      // For Daily tab, data is handled by useTickerSnapshot hook automatically
      // We just need to handle other tabs
      if (activeTab === 'daily') {
        setLoading(false);
        return;
      }

      // Check if data already exists to prevent unnecessary re-fetching (like ListTechnicals/ListFundamentals)
      let tickersNeedingData: string[] = [];
      if (activeTab === 'performance') {
        tickersNeedingData = tickerList.filter((symbol) => !tickerPerformanceData[symbol]);
        if (tickersNeedingData.length === 0) {
          const syncedTickers = await fetchTickerBasics();
          const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
          const syncedTickersWithDate = syncedTickersOnly.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date =
                  typeof t.last_sync_ended_at === 'string' ? new Date(t.last_sync_ended_at) : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);

            if (lastSyncDates.length > 0) {
              setLastUpdated(new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime()))));
            }
          }
          setLoading(false);
          return;
        }
      } else if (activeTab === 'technicals') {
        tickersNeedingData = tickerList.filter(symbol => {
          const existingData = tickerTechnicalData[symbol];
          return !existingData || !existingData.price;
        });
        
        if (tickersNeedingData.length === 0) {
          console.log('Landing: All technical tickers already have data, skipping API calls');
          // Still need to calculate lastUpdated from sync status
          const syncedTickers = await fetchTickerBasics();
          const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
          const syncedTickersWithDate = syncedTickersOnly.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }
          setLoading(false);
          return;
        }
      } else if (activeTab === 'fundamentals') {
        tickersNeedingData = tickerList.filter(symbol => {
          const existingData = tickerFundamentalsData[symbol];
          return !existingData || !existingData._comparisonData || 
                 (existingData._timeframe && existingData._timeframe !== timeframe);
        });
        
        if (tickersNeedingData.length === 0) {
          console.log('Landing: All fundamental tickers already have data, skipping API calls');
          // Still need to calculate lastUpdated from sync status
          const syncedTickers = await fetchTickerBasics();
          const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
          const syncedTickersWithDate = syncedTickersOnly.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }
          setLoading(false);
          return;
        }
      } else if (activeTab === 'analysts') {
        tickersNeedingData = tickerList.filter(symbol => {
          const existingAnalystData = analystViewsData.find(item => item.tickerSymbol === symbol);
          const existingPriceData = analystViewsPriceData[symbol];
          return !existingAnalystData || !existingPriceData;
        });
        
        if (tickersNeedingData.length === 0) {
          console.log('Landing: All analyst views tickers already have data, skipping API calls');
          // Still need to calculate lastUpdated from sync status
          const syncedTickers = await fetchTickerBasics();
          const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
          const syncedTickersWithDate = syncedTickersOnly.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }
          setLoading(false);
          return;
        }
      } else if (activeTab === 'insiders') {
        tickersNeedingData = tickerList.filter(symbol => {
          // Normalize symbol to uppercase for consistent lookup
          const normalizedSymbol = symbol.toUpperCase();
          const existingData = insiderData[normalizedSymbol];
          return !existingData;
        });
        
        if (tickersNeedingData.length === 0) {
          console.log('Landing: All insider tickers already have data, skipping API calls');
          // Still need to calculate lastUpdated from insider sync status
          const syncedTickers = await fetchTickerBasics();
          const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
          const tickerBasicsIds = syncedTickersOnly.map((t: any) => t.ticker_basics_id).filter((id: any) => id);
          
          if (tickerBasicsIds.length > 0) {
            try {
              const insiderSyncResponse = await tickerApi.checkInsiderSyncStatus({
                ticker_basics_ids: tickerBasicsIds,
              });
              
              const syncedTickersWithDate = insiderSyncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
              if (syncedTickersWithDate.length > 0) {
                const lastSyncDates = syncedTickersWithDate
                  .map((t: any) => {
                    const date = typeof t.last_sync_ended_at === 'string' 
                      ? new Date(t.last_sync_ended_at) 
                      : t.last_sync_ended_at;
                    return date instanceof Date && !isNaN(date.getTime()) ? date : null;
                  })
                  .filter((d: Date | null): d is Date => d !== null);
                
                if (lastSyncDates.length > 0) {
                  const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
                  setLastUpdated(latestDate);
                }
              }
            } catch (err) {
              console.error('Error checking insider sync status:', err);
            }
          }
          setLoading(false);
          return;
        }
      } else if (activeTab === 'proscons') {
        tickersNeedingData = tickerList.filter(symbol => !prosConsRows[symbol]);
        if (tickersNeedingData.length === 0 && prosConsSyncingTickers.size === 0) {
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      
      try {
        console.log(`Landing: Loading data for ${activeTab} tab, tickerList:`, tickerList);
        const syncedTickers = await fetchTickerBasics();
        console.log(`Landing: Fetched ticker basics, count:`, syncedTickers.length);
        
        if (syncedTickers.length === 0) {
          console.log('Landing: No tickers returned from fetchTickerBasics');
          setLoading(false);
          return;
        }

        // Filter for synced tickers
        const syncedTickersOnly = syncedTickers.filter((ticker: any) => ticker.is_synced === true);
        console.log(`Landing: Synced tickers count:`, syncedTickersOnly.length);
        
        // Calculate last updated from latest last_sync_ended_at of synced tickers
        const syncedTickersWithDate = syncedTickersOnly.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickersWithDate.length > 0) {
          const lastSyncDates = syncedTickersWithDate
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
        
        if (syncedTickersOnly.length === 0 && activeTab !== 'proscons' && activeTab !== 'performance') {
          console.log('Landing: No synced tickers found');
          setLoading(false);
          return;
        }

        if (activeTab === 'performance') {
          console.log('Landing: Fetching performance data...');
          await new Promise((resolve) => setTimeout(resolve, 100));
          try {
            await loadLandingPerformanceTabData();
          } catch (perfErr: any) {
            if (axios.isAxiosError(perfErr) && perfErr.response?.status === 429) {
              setError('Rate limit exceeded. Please try again later.');
            } else {
              throw perfErr;
            }
          }
          console.log('Landing: Performance data fetched');
        } else if (activeTab === 'technicals') {
          console.log('Landing: Fetching technical data...');
          // Step 1: Fetch technical data and moving averages in parallel (with small delay)
          await new Promise(resolve => setTimeout(resolve, 100));
          const technicalData = await fetchAllTechnicalData(syncedTickersOnly);
          console.log('Landing: Technical data fetched:', Object.keys(technicalData).length, 'tickers');
          
          // Step 2: Fetch price data (with small delay between calls, like ListTechnicals)
          await new Promise(resolve => setTimeout(resolve, 100));
          const tickerSymbols = syncedTickersOnly.map((ticker: any) => ticker.ticker_symbol);
          await fetchPriceData(tickerSymbols, technicalData);
          console.log('Landing: Price data fetched');
        } else if (activeTab === 'fundamentals') {
          console.log('Landing: Fetching fundamental data...');
          
          // For Fundamentals tab, fetch fundamental data (same sequence as ListFundamentals)
          // Reuse sync response data from fetchTickerBasics to avoid duplicate API call
          // We need to reconstruct the sync response format from syncedTickers
          const syncResponseData = {
            tickers: syncedTickers.map((ticker: any) => ({
              ticker_symbol: ticker.ticker_symbol,
              ticker_basics_id: ticker.ticker_basics_id,
              is_synced: ticker.is_synced,
              ticker_name: ticker.ticker_name,
            })),
          };
          
          // Store ticker names in sessionStorage (don't update state to avoid loops)
          syncResponseData.tickers.forEach((ticker: any) => {
            if (ticker.ticker_name) {
              sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
            }
            if (ticker.ticker_symbol) {
              sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
            }
          });
          
          // Get synced ticker symbols
          const syncedTickerSymbols = syncedTickersOnly.map((ticker: any) => ticker.ticker_symbol);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          await fetchFundamentalData(syncedTickerSymbols, syncResponseData);
          console.log('Landing: Fundamental data fetched');
        } else if (activeTab === 'analysts') {
          console.log('Landing: Fetching analyst views data...');
          // For Analysts tab, fetch analyst views data (same sequence as ListAnalystViews)
          // Get synced ticker symbols
          const syncedTickerSymbols = syncedTickersOnly.map((ticker: any) => ticker.ticker_symbol);
          
          // Store ticker names from sync response
          const newTickerNames: { [symbol: string]: string } = {};
          syncedTickers.forEach((ticker: any) => {
            if (ticker.ticker_name) {
              newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
              sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
            }
            if (ticker.ticker_symbol) {
              sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
            }
          });
          setTickerNames(prev => ({ ...prev, ...newTickerNames }));
          
          // Fetch analyst views data and price data in parallel (same as ListAnalystViews)
          await Promise.all([
            fetchAnalystViewsData(syncedTickerSymbols),
            fetchAnalystViewsPriceData(syncedTickerSymbols)
          ]);
          console.log('Landing: Analyst views data fetched');
        } else if (activeTab === 'insiders') {
          console.log('Landing: Fetching insider transactions data...');
          // For Insiders tab, fetch insider transactions data (same sequence as ListInsiders)
          // Get synced ticker basics IDs
          const tickerBasicsIds = syncedTickersOnly.map((ticker: any) => ticker.ticker_basics_id).filter((id: any) => id);
          
          if (tickerBasicsIds.length === 0) {
            console.log('Landing: No ticker basics IDs available for insider transactions');
            setLoading(false);
            return;
          }
          
          // Store ticker names from sync response
          const newTickerNames: { [symbol: string]: string } = {};
          syncedTickers.forEach((ticker: any) => {
            if (ticker.ticker_name) {
              newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
              sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
            }
            if (ticker.ticker_symbol) {
              sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
            }
          });
          setTickerNames(prev => ({ ...prev, ...newTickerNames }));
          
          // Check insider sync status first
          const insiderSyncResponse = await tickerApi.checkInsiderSyncStatus({
            ticker_basics_ids: tickerBasicsIds,
          });
          
          // Calculate last updated from insider sync status
          const syncedTickersWithDate = insiderSyncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
          if (syncedTickersWithDate.length > 0) {
            const lastSyncDates = syncedTickersWithDate
              .map((t: any) => {
                const date = typeof t.last_sync_ended_at === 'string' 
                  ? new Date(t.last_sync_ended_at) 
                  : t.last_sync_ended_at;
                return date instanceof Date && !isNaN(date.getTime()) ? date : null;
              })
              .filter((d: Date | null): d is Date => d !== null);
            
            if (lastSyncDates.length > 0) {
              const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
              setLastUpdated(latestDate);
            }
          }
          
          // Get synced ticker basics IDs for fetching transactions
          const syncedInsiderTickerIds = insiderSyncResponse.data.tickers
            .filter((t: any) => t.is_synced === true)
            .map((t: any) => t.ticker_basics_id);
          
          if (syncedInsiderTickerIds.length > 0) {
            // Fetch insider transactions data
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 90);
            const endDateStr = today.toISOString().split('T')[0];
            const startDateStr = startDate.toISOString().split('T')[0];
            
            const transactionsResponse = await tickerApi.getInsiderTransactionsByDateRange({
              tickerBasicsIds: syncedInsiderTickerIds,
              startDate: startDateStr,
              endDate: endDateStr,
              ownerHasProSubscription: true, // Landing page should show all indicators
            });
            
            if (transactionsResponse.success && transactionsResponse.data) {
              const newInsiderData: Record<string, any> = {};
              transactionsResponse.data.forEach((item: any) => {
                // Normalize ticker symbol to uppercase for consistent lookup
                const normalizedSymbol = (item.tickerSymbol || '').toUpperCase();
                newInsiderData[normalizedSymbol] = item;
              });
              setInsiderData(prev => ({ ...prev, ...newInsiderData }));
            }
          }
          
          console.log('Landing: Insider transactions data fetched');
        } else if (activeTab === 'proscons') {
          console.log('Landing: Fetching pros/cons data...');
          const cached = await tickerApi.landingCache.getCachedCompleteProsCons();
          const syncItems = cached?.data?.syncStatus?.data?.tickers || [];
          const prosConsItems = cached?.data?.prosCons?.data || [];

          const syncingSet = new Set<string>();
          syncItems.forEach((t: any) => {
            if (t?.is_syncing && t?.ticker_symbol) syncingSet.add(String(t.ticker_symbol));
          });
          setProsConsSyncingTickers(syncingSet);

          const rows: Record<string, any> = {};
          prosConsItems.forEach((row: any) => {
            if (row?.tickerSymbol) rows[row.tickerSymbol] = row;
          });
          setProsConsRows(rows);
          console.log('Landing: Pros/cons data fetched');
        }
      } catch (error) {
        console.error('Landing: Error loading data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setError(`Failed to load stock data: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // Only include actual values, not callback functions, to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, refetchTrigger, tickerList, timeframe, fetchAnalystViewsData, fetchAnalystViewsPriceData]);

  // Clear fundamentals data when timeframe changes (main useEffect will handle refetch)
  useEffect(() => {
    if (activeTab === 'fundamentals') {
      // Clear existing fundamentals data when timeframe changes
      // The main useEffect will detect the mismatch and refetch automatically
      // Only clear when timeframe changes, not when switching tabs
      setTickerFundamentalsData({});
    }
  }, [timeframe]); // Only depend on timeframe, not activeTab

  // Update loading state for Daily tab based on snapshot loading state
  useEffect(() => {
    if (activeTab === 'daily') {
      if (snapshotLoading === false) {
        setLoading(false);
      } else {
        setLoading(true);
      }
    }
    // Note: Don't reset lastUpdated when switching tabs - it will be recalculated if needed
  }, [activeTab, snapshotLoading]);

  // Close embedded daily view when switching away from Daily tab
  useEffect(() => {
    if (activeTab !== 'daily' && selectedTickerForDailyView) {
      handleCloseDailyView();
    }
  }, [activeTab]);
  
  // Close embedded technicals view when switching away from Technicals tab
  useEffect(() => {
    if (activeTab !== 'technicals' && selectedTickerForTechnicalsView) {
      handleCloseTechnicalsView();
    }
  }, [activeTab]);
  
  // Close embedded analyst views view when switching away from Analysts tab
  useEffect(() => {
    if (activeTab !== 'analysts' && selectedTickerForAnalystViewsView) {
      handleCloseAnalystViewsView();
    }
    if (activeTab !== 'insiders' && selectedTickerForInsidersView) {
      handleCloseInsidersView();
    }
  }, [activeTab]);
  
  // Scroll to top of table container when tab changes (Featured Stocks grid only — not embedded ticker)
  useEffect(() => {
    const embeddedOpen =
      selectedTickerForDailyView ||
      selectedTickerForTechnicalsView ||
      selectedTickerForFundamentalsView ||
      selectedTickerForAnalystViewsView ||
      selectedTickerForProsConsView ||
      selectedTickerForInsidersView ||
      selectedTickerForPerformanceView;
    if (embeddedOpen) return;

    const timer = setTimeout(() => {
      const tableSection = tableSectionRef.current;
      if (tableSection) {
        const scrollableContainer = tableSection.querySelector<HTMLElement>('.overflow-x-auto.overflow-y-auto');
        if (scrollableContainer) {
          scrollableContainer.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [
    activeTab,
    selectedTickerForDailyView,
    selectedTickerForTechnicalsView,
    selectedTickerForFundamentalsView,
    selectedTickerForAnalystViewsView,
    selectedTickerForProsConsView,
    selectedTickerForInsidersView,
    selectedTickerForPerformanceView,
  ]);

  // Update labels when market status is determined and cache them (same as ListDaily)
  useEffect(() => {
    if (marketStatus !== null) {
      const latestLabel = getLatestLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
      const openLabel = getOpenLabel(marketStatus, marketOpenTimestamp, marketCloseTimestamp);
      setCachedLatestLabel(latestLabel);
      setCachedOpenLabel(openLabel);
      // Cache the labels for next time
      localStorage.setItem('stocktable-latest-label', latestLabel);
      localStorage.setItem('stocktable-open-label', openLabel);
    }
  }, [marketStatus, marketOpenTimestamp, marketCloseTimestamp]);

  // Use shared hook for Daily tab statistics
  const dailyTabStatistics = useDailyTabStatistics({
    marketStatus,
    isEditingTickers: false,
    cachedLatestLabel,
    cachedOpenLabel,
    marketOpenTimestamp,
    marketCloseTimestamp,
    marketPreMarketTimestamp,
  });

  const visibleStatistics = activeTab === 'daily' ? dailyTabStatistics : undefined;

  // Helper function to get ticker data for display
  const getTickerData = (symbol: string): TickerDisplayData | undefined => {
    // For Daily tab, get data from snapshotData (same as ListDaily)
    if (activeTab === 'daily') {
      const tickerDataForSymbol = snapshotData?.find(t => t.ticker === symbol);
      
      // Get ticker name from sessionStorage cache or tickerNames state (same as ListDaily)
      const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
      const tickerName = cachedName || tickerNames[symbol] || symbol;
      const tickerSymbol = cachedSymbol || symbol;
      
      if (!tickerDataForSymbol) {
        return undefined;
      }
      
      return {
        ...tickerDataForSymbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbol,
      };
    }

    // For Fundamentals tab, get data from tickerFundamentalsData
    if (activeTab === 'fundamentals') {
      const tickerDataForSymbol = tickerFundamentalsData[symbol];
      
      if (!tickerDataForSymbol) {
        return undefined;
      }
      
      return tickerDataForSymbol;
    }

    // For Analysts tab, combine analyst views data with price data (same as ListAnalystViews)
    if (activeTab === 'analysts') {
      const analystData = analystViewsData.find(item => item.tickerSymbol === symbol);
      const priceDataForSymbol = analystViewsPriceData[symbol];
      
      // Get ticker name from sessionStorage cache or tickerNames state
      const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
      const tickerName = cachedName || tickerNames[symbol] || symbol;
      const tickerSymbol = cachedSymbol || symbol;
      
      if (!analystData && !priceDataForSymbol) {
        return undefined;
      }
      
      const currentPrice = priceDataForSymbol?.currentPrice || priceDataForSymbol?.close_price_adjusted;
      const previousPrice = priceDataForSymbol?.previousPrice || priceDataForSymbol?.previous_close_price_adjusted;
      const change = currentPrice && previousPrice ? currentPrice - previousPrice : (priceDataForSymbol?.change || 0);
      const changePercent = currentPrice && previousPrice ? (change / previousPrice) * 100 : (priceDataForSymbol?.changePercent || 0);
      
      // Calculate price target difference
      const priceTargetDifference = currentPrice && analystData?.averagePriceTarget 
        ? ((analystData.averagePriceTarget - currentPrice) / currentPrice) * 100 
        : undefined;

      return {
        // Required TickerDisplayData fields
        ticker: symbol,
        name: symbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbol,
        latest: currentPrice || 0,
        change: change,
        changePercent: changePercent,
        volume: priceDataForSymbol?.volume || 0,
        high: priceDataForSymbol?.high || 0,
        low: priceDataForSymbol?.low || 0,
        open: priceDataForSymbol?.open || 0,
        prevClose: previousPrice || 0,
        updated: priceDataForSymbol?.date?.getTime() || Date.now(),
        isSyncing: false,
        // Add all analyst view data
        ...analystData,
        // Override with calculated values
        lastClosingPrice: currentPrice,
        priceTargetDifference: priceTargetDifference,
        // Add next earnings date
        nextEarningsDate: nextEarningsDates[symbol] || null,
      } as TickerDisplayData & typeof analystData;
    }

    // For Insiders tab, get data from insiderData
    if (activeTab === 'insiders') {
      // Normalize symbol to uppercase for consistent lookup
      const normalizedSymbol = symbol.toUpperCase();
      const tickerDataForSymbol = insiderData[normalizedSymbol];
      
      // Get ticker name from sessionStorage cache or tickerNames state
      const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
      const tickerName = cachedName || tickerNames[symbol] || symbol;
      const tickerSymbol = cachedSymbol || symbol;
      
      if (!tickerDataForSymbol) {
        return undefined;
      }
      
      // Transform insider data to TickerDisplayData format (similar to ListInsiders)
      const result: any = {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: false,
        // Aggregated data
        numberOfBuyers: tickerDataForSymbol.aggregatedData?.numberOfBuyers ?? null,
        numberOfSellers: tickerDataForSymbol.aggregatedData?.numberOfSellers ?? null,
        buyersMinusSellers: tickerDataForSymbol.aggregatedData?.buyersMinusSellers ?? null,
        numberOfPurchases: tickerDataForSymbol.aggregatedData?.numberOfPurchases ?? null,
        numberOfSales: tickerDataForSymbol.aggregatedData?.numberOfSales ?? null,
        purchasesMinusSales: tickerDataForSymbol.aggregatedData?.purchasesMinusSales ?? null,
        totalSharesPurchased: tickerDataForSymbol.aggregatedData?.totalSharesPurchased ?? null,
        totalSharesSold: tickerDataForSymbol.aggregatedData?.totalSharesSold ?? null,
        sharesPurchasedMinusSharesSold: tickerDataForSymbol.aggregatedData?.sharesPurchasedMinusSharesSold ?? null,
      };
      
      // Recent transactions - store full transaction objects
      if (tickerDataForSymbol.detailedData?.recentTransactions) {
        tickerDataForSymbol.detailedData.recentTransactions.forEach((transaction: any, idx: number) => {
          result[`recentTransaction${idx + 1}`] = transaction || null;
        });
      } else {
        // Initialize all recentTransaction fields to null if data is not available
        for (let idx = 0; idx < 10; idx++) {
          result[`recentTransaction${idx + 1}`] = null;
        }
      }
      
      return result as TickerDisplayData;
    }

    // For Pros & Cons tab, get data from prosConsRows (and mark syncing)
    if (activeTab === 'proscons') {
      const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
      const tickerName = cachedName || tickerNames[symbol] || symbol;
      const tickerSymbol = cachedSymbol || symbol;

      const row = prosConsRows[symbol];

      const base: any = {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: prosConsSyncingTickers.has(symbol),
      };

      if (row?.prosCons) {
        const { pros, cons } = prosConsLineArrays(row.prosCons);
        base.pros = pros;
        base.cons = cons;
      } else {
        base.pros = null;
        base.cons = null;
      }

      return base as TickerDisplayData;
    }

    if (activeTab === 'performance') {
      const basics = tickerPerformanceBasicsMap[symbol];
      if (!basics) {
        return { ticker: symbol, company: symbol } as any;
      }
      return {
        ticker: symbol,
        company: basics.ticker_name || symbol,
        ticker_basics_id: basics.id,
      } as any;
    }

    // For other tabs, use technical data
    const tickerData = tickerTechnicalData[symbol];
    
    if (!tickerData) {
      return {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerNames[symbol] || symbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        updated: Date.now(),
        prevClose: 0,
        isSyncing: false,
      };
    }

    return {
      ticker: symbol,
      name: symbol,
      ticker_name: tickerNames[symbol] || symbol,
      latest: tickerData.price?.close_price_adjusted || 0,
      change: 0,
      changePercent: 0,
      volume: 0,
      high: 0,
      low: 0,
      open: 0,
      updated: tickerData.price?.date?.getTime() || Date.now(),
      prevClose: tickerData.price?.close_price_adjusted || 0,
      isSyncing: false,
    };
  };

  // Helper function to get statistic color
  const getStatisticColor = (statisticKey: string, ticker: TickerDisplayData): string => {
    // Handle Daily tab colors using shared utility
    if (activeTab === 'daily') {
      return getDailyStatisticColor(statisticKey, ticker, {
        marketStatus,
        isEditingTickers: false,
      });
    }

    // Handle Fundamentals tab using shared utility
    if (activeTab === 'fundamentals') {
      return getFundamentalsStatisticColor(statisticKey, ticker, {
        timeframe,
      });
    }

    // Handle Analysts tab using shared utility
    if (activeTab === 'analysts') {
      return getAnalystViewsStatisticColor(statisticKey, ticker, {
        syncingTickers: undefined,
        unsyncedTickers: undefined,
        pollingLimitReached: undefined,
      });
    }

    // Handle Insiders tab (same logic as ListInsiders)
    if (activeTab === 'insiders') {
      if (ticker.isSyncing) return 'text-text-light-neutral dark:text-text-secondary';
      
      const value = (ticker as any)[statisticKey];
      if (value === null || value === undefined) {
        return 'text-text-light-neutral dark:text-text-secondary';
      }
      
      // For difference statistics (buyers - sellers, purchases - sales, etc.)
      if (statisticKey === 'buyersMinusSellers' || 
          statisticKey === 'purchasesMinusSales' || 
          statisticKey === 'sharesPurchasedMinusSharesSold') {
        if (typeof value === 'number') {
          if (value > 0) {
            return 'text-green-light dark:text-green-500'; // Positive (more buyers/purchases)
          } else if (value < 0) {
            return 'text-red-light dark:text-red-500'; // Negative (more sellers/sales)
          }
        }
        return 'text-text-light-neutral dark:text-text-secondary'; // Zero or neutral
      }
      
      // Default neutral color for all other statistics
      return 'text-text-light-neutral dark:text-text-secondary';
    }

    // Pros & Cons: neutral color
    if (activeTab === 'proscons') {
      return 'text-text-light-neutral dark:text-text-secondary';
    }

    if (activeTab === 'performance') {
      return getPerformanceStatisticColor(statisticKey, ticker, {
        tickerPerformanceData,
        syncingTickers: performanceSyncingTickers,
        unsyncedTickers: performanceUnsyncedTickers,
        pollingLimitReached: performancePollingLimitReached,
      });
    }

    // Handle Technicals tab using shared utility
    if (activeTab === 'technicals') {
      return getTechnicalsStatisticColor(statisticKey, ticker, {
        tickerTechnicalData,
      });
    }
    
    // Default secondary color
    return 'text-text-light-neutral dark:text-text-secondary';
  };

  // Helper functions for formatting (keep formatNumber and formatPrice for other tabs)
  const formatPrice = (price: number) => `$${price.toFixed(2)}`;
  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${formatPrice(change)}`;
  };
  const formatChangePercent = (percent: number) => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };
  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };
  const formatVolume = (volume: number) => volume.toLocaleString();




  // Helper function to format statistic values
  const formatStatisticValue = (statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    // Handle Fundamentals tab statistics using shared utility
    if (activeTab === 'fundamentals') {
      return formatFundamentalsStatisticValue(statisticKey, ticker, {
        timeframe,
      });
    }

    // Handle Insiders tab (same logic as ListInsiders)
    if (activeTab === 'insiders') {
      if (ticker.isSyncing) return '-';
      const value = (ticker as any)[statisticKey];
      
      // Pro-only statistics (Transaction 1, 2, 3) - always show values on landing page
      // No need to check for locked status since landing page always shows all indicators
      
      if (value === null || value === undefined) return '-';
      
      // Check if this is a transaction detail statistic
      const isTransactionDetail = statisticKey.startsWith('recentTransaction');
      
      if (isTransactionDetail && typeof value === 'object' && value !== null) {
        // Format transaction details as multi-line text with reduced font size
        const transaction = value as any;
        const dateStr = transaction.transactionDate 
          ? new Date(transaction.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '-';
        const nameStr = transaction.name || '-';
        const positionStr = transaction.position || '-';
        
        // For recentTransaction, use Bought/Sold format
        const isPurchase = transaction.transactionCode && transaction.transactionCode.toUpperCase().startsWith('P');
        const sharesStr = transaction.totalShares 
          ? transaction.totalShares.toLocaleString('en-US', { maximumFractionDigits: 0 })
          : '-';
        const priceStr = transaction.weightedPrice !== null && transaction.weightedPrice !== undefined
          ? `$${transaction.weightedPrice.toFixed(2)}`
          : '';
        
        // Format transacted shares percentage
        let percentageStr = '';
        if (transaction.transactedSharesPercentage !== null && transaction.transactedSharesPercentage !== undefined) {
          const percentage = Math.round(transaction.transactedSharesPercentage * 100) / 100;
          percentageStr = ` (${percentage.toFixed(2)}% of holdings)`;
        }
        
        const actionStr = isPurchase 
          ? (priceStr ? `Bought ${sharesStr} at ${priceStr}${percentageStr}` : `Bought ${sharesStr}${percentageStr}`)
          : (priceStr ? `Sold ${sharesStr} at ${priceStr}${percentageStr}` : `Sold ${sharesStr}${percentageStr}`);
        
        // Color purchase transactions green, sales remain default (black)
        const textColorClass = isPurchase ? 'text-green-light dark:text-green-500' : '';
        
        return (
          <div className={`text-xs leading-tight ${textColorClass}`}>
            <div>{dateStr}</div>
            <div>{nameStr}</div>
            <div>{positionStr}</div>
            <div>{actionStr}</div>
          </div>
        );
      }
      
      if (typeof value === 'number') {
        // Format all numeric values with locale string (no decimals for counts/shares)
        // Important: 0 is a valid value, so we need to check for null/undefined, not falsy
        return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      return String(value);
    }

    // Handle Pros & Cons tab (numbered list, wrap like ListProsCons)
    if (activeTab === 'proscons') {
      if ((ticker as any).isSyncing) {
        return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" title="Syncing..." />;
      }
      const value = (ticker as any)[statisticKey];
      if (statisticKey === 'pros' || statisticKey === 'cons') {
        if (Array.isArray(value) && value.length > 0) {
          return <ProsConsNumberedList lines={value} />;
        }
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
          return <span className="text-gray-400 dark:text-text-secondary">-</span>;
        }
      }
      if (value === null || value === undefined || value === '') {
        return <span className="text-gray-400 dark:text-text-secondary">-</span>;
      }
      return (
        <div className="text-xs leading-tight whitespace-pre-wrap break-words">
          {String(value)}
        </div>
      );
    }

    // Handle Daily tab statistics using shared utility
    if (activeTab === 'daily') {
      return formatDailyStatisticValue(statisticKey, ticker, {
        marketStatus,
        isEditingTickers: false,
      });
    }

    // Handle Analysts tab using shared utility
    if (activeTab === 'analysts') {
      return formatAnalystViewsStatisticValue(statisticKey, ticker, {});
    }

    if (activeTab === 'performance') {
      return formatPerformanceStatisticValue(statisticKey, ticker, {
        tickerPerformanceData,
        syncingTickers: performanceSyncingTickers,
        unsyncedTickers: performanceUnsyncedTickers,
        pollingLimitReached: performancePollingLimitReached,
      });
    }

    // Handle Technicals tab using shared utility
    if (activeTab === 'technicals') {
      return formatTechnicalsStatisticValue(statisticKey, ticker, {
        tickerTechnicalData,
      });
    }
    
    // Default fallback
    return '-';
  };

  const clearProsConsPollingInterval = () => {
    if (prosConsSyncPollingIntervalRef.current) {
      clearInterval(prosConsSyncPollingIntervalRef.current);
      prosConsSyncPollingIntervalRef.current = null;
    }
  };

  const checkProsConsEmbeddedPoll = async (id: number, tickerSymbol: string, attempt: number) => {
    if (prosConsPollingStoppedRef.current) return;
    if (prosConsIsCheckingSyncRef.current) return;
    prosConsIsCheckingSyncRef.current = true;
    try {
      const resp = await tickerApi.checkProsAndConsSyncStatus({ ticker_basics_ids: [id] });
      const item = resp?.success ? resp?.data?.tickers?.[0] : undefined;
      const syncing = Boolean(item?.is_syncing && !item?.is_synced);
      setProsConsIsSyncing(syncing);
      if (item?.last_sync_ended_at) {
        const d = new Date(item.last_sync_ended_at);
        if (!isNaN(d.getTime())) setProsConsTickerLastUpdated(d);
      }
      const histResp = await prosConsHistoryApi.getProsAndConsHistory({
        tickerBasicsId: id,
        limit: 6,
        ownerHasProSubscription: true,
      });
      const rows: any[] = histResp?.success && Array.isArray(histResp?.data) ? histResp.data : [];
      setProsConsHistory(
        rows.map((r: any) => ({
          year: r.year ?? null,
          month: r.month ?? null,
          prosCons: r.prosCons ?? null,
        })),
      );

      if (syncing && attempt < 10) {
        clearProsConsPollingInterval();
        prosConsSyncPollingIntervalRef.current = setInterval(() => {
          if (prosConsPollingStoppedRef.current) return;
          if (prosConsIsCheckingSyncRef.current) return;
          const next = prosConsCurrentAttemptRef.current + 1;
          if (next > 10) {
            prosConsPollingStoppedRef.current = true;
            clearProsConsPollingInterval();
            setProsConsIsSyncing(false);
            return;
          }
          prosConsCurrentAttemptRef.current = next;
          void checkProsConsEmbeddedPoll(id, tickerSymbol, next);
        }, 5000);
      } else if (!syncing) {
        setProsConsIsSyncing(false);
      }
    } catch (e: any) {
      setProsConsTickerError(e?.message ?? String(e));
    } finally {
      prosConsIsCheckingSyncRef.current = false;
    }
  };

  const runProsConsEmbeddedOpen = async (tickerSymbol: string) => {
    try {
      const sym = tickerSymbol.toUpperCase();
      const syncResp = await tickerApi.checkTickerSyncStatus({ ticker_symbols: [sym] });
      const bi = syncResp?.data?.tickers?.find(
        (t: any) => (t.ticker_symbol || '').toUpperCase() === sym,
      );
      if (!bi?.ticker_basics_id) {
        throw new Error(`Ticker ${tickerSymbol} not found`);
      }
      const id = bi.ticker_basics_id;
      prosConsResolvedIdRef.current = id;
      setProsConsTickerBasicsId(id);
      const name = bi.ticker_name || tickerSymbol;
      const ts = bi.ticker_symbol || tickerSymbol;
      setProsConsTickerName(name);
      setProsConsTickerSymbol(ts);
      setCurrentEmbeddedTickerName(name);
      setCurrentEmbeddedTickerSymbol(ts);
      sessionStorage.setItem(`ticker-basics-id-${tickerSymbol}`, String(id));
      sessionStorage.setItem(`ticker-name-${tickerSymbol}`, name);
      sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, ts);
      prosConsCurrentAttemptRef.current = 1;
      await checkProsConsEmbeddedPoll(id, tickerSymbol, 1);
    } catch (e: any) {
      setProsConsTickerError(e?.message ?? String(e));
    } finally {
      setProsConsTickerLoading(false);
    }
  };

  const handleTickerClick = (tickerSymbol: string) => {
    // If we're on the Daily tab, show embedded daily view instead of navigating
    if (activeTab === 'daily') {
      setSelectedTickerForDailyView(tickerSymbol);
      setDailyTickerLoading(true);
      setDailyTickerError(null);
      setAllDailyPrices([]);
      setHasMoreDailyData(true);
      dailyCurrentPageRef.current = 0;
      
      // Fetch daily prices data
      fetchDailyTickerData(tickerSymbol, 0, 50, dailyTimeframe, dailySortColumn, dailySortDirection);
    } else if (activeTab === 'technicals') {
      // If we're on the Technicals tab, show embedded technicals view instead of navigating
      setSelectedTickerForTechnicalsView(tickerSymbol);
      setTechnicalsTickerLoading(true);
      setTechnicalsTickerError(null);
      
      // Fetch technicals data
      fetchTechnicalsTickerData(tickerSymbol);
    } else if (activeTab === 'performance') {
      openPerformanceEmbeddedViewRef.current?.(tickerSymbol);
    } else if (activeTab === 'fundamentals') {
      // If we're on the Fundamentals tab, show embedded fundamentals view instead of navigating
      setSelectedTickerForFundamentalsView(tickerSymbol);
      // Preserve the current timeframe from the list view
      setFundamentalsTimeframe(timeframe);
      setFundamentalsTickerLoading(true);
      setFundamentalsTickerError(null);
      
      // Reset polling state
      fundamentalsPollingStoppedRef.current = false;
      setFundamentalsPollingLimitReached(false);
      setFundamentalsSyncPollingAttempts(0);
      fundamentalsCurrentAttemptRef.current = 1;
      fundamentalsIsCheckingSyncRef.current = false;
      clearFundamentalsPollingInterval();
      
      // Fetch fundamentals data
      checkFundamentalsSyncAndFetchData(tickerSymbol, 1);
        } else if (activeTab === 'analysts') {
      // If we're on the Analysts tab, show embedded analyst views view instead of navigating
      setSelectedTickerForAnalystViewsView(tickerSymbol);
      setAnalystViewsTickerLoading(true);
      setAnalystViewsTickerError(null);
      
      // Reset polling state
      analystViewsPollingStoppedRef.current = false;
      setAnalystViewsPollingLimitReached(false);
      setAnalystViewsSyncPollingAttempts(0);
      analystViewsCurrentAttemptRef.current = 1;
      analystViewsIsCheckingSyncRef.current = false;
      clearAnalystViewsPollingInterval();
      
      // Fetch analyst views data
      checkAnalystViewsSyncAndFetchData(tickerSymbol, 1);
    } else if (activeTab === 'insiders') {
      // If we're on the Insiders tab, show embedded insiders view instead of navigating
      setSelectedTickerForInsidersView(tickerSymbol);
      setInsidersTickerLoading(true);
      setInsidersTickerError(null);
      
      // Reset polling state
      insidersPollingStoppedRef.current = false;
      setInsidersPollingLimitReached(false);
      setInsidersSyncPollingAttempts(0);
      insidersCurrentAttemptRef.current = 1;
      insidersIsCheckingSyncRef.current = false;
      clearInsidersPollingInterval();
      
      // Fetch insiders data
      checkInsidersSyncAndFetchData(tickerSymbol, 1);
    } else if (activeTab === 'proscons') {
      setSelectedTickerForProsConsView(tickerSymbol);
      setProsConsTickerLoading(true);
      setProsConsTickerError(null);
      setProsConsHistory([]);
      prosConsPollingStoppedRef.current = false;
      prosConsResolvedIdRef.current = null;
      prosConsCurrentAttemptRef.current = 1;
      prosConsIsCheckingSyncRef.current = false;
      clearProsConsPollingInterval();
      void runProsConsEmbeddedOpen(tickerSymbol);
    } else {
      // For other tabs, navigate to technicals page
      navigate('/technicals');
    }
  };
  
  // Fetch daily ticker data
  const fetchDailyTickerData = async (
    tickerSymbol: string,
    page: number = 0,
    limit: number = 50,
    timeframe: string = dailyTimeframe,
    sortColumn: string = dailySortColumn,
    sortDirection: string = dailySortDirection
  ) => {
    try {
      setDailyTickerLoading(page === 0);
      setLoadingMoreDailyData(page > 0);
      
      // Check sync status to get last_sync_ended_at (only on first page)
      if (page === 0) {
        try {
          const syncResponse = await tickerApi.checkTickerSyncStatus({
            ticker_symbols: [tickerSymbol],
          });
          
          const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
          if (tickerInfo && tickerInfo.last_sync_ended_at) {
            const date = typeof tickerInfo.last_sync_ended_at === 'string' 
              ? new Date(tickerInfo.last_sync_ended_at) 
              : tickerInfo.last_sync_ended_at;
            if (date instanceof Date && !isNaN(date.getTime())) {
              setDailyTickerLastUpdated(date);
            }
          }
        } catch (syncError) {
          console.error('Error fetching sync status for daily ticker:', syncError);
          // Continue with fetching daily prices even if sync check fails
        }
      }
      
      const response = await tickerApi.getTickerDailyPrices(
        tickerSymbol,
        page,
        limit,
        timeframe,
        sortColumn,
        sortDirection
      );
      
      if (page === 0) {
        setDailyTickerData(response.data);
        // Update shared cache for persistent display across tab switches
        if (response.data?.ticker_name) {
          setCurrentEmbeddedTickerName(response.data.ticker_name);
        }
        if (tickerSymbol) {
          setCurrentEmbeddedTickerSymbol(tickerSymbol);
        }
        setAllDailyPrices(response.data.daily_prices || []);
      } else {
        setAllDailyPrices(prev => [...prev, ...(response.data.daily_prices || [])]);
      }
      
      setHasMoreDailyData(response.meta.hasMore);
      dailyCurrentPageRef.current = page;
      setDailyTickerError(null);
    } catch (error) {
      console.error('Error fetching daily ticker data:', error);
      setDailyTickerError(error instanceof Error ? error.message : 'Failed to fetch daily data');
    } finally {
      setDailyTickerLoading(false);
      setLoadingMoreDailyData(false);
    }
  };
  
  // Load more daily data
  const loadMoreDailyData = useCallback(async () => {
    if (!selectedTickerForDailyView || loadingMoreDailyData || !hasMoreDailyData) {
      return;
    }
    
    const nextPage = dailyCurrentPageRef.current + 1;
    await fetchDailyTickerData(
      selectedTickerForDailyView,
      nextPage,
      50,
      dailyTimeframe,
      dailySortColumn,
      dailySortDirection
    );
  }, [selectedTickerForDailyView, loadingMoreDailyData, hasMoreDailyData, dailyTimeframe, dailySortColumn, dailySortDirection]);
  
  // Handle daily timeframe change
  const handleDailyTimeframeChange = (newTimeframe: Timeframe) => {
    setDailyTimeframe(newTimeframe);
    setIsDailyDropdownOpen(false);
    if (selectedTickerForDailyView) {
      setAllDailyPrices([]);
      setHasMoreDailyData(true);
      dailyCurrentPageRef.current = 0;
      fetchDailyTickerData(selectedTickerForDailyView, 0, 50, newTimeframe, dailySortColumn, dailySortDirection);
    }
  };
  
  // Use full screen hook for keyboard support
  useFullScreen(isDailyFullScreen, () => setIsDailyFullScreen(false));
  
  // Setup scroll handler for full screen table
  useEffect(() => {
    if (isDailyFullScreen && dailyFullScreenTableRef.current) {
      const handleFullScreenScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = dailyFullScreenTableRef.current!;
        if (scrollTop + clientHeight >= scrollHeight - 5 && hasMoreDailyData && !loadingMoreDailyData) {
          loadMoreDailyData();
        }
      };

      dailyFullScreenTableRef.current.addEventListener('scroll', handleFullScreenScroll);
      
      return () => {
        if (dailyFullScreenTableRef.current) {
          dailyFullScreenTableRef.current.removeEventListener('scroll', handleFullScreenScroll);
        }
      };
    }
  }, [isDailyFullScreen, hasMoreDailyData, loadingMoreDailyData, loadMoreDailyData]);
  
  // Handle clicking outside dropdown - handled by TimeframeDropdown component
  
  // Handle daily sort
  const handleDailySort = (column: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted') => {
    const newDirection = dailySortColumn === column 
      ? (dailySortDirection === 'desc' ? 'asc' : 'desc')
      : 'desc';
    
    setDailySortColumn(column);
    setDailySortDirection(newDirection);
    
    if (selectedTickerForDailyView) {
      setAllDailyPrices([]);
      setHasMoreDailyData(true);
      dailyCurrentPageRef.current = 0;
      fetchDailyTickerData(selectedTickerForDailyView, 0, 50, dailyTimeframe, column, newDirection);
    }
  };
  
  // Close embedded daily view
  const handleCloseDailyView = () => {
    // Check if other embedded views are active before clearing
    const hasOtherViews = !!(selectedTickerForTechnicalsView || selectedTickerForFundamentalsView || selectedTickerForAnalystViewsView || selectedTickerForInsidersView || selectedTickerForProsConsView || selectedTickerForPerformanceView);
    
    setSelectedTickerForDailyView(null);
    setDailyTickerData(null);
    setAllDailyPrices([]);
    setDailyTickerError(null);
    setIsDailyFullScreen(false);
    setIsDailyDropdownOpen(false);
    
    // Clear cache only if no other embedded view is active
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };
  
  // Date columns for technicals table (imported from tickerTechnicalsUtils)
  
  // Fetch technicals ticker data
  const fetchTechnicalsTickerData = async (tickerSymbol: string) => {
    try {
      setTechnicalsTickerLoading(true);
      setTechnicalsTickerError(null);
      
      // Get ticker sync status to get ticker_basics_id
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [tickerSymbol],
      });
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
      if (!tickerInfo) {
        throw new Error(`Ticker ${tickerSymbol} not found`);
      }
      
      const name = tickerInfo.ticker_name || tickerSymbol;
      const symbol = tickerInfo.ticker_symbol || tickerSymbol;
      setTechnicalsTickerName(name);
      setTechnicalsTickerSymbol(symbol);
      // Update shared cache for persistent display across tab switches
      setCurrentEmbeddedTickerName(name);
      setCurrentEmbeddedTickerSymbol(symbol);
      sessionStorage.setItem(`ticker-name-${tickerSymbol}`, name);
      sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, symbol);
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setTechnicalsTickerLastUpdated(date);
        }
      }
      
      const tickerBasicsId = tickerInfo.ticker_basics_id;
      
      if (!tickerInfo.is_synced) {
        throw new Error(`Ticker ${tickerSymbol} is not synced yet`);
      }
      
      // Fetch technical data with multiple days ago
      // Landing page should show all indicators (ownerHasProSubscription: true)
      const daysAgoArray = TECHNICALS_DATE_COLUMNS.map(col => col.daysAgo);
      const response = await tickerApi.getTickerTechnical({
        tickerBasicsIds: [tickerBasicsId],
        daysAgoArray: daysAgoArray,
        ownerHasProSubscription: true, // Landing page should show all indicators
      });
      
      if (response.data && response.data.length > 0) {
        setTechnicalsTickerData(response.data[0]);
        
        // Extract dates for each daysAgo
        const dateMap: { [daysAgo: number]: string } = {};
        response.data[0].indicators.forEach((indicator: any) => {
          if (!dateMap[indicator.daysAgo]) {
            dateMap[indicator.daysAgo] = new Date(indicator.date).toLocaleDateString();
          }
        });
        setTechnicalsDates(dateMap);
        
        // Fetch daily prices for closing price calculations
        await fetchTechnicalsDailyPrices(tickerSymbol, tickerBasicsId);
      } else {
        throw new Error('No technical data found for this ticker');
      }
      
      setTechnicalsTickerError(null);
    } catch (error) {
      console.error('Error fetching technicals ticker data:', error);
      setTechnicalsTickerError(error instanceof Error ? error.message : 'Failed to fetch technicals data');
    } finally {
      setTechnicalsTickerLoading(false);
    }
  };
  
  // Fetch daily prices for technicals view
  const fetchTechnicalsDailyPrices = async (tickerSymbol: string, tickerBasicsId: number) => {
    try {
      const daysAgoArray = TECHNICALS_DATE_COLUMNS.map(col => col.daysAgo);
      const response = await tickerApi.getTickerDailyPrices(
        tickerSymbol, 
        0, // page
        100, // limit - get enough to cover all days
        '5years', // timeframe
        'date', // sortColumn
        'desc' // sortDirection
      );

      // Create a map of daysAgo to price data
      const priceMap: { [daysAgo: number]: any } = {};
      
      // Since daily_prices is sorted by date descending (most recent first),
      // we can use the index directly to match daysAgo
      daysAgoArray.forEach(daysAgo => {
        if (daysAgo < response.data.daily_prices.length) {
          const priceData = response.data.daily_prices[daysAgo];
          priceMap[daysAgo] = {
            date: priceData.date,
            close_price_adjusted: priceData.close_price_adjusted
          };
        }
      });
      
      setTechnicalsDailyPrices(priceMap);
    } catch (error) {
      console.error('Error fetching daily prices for technicals:', error);
      // Don't throw error, just log it - technical data can still be displayed
    }
  };
  
  // Close embedded technicals view
  const handleCloseTechnicalsView = () => {
    // Check if other embedded views are active before clearing
    const hasOtherViews = !!(selectedTickerForDailyView || selectedTickerForFundamentalsView || selectedTickerForAnalystViewsView || selectedTickerForInsidersView || selectedTickerForProsConsView || selectedTickerForPerformanceView);
    
    setSelectedTickerForTechnicalsView(null);
    setTechnicalsTickerData(null);
    setTechnicalsDates({});
    setTechnicalsDailyPrices({});
    setTechnicalsTickerError(null);
    setIsTechnicalsFullScreen(false);
    
    // Clear cache only if no other embedded view is active
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };

  const clearPerformanceEmbeddedPolling = useCallback(() => {
    if (performanceEmbeddedPollingRef.current) {
      clearInterval(performanceEmbeddedPollingRef.current);
      performanceEmbeddedPollingRef.current = null;
    }
  }, []);

  const stopPerformanceEmbeddedPolling = useCallback(() => {
    performanceEmbeddedStoppedRef.current = true;
    clearPerformanceEmbeddedPolling();
  }, [clearPerformanceEmbeddedPolling]);

  const fetchPerformanceEmbeddedHistory = useCallback(
    async (basicsId: number) => {
      const resp = await performanceApi.getTickerPerformanceHistory({ tickerBasicsId: basicsId, limit: 6 });
      const rows: PerformanceHistoryRow[] = (resp as any)?.data || [];
      setPerformanceEmbeddedHistory(rows);
      const dates = rows
        .map((r) => (r.date ? new Date(r.date as any) : null))
        .filter((d): d is Date => !!d && !isNaN(d.getTime()));
      if (dates.length > 0) {
        setPerformanceEmbeddedLastUpdated(new Date(Math.max(...dates.map((d) => d.getTime()))));
      }
    },
    [performanceApi],
  );

  const checkPerformanceEmbeddedAndFetch = useCallback(
    async (tickerSymbol: string, attemptNumber: number = 1) => {
      if (performanceEmbeddedStoppedRef.current) return;
      if (performanceEmbeddedCheckingRef.current) return;
      performanceEmbeddedCheckingRef.current = true;
      try {
        const symbol = String(tickerSymbol).toUpperCase();
        const syncResponse = await performanceApi.checkTickerSyncStatus({ ticker_symbols: [symbol] });
        const status = syncResponse?.data?.tickers?.[0];
        if (!status) {
          setPerformanceEmbeddedError('Invalid sync status response');
          stopPerformanceEmbeddedPolling();
          return;
        }

        if (status.ticker_name) {
          setCurrentEmbeddedTickerName(status.ticker_name);
          sessionStorage.setItem(`ticker-name-${symbol}`, status.ticker_name);
        }
        if (status.ticker_symbol) {
          setCurrentEmbeddedTickerSymbol(status.ticker_symbol);
          sessionStorage.setItem(`ticker-symbol-${symbol}`, status.ticker_symbol);
        }

        if (status.is_synced) {
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
          stopPerformanceEmbeddedPolling();
          if (status.last_sync_ended_at) {
            const d = new Date(status.last_sync_ended_at);
            if (!isNaN(d.getTime())) {
              setPerformanceEmbeddedLastUpdated(d);
            }
          }
          await fetchPerformanceEmbeddedHistory(status.ticker_basics_id);
          return;
        }

        setPerformanceEmbeddedIsSyncing(true);
        if (attemptNumber >= 20) {
          setPerformanceEmbeddedPollingLimit(true);
          setPerformanceEmbeddedIsSyncing(false);
          stopPerformanceEmbeddedPolling();
          return;
        }

        clearPerformanceEmbeddedPolling();
        performanceEmbeddedStoppedRef.current = false;
        performanceEmbeddedAttemptRef.current = attemptNumber;
        performanceEmbeddedPollingRef.current = setInterval(() => {
          if (performanceEmbeddedStoppedRef.current) {
            clearPerformanceEmbeddedPolling();
            return;
          }
          const nextAttempt = performanceEmbeddedAttemptRef.current + 1;
          performanceEmbeddedAttemptRef.current = nextAttempt;
          void checkPerformanceEmbeddedAndFetch(tickerSymbol, nextAttempt);
        }, 2000);
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response?.status === 429) {
          setPerformanceEmbeddedError('Rate limit exceeded. Please try again later.');
        } else {
          setPerformanceEmbeddedError(err?.message || 'Failed to load performance data');
        }
        stopPerformanceEmbeddedPolling();
      } finally {
        performanceEmbeddedCheckingRef.current = false;
        setPerformanceEmbeddedLoading(false);
      }
    },
    [performanceApi, fetchPerformanceEmbeddedHistory, clearPerformanceEmbeddedPolling, stopPerformanceEmbeddedPolling],
  );
  
  // Close embedded performance view
  const handleClosePerformanceView = useCallback(() => {
    const hasOtherViews = !!(
      selectedTickerForDailyView ||
      selectedTickerForTechnicalsView ||
      selectedTickerForFundamentalsView ||
      selectedTickerForAnalystViewsView ||
      selectedTickerForInsidersView ||
      selectedTickerForProsConsView
    );
    performanceEmbeddedStoppedRef.current = true;
    clearPerformanceEmbeddedPolling();
    setSelectedTickerForPerformanceView(null);
    setPerformanceEmbeddedHistory([]);
    setPerformanceEmbeddedError(null);
    setIsPerformanceFullScreen(false);
    setPerformanceEmbeddedIsSyncing(false);
    setPerformanceEmbeddedPollingLimit(false);
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  }, [
    clearPerformanceEmbeddedPolling,
    selectedTickerForDailyView,
    selectedTickerForTechnicalsView,
    selectedTickerForFundamentalsView,
    selectedTickerForAnalystViewsView,
    selectedTickerForInsidersView,
    selectedTickerForProsConsView,
  ]);

  useEffect(() => {
    if (activeTab !== 'performance' && selectedTickerForPerformanceView) {
      handleClosePerformanceView();
    }
  }, [activeTab, selectedTickerForPerformanceView, handleClosePerformanceView]);

  openPerformanceEmbeddedViewRef.current = (tickerSymbol: string) => {
    setSelectedTickerForPerformanceView(tickerSymbol);
    setPerformanceEmbeddedLoading(true);
    setPerformanceEmbeddedError(null);
    setPerformanceEmbeddedHistory([]);
    setPerformanceEmbeddedLastUpdated(null);
    performanceEmbeddedStoppedRef.current = false;
    setPerformanceEmbeddedPollingLimit(false);
    setPerformanceEmbeddedIsSyncing(false);
    performanceEmbeddedAttemptRef.current = 1;
    clearPerformanceEmbeddedPolling();
    void checkPerformanceEmbeddedAndFetch(tickerSymbol, 1);
  };
  
  // Use full screen hook for technicals keyboard support
  useFullScreen(isTechnicalsFullScreen, () => setIsTechnicalsFullScreen(false));
  useFullScreen(isPerformanceFullScreen, () => setIsPerformanceFullScreen(false));
  
  // Fundamentals polling helper functions
  const clearFundamentalsPollingInterval = () => {
    if (fundamentalsSyncPollingIntervalRef.current) {
      clearInterval(fundamentalsSyncPollingIntervalRef.current);
      fundamentalsSyncPollingIntervalRef.current = null;
    }
  };

  const stopFundamentalsPolling = () => {
    fundamentalsPollingStoppedRef.current = true;
    clearFundamentalsPollingInterval();
    setFundamentalsIsSyncing(false);
  };

  // Generate quarterly columns for fundamentals using shared utility
  const generateFundamentalsQuarterlyColumns = async (financials: any[], ratios: any[], tickerSymbol?: string) => {
    // Fetch latest daily date if ticker symbol is available
    let latestDailyDate: Date | null = null;
    if (tickerSymbol) {
      try {
        const dailyResponse = await tickerApi.getTickerDailyPrices(tickerSymbol, 0, 1, undefined, 'date', 'desc');
        if (dailyResponse.data?.daily_prices && dailyResponse.data.daily_prices.length > 0) {
          const latestDaily = dailyResponse.data.daily_prices[0];
          if (latestDaily.date) {
            latestDailyDate = new Date(latestDaily.date);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch latest daily date for quarterly columns:', error);
        // Continue without latest daily date
      }
    }
    const columns = generateQuarterlyColumnsUtil(financials, ratios, latestDailyDate);
    setFundamentalsQuarterlyColumns(columns);
  };

  // Generate annual columns for fundamentals using shared utility
  const generateFundamentalsAnnualColumns = (financials: any[], ratios: any[]) => {
    const columns = generateAnnualColumnsUtil(financials, ratios);
    setFundamentalsAnnualColumns(columns);
  };

  // Check sync status and fetch fundamentals data (same sequence as TickerFundamentals)
  const checkFundamentalsSyncAndFetchData = async (tickerSymbol: string, attemptNumber: number = 1) => {
    if (!tickerSymbol) return;
    
    if (attemptNumber > 5) {
      console.warn(`Landing: Fundamentals attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopFundamentalsPolling();
      setFundamentalsPollingLimitReached(true);
      fundamentalsIsCheckingSyncRef.current = false;
      return;
    }
    
    if (fundamentalsPollingStoppedRef.current) {
      console.log(`Landing: checkFundamentalsSyncAndFetchData called but polling is stopped, ignoring`);
      fundamentalsIsCheckingSyncRef.current = false;
      return;
    }
    
    if (fundamentalsIsCheckingSyncRef.current) {
      console.log(`Landing: checkFundamentalsSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    fundamentalsIsCheckingSyncRef.current = true;
    setFundamentalsSyncPollingAttempts(attemptNumber);

    try {
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [tickerSymbol],
      });
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
      if (!tickerInfo) {
        throw new Error(`Ticker ${tickerSymbol} not found`);
      }
      
      setFundamentalsTickerBasicsId(tickerInfo.ticker_basics_id);
      const name = tickerInfo.ticker_name || tickerSymbol;
      const symbol = tickerInfo.ticker_symbol || tickerSymbol;
      setFundamentalsTickerName(name);
      setFundamentalsTickerSymbol(symbol);
      // Update shared cache for persistent display across tab switches
      setCurrentEmbeddedTickerName(name);
      setCurrentEmbeddedTickerSymbol(symbol);
      sessionStorage.setItem(`ticker-name-${tickerSymbol}`, name);
      sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, symbol);
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setFundamentalsTickerLastUpdated(date);
        }
      }
      
      if (tickerInfo.is_synced) {
        console.log(`Landing: ${tickerSymbol} is synced, fetching fundamentals data`);
        
        const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
          tickerApi.getPast2YearsQuarterlyFinancials(tickerInfo.ticker_basics_id),
          tickerApi.getPast2YearsQuarterlyRatios(tickerInfo.ticker_basics_id)
        ]);

        const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
          tickerApi.getPast5YearsAnnualFinancials(tickerInfo.ticker_basics_id),
          tickerApi.getPast5YearsAnnualRatios(tickerInfo.ticker_basics_id)
        ]);

        if (quarterlyFinancialsResponse.success) {
          setFundamentalsQuarterlyFinancials(quarterlyFinancialsResponse.data);
        }
        
        if (quarterlyRatiosResponse.success) {
          setFundamentalsQuarterlyRatios(quarterlyRatiosResponse.data);
        }

        if (annualFinancialsResponse.success) {
          setFundamentalsAnnualFinancials(annualFinancialsResponse.data);
        }
        
        if (annualRatiosResponse.success) {
          setFundamentalsAnnualRatios(annualRatiosResponse.data);
        }

        await generateFundamentalsQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, tickerSymbol);
        generateFundamentalsAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
        
        stopFundamentalsPolling();
        setFundamentalsTickerLoading(false);
        setFundamentalsTickerError(null);
        fundamentalsIsCheckingSyncRef.current = false;
      } else {
        if (attemptNumber >= 5) {
          console.warn(`Sync polling limit reached (5 attempts) for ticker: ${tickerSymbol}. Attempting to fetch data anyway.`);
          setFundamentalsPollingLimitReached(true);
          stopFundamentalsPolling();
          
          try {
            const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
              tickerApi.getPast2YearsQuarterlyFinancials(tickerInfo.ticker_basics_id),
              tickerApi.getPast2YearsQuarterlyRatios(tickerInfo.ticker_basics_id)
            ]);

            const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
              tickerApi.getPast5YearsAnnualFinancials(tickerInfo.ticker_basics_id),
              tickerApi.getPast5YearsAnnualRatios(tickerInfo.ticker_basics_id)
            ]);

            if (quarterlyFinancialsResponse.success) {
              setFundamentalsQuarterlyFinancials(quarterlyFinancialsResponse.data);
            }
            
            if (quarterlyRatiosResponse.success) {
              setFundamentalsQuarterlyRatios(quarterlyRatiosResponse.data);
            }

            if (annualFinancialsResponse.success) {
              setFundamentalsAnnualFinancials(annualFinancialsResponse.data);
            }
            
            if (annualRatiosResponse.success) {
              setFundamentalsAnnualRatios(annualRatiosResponse.data);
            }

            await generateFundamentalsQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, tickerSymbol);
            generateFundamentalsAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
            
            setFundamentalsTickerLoading(false);
            setFundamentalsTickerError(null);
          } catch (fetchErr) {
            console.error(`Landing: Failed to fetch fundamentals data for ${tickerSymbol} after polling limit:`, fetchErr);
            setFundamentalsTickerError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
            setFundamentalsTickerLoading(false);
          }
          fundamentalsIsCheckingSyncRef.current = false;
        } else {
          if (fundamentalsPollingStoppedRef.current) {
            console.log(`Landing: Polling stopped, not setting up interval`);
            fundamentalsIsCheckingSyncRef.current = false;
            return;
          }
          
          console.log(`Landing: ${tickerSymbol} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
          setFundamentalsIsSyncing(true);
          
          fundamentalsCurrentAttemptRef.current = attemptNumber;
          
          const pollingDelay = 2000;
          clearFundamentalsPollingInterval();
          
          const interval = setInterval(() => {
            if (fundamentalsPollingStoppedRef.current) {
              clearInterval(interval);
              fundamentalsSyncPollingIntervalRef.current = null;
              return;
            }
            
            const nextAttempt = fundamentalsCurrentAttemptRef.current + 1;
            
            if (nextAttempt > 5) {
              console.log(`Landing: Reached attempt limit (5), stopping interval`);
              clearInterval(interval);
              fundamentalsSyncPollingIntervalRef.current = null;
              fundamentalsPollingStoppedRef.current = true;
              return;
            }
            
            fundamentalsCurrentAttemptRef.current = nextAttempt;
            checkFundamentalsSyncAndFetchData(tickerSymbol, nextAttempt);
          }, pollingDelay);
          
          fundamentalsSyncPollingIntervalRef.current = interval;
          fundamentalsIsCheckingSyncRef.current = false;
        }
      }
    } catch (err) {
      console.error(`Landing: Error in checkFundamentalsSyncAndFetchData:`, err);
      
      const isRateLimitError = 
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`Landing: Rate limit (429) detected for ${tickerSymbol}. Stopping polling and attempting to fetch data anyway.`);
        stopFundamentalsPolling();
        setFundamentalsPollingLimitReached(true);
        
        try {
          if (fundamentalsTickerBasicsId) {
            const [quarterlyFinancialsResponse, quarterlyRatiosResponse] = await Promise.all([
              tickerApi.getPast2YearsQuarterlyFinancials(fundamentalsTickerBasicsId),
              tickerApi.getPast2YearsQuarterlyRatios(fundamentalsTickerBasicsId)
            ]);

            const [annualFinancialsResponse, annualRatiosResponse] = await Promise.all([
              tickerApi.getPast5YearsAnnualFinancials(fundamentalsTickerBasicsId),
              tickerApi.getPast5YearsAnnualRatios(fundamentalsTickerBasicsId)
            ]);

            if (quarterlyFinancialsResponse.success) {
              setFundamentalsQuarterlyFinancials(quarterlyFinancialsResponse.data);
            }
            
            if (quarterlyRatiosResponse.success) {
              setFundamentalsQuarterlyRatios(quarterlyRatiosResponse.data);
            }

            if (annualFinancialsResponse.success) {
              setFundamentalsAnnualFinancials(annualFinancialsResponse.data);
            }
            
            if (annualRatiosResponse.success) {
              setFundamentalsAnnualRatios(annualRatiosResponse.data);
            }

            await generateFundamentalsQuarterlyColumns(quarterlyFinancialsResponse.data, quarterlyRatiosResponse.data, tickerSymbol);
            generateFundamentalsAnnualColumns(annualFinancialsResponse.data, annualRatiosResponse.data);
            
            setFundamentalsTickerLoading(false);
            setFundamentalsTickerError(null);
          } else {
            throw new Error('Ticker basics ID not available');
          }
        } catch (fetchErr) {
          console.error(`Landing: Failed to fetch fundamentals data for ${tickerSymbol} after rate limit error:`, fetchErr);
          setFundamentalsTickerError('Rate limit exceeded. Please try again later.');
          setFundamentalsTickerLoading(false);
        }
        fundamentalsIsCheckingSyncRef.current = false;
      } else {
        setFundamentalsTickerError(err instanceof Error ? err.message : 'Failed to fetch ticker data');
        stopFundamentalsPolling();
        setFundamentalsTickerLoading(false);
        fundamentalsIsCheckingSyncRef.current = false;
      }
    }
  };

  // Cleanup fundamentals polling on unmount or when ticker changes
  useEffect(() => {
    return () => {
      stopFundamentalsPolling();
    };
  }, []);

  // Close embedded fundamentals view
  const handleCloseFundamentalsView = () => {
    // Check if other embedded views are active before clearing
    const hasOtherViews = !!(selectedTickerForDailyView || selectedTickerForTechnicalsView || selectedTickerForAnalystViewsView || selectedTickerForInsidersView || selectedTickerForProsConsView || selectedTickerForPerformanceView);
    
    setSelectedTickerForFundamentalsView(null);
    setFundamentalsQuarterlyFinancials([]);
    setFundamentalsQuarterlyRatios([]);
    setFundamentalsAnnualFinancials([]);
    setFundamentalsAnnualRatios([]);
    setFundamentalsQuarterlyColumns([]);
    setFundamentalsAnnualColumns([]);
    setFundamentalsTickerError(null);
    setFundamentalsTickerBasicsId(null);
    setIsFundamentalsFullScreen(false);
    stopFundamentalsPolling();
    
    // Clear cache only if no other embedded view is active
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };
  
  // Use full screen hook for fundamentals keyboard support
  useFullScreen(isFundamentalsFullScreen, () => setIsFundamentalsFullScreen(false));
  
  // Function to clear analyst views polling interval
  const clearAnalystViewsPollingInterval = () => {
    if (analystViewsSyncPollingIntervalRef.current) {
      clearInterval(analystViewsSyncPollingIntervalRef.current);
      analystViewsSyncPollingIntervalRef.current = null;
    }
  };

  // Stop analyst views polling
  const stopAnalystViewsPolling = () => {
    analystViewsPollingStoppedRef.current = true;
    clearAnalystViewsPollingInterval();
    setAnalystViewsIsSyncing(false);
  };

  // Fetch price data for analyst views ticker
  const fetchAnalystViewsTickerPriceData = async (tickerSymbol: string) => {
    try {
      const response = await tickerApi.getTickerLatestPrice({
        ticker_symbols: [tickerSymbol],
      });

      if (response.success && response.data && response.data.tickers) {
        const tickerPriceData = response.data.tickers.find((p: any) => p.ticker_symbol === tickerSymbol);
        if (tickerPriceData && tickerPriceData.prices && tickerPriceData.prices.length > 0) {
          const latestPrice = tickerPriceData.prices[0];
          const previousPrice = tickerPriceData.prices.length > 1 ? tickerPriceData.prices[1] : null;
          
          setAnalystViewsTickerPriceData({
            ticker_symbol: tickerPriceData.ticker_symbol,
            ticker_basics_id: tickerPriceData.ticker_basics_id,
            close_price_adjusted: latestPrice.close_price_adjusted,
            date: new Date(latestPrice.date),
            previous_close_price_adjusted: previousPrice ? previousPrice.close_price_adjusted : null,
            previous_date: previousPrice ? new Date(previousPrice.date) : null,
            currentPrice: latestPrice.close_price_adjusted,
            previousPrice: previousPrice ? previousPrice.close_price_adjusted : null,
            change: latestPrice.change,
            changePercent: latestPrice.changePercent,
            volume: latestPrice.volume,
            high: latestPrice.high,
            low: latestPrice.low,
            open: latestPrice.open,
            prevClose: latestPrice.previousClose,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching analyst views price data:', error);
    }
  };

  // Check sync status and fetch analyst views data
  const checkAnalystViewsSyncAndFetchData = async (tickerSymbol: string, attemptNumber: number = 1) => {
    if (!tickerSymbol) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`Landing: Analyst views attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopAnalystViewsPolling();
      setAnalystViewsPollingLimitReached(true);
      analystViewsIsCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped
    if (analystViewsPollingStoppedRef.current) {
      console.log(`Landing: checkAnalystViewsSyncAndFetchData called but polling is stopped, ignoring`);
      analystViewsIsCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution
    if (analystViewsIsCheckingSyncRef.current) {
      console.log(`Landing: checkAnalystViewsSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    analystViewsIsCheckingSyncRef.current = true;

    console.log(`Landing: Checking analyst views sync status for ${tickerSymbol} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setAnalystViewsSyncPollingAttempts(attemptNumber);

    try {
      // Check sync status first
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [tickerSymbol],
      });

      if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
        const tickerData = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
        
        if (!tickerData) {
          throw new Error(`Ticker ${tickerSymbol} not found`);
        }
        
        // Store ticker name and symbol
        if (tickerData.ticker_name) {
          setAnalystViewsTickerName(tickerData.ticker_name);
          // Update shared cache for persistent display across tab switches
          setCurrentEmbeddedTickerName(tickerData.ticker_name);
          sessionStorage.setItem(`ticker-name-${tickerSymbol}`, tickerData.ticker_name);
        }
        if (tickerData.ticker_symbol) {
          setAnalystViewsTickerSymbol(tickerData.ticker_symbol);
          // Update shared cache for persistent display across tab switches
          setCurrentEmbeddedTickerSymbol(tickerData.ticker_symbol);
          sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, tickerData.ticker_symbol);
        }
        
        // Set lastUpdated from last_sync_ended_at
        if (tickerData.last_sync_ended_at) {
          const date = typeof tickerData.last_sync_ended_at === 'string' 
            ? new Date(tickerData.last_sync_ended_at) 
            : tickerData.last_sync_ended_at;
          if (date instanceof Date && !isNaN(date.getTime())) {
            setAnalystViewsTickerLastUpdated(date);
          }
        }
        
        if (tickerData.is_synced) {
          console.log(`Landing: ${tickerSymbol} is synced, fetching analyst views data`);
          
          // Fetch past 4 quarters analyst views data
          const analystViewsResponse = await tickerApi.getPast4QuartersAnalystViews({
            ticker_basics_id: tickerData.ticker_basics_id,
            category: 'all',
            key: 'all'
          });

          if (analystViewsResponse.success && analystViewsResponse.data) {
            setAnalystViewsTickerData(analystViewsResponse.data);
            setAnalystViewsTickerBasicsId(tickerData.ticker_basics_id);
            
            // Fetch market_type and nextEarningsDate from ticker basics
            try {
              const tickerBasicsResponse = await tickerApi.getTickerBasics([tickerSymbol.toUpperCase()]);
              if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
                const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === tickerSymbol.toUpperCase());
                if (tickerBasic) {
                  if (tickerBasic.marketType) {
                    setAnalystViewsMarketType(tickerBasic.marketType);
                    sessionStorage.setItem(`ticker-market-type-${tickerSymbol}`, tickerBasic.marketType);
                  }
                  if (tickerBasic.nextEarningsDate) {
                    const date = typeof tickerBasic.nextEarningsDate === 'string' 
                      ? new Date(tickerBasic.nextEarningsDate) 
                      : tickerBasic.nextEarningsDate;
                    if (date instanceof Date && !isNaN(date.getTime())) {
                      setAnalystViewsNextEarningsDate(date);
                    }
                  }
                }
              }
            } catch (err) {
              console.error('Error fetching ticker basics for market_type:', err);
            }
            
            setAnalystViewsTickerError(null);
            setAnalystViewsTickerLoading(false);
            console.log(`Landing: Successfully fetched analyst views data for ${tickerSymbol}`);
            
            // Extract the date from the latest quarter's analyst views
            if (analystViewsResponse.data.quarters && analystViewsResponse.data.quarters.length > 0) {
              const latestQuarter = analystViewsResponse.data.quarters[0];
              const latestQuarterViews = analystViewsResponse.data.analystViews.filter((view: TickerAnalystView) => 
                view.year === latestQuarter.year && view.quarter === latestQuarter.quarter
              );
              
              if (latestQuarterViews.length > 0) {
                setAnalystViewsLatestDate(latestQuarterViews[0].date);
              }
            }
            
            // Fetch price data for Last Closing Price and Potential Upside calculations
            await fetchAnalystViewsTickerPriceData(tickerSymbol);
          } else {
            setAnalystViewsTickerError(analystViewsResponse.message || 'Failed to fetch analyst views data');
            setAnalystViewsTickerLoading(false);
          }
          
          setAnalystViewsIsSyncing(false);
          stopAnalystViewsPolling();
          analystViewsIsCheckingSyncRef.current = false;
        } else {
          // If we've reached the polling limit, try to fetch data anyway
          if (attemptNumber >= 5) {
            console.warn(`Landing: Analyst views sync polling limit reached (5 attempts) for ticker: ${tickerSymbol}. Attempting to fetch data anyway.`);
            setAnalystViewsPollingLimitReached(true);
            stopAnalystViewsPolling();
            
            // Still try to fetch analyst views data even though sync status is false
            try {
              const analystViewsResponse = await tickerApi.getPast4QuartersAnalystViews({
                ticker_basics_id: tickerData.ticker_basics_id,
                category: 'all',
                key: 'all'
              });

              if (analystViewsResponse.success && analystViewsResponse.data) {
                setAnalystViewsTickerData(analystViewsResponse.data);
                setAnalystViewsTickerBasicsId(tickerData.ticker_basics_id);
                
                // Fetch market_type and nextEarningsDate from ticker basics
                try {
                  const tickerBasicsResponse = await tickerApi.getTickerBasics([tickerSymbol.toUpperCase()]);
                  if (tickerBasicsResponse.success && tickerBasicsResponse.data && tickerBasicsResponse.data.length > 0) {
                    const tickerBasic = tickerBasicsResponse.data.find((tb: any) => tb.tickerSymbol === tickerSymbol.toUpperCase());
                    if (tickerBasic) {
                      if (tickerBasic.marketType) {
                        setAnalystViewsMarketType(tickerBasic.marketType);
                        sessionStorage.setItem(`ticker-market-type-${tickerSymbol}`, tickerBasic.marketType);
                      }
                      if (tickerBasic.nextEarningsDate) {
                        const date = typeof tickerBasic.nextEarningsDate === 'string' 
                          ? new Date(tickerBasic.nextEarningsDate) 
                          : tickerBasic.nextEarningsDate;
                        if (date instanceof Date && !isNaN(date.getTime())) {
                          setAnalystViewsNextEarningsDate(date);
                        }
                      }
                    }
                  }
                } catch (err) {
                  console.error('Error fetching ticker basics for market_type:', err);
                }
                
                setAnalystViewsTickerError(null);
                setAnalystViewsTickerLoading(false);
                
                // Extract the date from the latest quarter's analyst views
                if (analystViewsResponse.data.quarters && analystViewsResponse.data.quarters.length > 0) {
                  const latestQuarter = analystViewsResponse.data.quarters[0];
                  const latestQuarterViews = analystViewsResponse.data.analystViews.filter((view: TickerAnalystView) => 
                    view.year === latestQuarter.year && view.quarter === latestQuarter.quarter
                  );
                  
                  if (latestQuarterViews.length > 0) {
                    setAnalystViewsLatestDate(latestQuarterViews[0].date);
                  }
                }
                
                // Fetch price data
                await fetchAnalystViewsTickerPriceData(tickerSymbol);
                console.log(`Landing: Successfully fetched analyst views data for ${tickerSymbol} despite sync status being false`);
              } else {
                throw new Error(analystViewsResponse.message || 'Failed to fetch analyst views data');
              }
            } catch (fetchErr) {
              console.error(`Landing: Failed to fetch analyst views data for ${tickerSymbol} after polling limit:`, fetchErr);
              setAnalystViewsTickerError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
              setAnalystViewsTickerLoading(false);
            }
            analystViewsIsCheckingSyncRef.current = false;
          } else {
            // Double-check we haven't stopped polling before setting up interval
            if (analystViewsPollingStoppedRef.current) {
              console.log(`Landing: Analyst views polling stopped, not setting up interval`);
              analystViewsIsCheckingSyncRef.current = false;
              return;
            }
            
            console.log(`Landing: ${tickerSymbol} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
            setAnalystViewsIsSyncing(true);
            
            // Update the current attempt ref so the interval callback knows the next attempt number
            analystViewsCurrentAttemptRef.current = attemptNumber;
            
            // Continue polling with 2 second delay
            const pollingDelay = 2000; // 2 seconds
            
            // Clear any existing interval before creating a new one
            clearAnalystViewsPollingInterval();
            
            console.log(`Landing: Starting analyst views sync polling with ${pollingDelay}ms delay`);
            const interval = setInterval(() => {
              // Check if polling was stopped or we've reached the limit
              if (analystViewsPollingStoppedRef.current) {
                clearInterval(interval);
                analystViewsSyncPollingIntervalRef.current = null;
                return;
              }
              
              // Get the next attempt number from the ref
              const nextAttempt = analystViewsCurrentAttemptRef.current + 1;
              
              // Check if we've exceeded the limit
              if (nextAttempt > 5) {
                console.log(`Landing: Reached analyst views attempt limit (5), stopping interval`);
                clearInterval(interval);
                analystViewsSyncPollingIntervalRef.current = null;
                stopAnalystViewsPolling();
                setAnalystViewsPollingLimitReached(true);
                analystViewsIsCheckingSyncRef.current = false;
                return;
              }
              
              // Update the ref for the next iteration
              analystViewsCurrentAttemptRef.current = nextAttempt;
              
              // Call the fetch function with the next attempt number
              checkAnalystViewsSyncAndFetchData(tickerSymbol, nextAttempt);
            }, pollingDelay);
            
            analystViewsSyncPollingIntervalRef.current = interval;
          }
        }
      } else {
        throw new Error('Failed to check sync status');
      }
    } catch (error) {
      console.error(`Landing: Error checking analyst views sync status for ${tickerSymbol}:`, error);
      setAnalystViewsTickerError(error instanceof Error ? error.message : 'Failed to fetch ticker data');
      setAnalystViewsTickerLoading(false);
      setAnalystViewsIsSyncing(false);
      stopAnalystViewsPolling();
      analystViewsIsCheckingSyncRef.current = false;
    }
  };

  // Cleanup analyst views polling on unmount
  useEffect(() => {
    return () => {
      stopAnalystViewsPolling();
    };
  }, []);

  // Close embedded analyst views view
  const handleCloseAnalystViewsView = () => {
    // Check if other embedded views are active before clearing
    const hasOtherViews = !!(selectedTickerForDailyView || selectedTickerForTechnicalsView || selectedTickerForFundamentalsView || selectedTickerForInsidersView || selectedTickerForProsConsView || selectedTickerForPerformanceView);
    
    setSelectedTickerForAnalystViewsView(null);
    setAnalystViewsTickerData(null);
    setAnalystViewsTickerPriceData(null);
    setAnalystViewsTickerError(null);
    setAnalystViewsTickerBasicsId(null);
    setAnalystViewsTickerName('');
    setAnalystViewsTickerSymbol('');
    setAnalystViewsLatestDate(null);
    setAnalystViewsMarketType(null);
    setIsAnalystViewsFullScreen(false);
    stopAnalystViewsPolling();
    
    // Clear cache only if no other embedded view is active
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };

  // Use full screen hook for analyst views keyboard support
  useFullScreen(isAnalystViewsFullScreen, () => setIsAnalystViewsFullScreen(false));
  
  // Function to clear insiders polling interval
  const clearInsidersPollingInterval = () => {
    if (insidersSyncPollingIntervalRef.current) {
      clearInterval(insidersSyncPollingIntervalRef.current);
      insidersSyncPollingIntervalRef.current = null;
    }
  };

  // Stop insiders polling
  const stopInsidersPolling = () => {
    insidersPollingStoppedRef.current = true;
    clearInsidersPollingInterval();
    setInsidersIsSyncing(false);
  };

  // Check insider sync status and fetch data
  const checkInsidersSyncAndFetchData = async (tickerSymbol: string, attemptNumber: number = 1) => {
    if (!tickerSymbol) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`Landing: Insiders attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopInsidersPolling();
      setInsidersPollingLimitReached(true);
      insidersIsCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped
    if (insidersPollingStoppedRef.current) {
      console.log(`Landing: checkInsidersSyncAndFetchData called but polling is stopped, ignoring`);
      insidersIsCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution
    if (insidersIsCheckingSyncRef.current) {
      console.log(`Landing: checkInsidersSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    insidersIsCheckingSyncRef.current = true;

    console.log(`Landing: Checking insiders sync status for ${tickerSymbol} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setInsidersSyncPollingAttempts(attemptNumber);

    try {
      // First, get ticker_basics_id from ticker symbol
      let currentTickerBasicsId = insidersTickerBasicsId;
      if (!currentTickerBasicsId) {
        const cachedId = sessionStorage.getItem(`ticker-basics-id-${tickerSymbol}`);
        if (cachedId) {
          currentTickerBasicsId = parseInt(cachedId, 10);
        }
      }
      
      // If we still don't have it, get it from checkTickerSyncStatus
      if (!currentTickerBasicsId) {
        const basicsResponse = await tickerApi.checkTickerSyncStatus({
          ticker_symbols: [tickerSymbol],
        });
        
        const basicsInfo = basicsResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
        if (!basicsInfo || !basicsInfo.ticker_basics_id) {
          throw new Error(`Ticker ${tickerSymbol} not found`);
        }
        
        currentTickerBasicsId = basicsInfo.ticker_basics_id;
        if (currentTickerBasicsId) {
          sessionStorage.setItem(`ticker-basics-id-${tickerSymbol}`, currentTickerBasicsId.toString());
        }
        
        // Also cache name and symbol
        const name = basicsInfo.ticker_name || tickerSymbol;
        const symbol = basicsInfo.ticker_symbol || tickerSymbol;
        setInsidersTickerName(name);
        setInsidersTickerSymbol(symbol);
        setCurrentEmbeddedTickerName(name);
        setCurrentEmbeddedTickerSymbol(symbol);
        sessionStorage.setItem(`ticker-name-${tickerSymbol}`, name);
        sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, symbol);
      }
      
      setInsidersTickerBasicsId(currentTickerBasicsId);
      
      // Ensure we have a valid ticker_basics_id
      if (!currentTickerBasicsId) {
        throw new Error(`Unable to get ticker_basics_id for ${tickerSymbol}`);
      }
      
      // Now check insider sync status
      const syncResponse = await tickerApi.checkInsiderSyncStatus({
        ticker_basics_ids: [currentTickerBasicsId],
      });
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_basics_id === currentTickerBasicsId);
      if (!tickerInfo) {
        throw new Error(`Ticker ${tickerSymbol} not found in insider sync status`);
      }
      
      // Update name and symbol from insider sync response if not already set
      if (!insidersTickerName && tickerInfo.ticker_name) {
        setInsidersTickerName(tickerInfo.ticker_name);
        setCurrentEmbeddedTickerName(tickerInfo.ticker_name);
        sessionStorage.setItem(`ticker-name-${tickerSymbol}`, tickerInfo.ticker_name);
      }
      if (!insidersTickerSymbol && tickerInfo.ticker_symbol) {
        setInsidersTickerSymbol(tickerInfo.ticker_symbol);
        setCurrentEmbeddedTickerSymbol(tickerInfo.ticker_symbol);
        sessionStorage.setItem(`ticker-symbol-${tickerSymbol}`, tickerInfo.ticker_symbol);
      }
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setInsidersTickerLastUpdated(date);
        }
      }
      
      // Check if ticker is synced
      if (tickerInfo.is_synced) {
        console.log(`Landing: ${tickerSymbol} is synced, fetching insider transactions data`);
        
        // Fetch insider transactions data
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 90);
        const endDateStr = today.toISOString().split('T')[0];
        const startDateStr = startDate.toISOString().split('T')[0];
        
        try {
          const transactionsResponse = await tickerApi.getInsiderTransactionsByDateRange({
            tickerBasicsIds: [currentTickerBasicsId],
            startDate: startDateStr,
            endDate: endDateStr,
            ownerHasProSubscription: true, // Landing page should show all indicators
          });
          
          if (transactionsResponse.success && transactionsResponse.data && transactionsResponse.data.length > 0) {
            // Find the data for this ticker (should be the first/only one)
            const tickerData = transactionsResponse.data.find((item: any) => 
              item.tickerSymbol === tickerSymbol || item.tickerSymbol === tickerSymbol.toUpperCase()
            ) || transactionsResponse.data[0];
            setInsidersTickerData(tickerData);
          } else {
            setInsidersTickerData(null);
          }
        
          // Stop polling since we got the data
          stopInsidersPolling();
          setInsidersTickerLoading(false);
          setInsidersTickerError(null);
          insidersIsCheckingSyncRef.current = false;
        } catch (err) {
          console.error('Error fetching insider transactions:', err);
          setInsidersTickerError('Failed to fetch insider transaction data');
          stopInsidersPolling();
          setInsidersTickerLoading(false);
          insidersIsCheckingSyncRef.current = false;
        }
      } else {
        // If we've reached the polling limit, try to fetch data anyway
        if (attemptNumber >= 5) {
          console.log(`Landing: Reached polling limit, attempting to fetch data anyway for ${tickerSymbol}`);
          
          const today = new Date();
          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 90);
          const endDateStr = today.toISOString().split('T')[0];
          const startDateStr = startDate.toISOString().split('T')[0];
          
          try {
            const transactionsResponse = await tickerApi.getInsiderTransactionsByDateRange({
              tickerBasicsIds: [currentTickerBasicsId],
              startDate: startDateStr,
              endDate: endDateStr,
              ownerHasProSubscription: true, // Landing page should show all indicators
            });
            
            if (transactionsResponse.success && transactionsResponse.data && transactionsResponse.data.length > 0) {
              // Find the data for this ticker (should be the first/only one)
              const tickerData = transactionsResponse.data.find((item: any) => 
                item.tickerSymbol === tickerSymbol || item.tickerSymbol === tickerSymbol.toUpperCase()
              ) || transactionsResponse.data[0];
              setInsidersTickerData(tickerData);
            } else {
              setInsidersTickerData(null);
            }
            
            stopInsidersPolling();
            setInsidersTickerLoading(false);
            setInsidersTickerError(null);
            insidersIsCheckingSyncRef.current = false;
          } catch (err) {
            console.error('Error fetching insider transactions after polling limit:', err);
            setInsidersTickerError('Failed to fetch insider transaction data');
            stopInsidersPolling();
            setInsidersTickerLoading(false);
            insidersIsCheckingSyncRef.current = false;
          }
        } else {
          // Continue polling
          console.log(`Landing: ${tickerSymbol} is not synced yet, will retry (attempt ${attemptNumber})`);
          setInsidersIsSyncing(true);
          insidersIsCheckingSyncRef.current = false;
          
          // Set up interval for next attempt
          const delay = Math.min(1000 * Math.pow(2, attemptNumber - 1), 10000); // Exponential backoff, max 10s
          insidersSyncPollingIntervalRef.current = setTimeout(() => {
            checkInsidersSyncAndFetchData(tickerSymbol, attemptNumber + 1);
          }, delay);
        }
      }
    } catch (error) {
      console.error(`Landing: Error checking insiders sync status for ${tickerSymbol}:`, error);
      setInsidersTickerError(error instanceof Error ? error.message : 'Failed to fetch ticker data');
      setInsidersTickerLoading(false);
      setInsidersIsSyncing(false);
      stopInsidersPolling();
      insidersIsCheckingSyncRef.current = false;
    }
  };

  // Cleanup insiders polling on unmount
  useEffect(() => {
    return () => {
      stopInsidersPolling();
    };
  }, []);

  // Close embedded insiders view
  const handleCloseInsidersView = () => {
    // Check if other embedded views are active before clearing
    const hasOtherViews = !!(selectedTickerForDailyView || selectedTickerForTechnicalsView || selectedTickerForFundamentalsView || selectedTickerForAnalystViewsView || selectedTickerForProsConsView || selectedTickerForPerformanceView);
    
    setSelectedTickerForInsidersView(null);
    setInsidersTickerData(null);
    setInsidersTickerError(null);
    setInsidersTickerBasicsId(null);
    setInsidersTickerName('');
    setInsidersTickerSymbol('');
    setIsInsidersFullScreen(false);
    stopInsidersPolling();
    
    // Clear cache only if no other embedded view is active
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };

  const handleCloseProsConsView = () => {
    const hasOtherViews = !!(
      selectedTickerForDailyView ||
      selectedTickerForTechnicalsView ||
      selectedTickerForFundamentalsView ||
      selectedTickerForAnalystViewsView ||
      selectedTickerForInsidersView ||
      selectedTickerForPerformanceView
    );
    prosConsPollingStoppedRef.current = true;
    clearProsConsPollingInterval();
    setSelectedTickerForProsConsView(null);
    setProsConsHistory([]);
    setProsConsTickerError(null);
    setProsConsTickerBasicsId(null);
    prosConsResolvedIdRef.current = null;
    setProsConsTickerName('');
    setProsConsTickerSymbol('');
    setProsConsIsSyncing(false);
    setProsConsTickerLastUpdated(null);
    setIsProsConsFullScreen(false);
    if (!hasOtherViews) {
      setCurrentEmbeddedTickerName('');
      setCurrentEmbeddedTickerSymbol('');
    }
  };

  useEffect(() => {
    if (activeTab !== 'proscons' && selectedTickerForProsConsView) {
      handleCloseProsConsView();
    }
  }, [activeTab]);

  useEffect(() => {
    return () => {
      prosConsPollingStoppedRef.current = true;
      if (prosConsSyncPollingIntervalRef.current) {
        clearInterval(prosConsSyncPollingIntervalRef.current);
        prosConsSyncPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Use full screen hook for insiders keyboard support
  useFullScreen(isInsidersFullScreen, () => setIsInsidersFullScreen(false));
  useFullScreen(isProsConsFullScreen, () => setIsProsConsFullScreen(false));

  // Embedded fullscreen overlays (Daily uses body lock inside DailyPriceTableFullScreen)
  useBodyScrollLock(
    isTechnicalsFullScreen ||
      isFundamentalsFullScreen ||
      isAnalystViewsFullScreen ||
      isInsidersFullScreen ||
      isProsConsFullScreen ||
      isPerformanceFullScreen,
  );

  // Helper functions for fundamentals data using shared utilities
  const getFundamentalsValue = (statisticKey: string, column: { year: number; quarter?: number }) => {
    const financialData = fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyFinancials : fundamentalsAnnualFinancials;
    const ratioData = fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyRatios : fundamentalsAnnualRatios;
    return getFundamentalsValueUtil(statisticKey, column, financialData, ratioData, fundamentalsTimeframe);
  };

  const formatFundamentalsValue = (value: number | null, type: string, statisticKey?: string) => {
    return formatFundamentalsValueUtil(value, type, statisticKey, fundamentalsTickerLoading || fundamentalsIsSyncing);
  };

  const getFundamentalsArrowInfo = (statisticKey: string, currentValue: number | null, previousValue: number | null): { direction: 'up' | 'down' | null, isGood: boolean } => {
    return getFundamentalsArrowInfoUtil(statisticKey, currentValue, previousValue, fundamentalsTimeframe);
  };

  const getFundamentalsArrowTooltip = (statisticKey: string, currentValue: number | null, previousValue: number | null): string => {
    return getFundamentalsArrowTooltipUtil(statisticKey, currentValue, previousValue, fundamentalsTimeframe);
  };

  // Screen size detection for responsive date column width
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate responsive width for date columns - smaller on small screens, larger on big screens
  // This ensures all date columns have equal width while adapting to screen size
  const calculateFundamentalsDateColumnWidth = () => {
    if (isSmallScreen) {
      // Smaller width on small screens to allow more columns to fit
      return 120; // Reduced from 150px
    }
    // Standard width on larger screens
    return 150;
  };
  
  const fundamentalsDateColumnWidth = calculateFundamentalsDateColumnWidth();

  /** First-column width for embedded Featured Stocks ticker tables (matches Fundamentals tab). */
  const EMBEDDED_FEATURED_STOCKS_INDICATORS_COLUMN_WIDTH = 200;
  const embeddedIndicatorsColStyle = {
    width: `${EMBEDDED_FEATURED_STOCKS_INDICATORS_COLUMN_WIDTH}px`,
    maxWidth: `${EMBEDDED_FEATURED_STOCKS_INDICATORS_COLUMN_WIDTH}px`,
  };
  const embeddedIndicatorsColgroupStyle = {
    width: `${EMBEDDED_FEATURED_STOCKS_INDICATORS_COLUMN_WIDTH}px`,
    minWidth: '150px',
    maxWidth: '500px',
  };

  // Helper function to get quarter data for analyst views (wrapper for utility)
  const getAnalystViewsQuarterDataForView = (quarter: QuarterData, quarterIndex: number): Record<string, any> => {
    const quarterData = getAnalystViewsQuarterData(quarter, quarterIndex, analystViewsTickerData, analystViewsTickerPriceData, analystViewsNextEarningsDate);
    return quarterData;
  };
  
  // Helper function to transform analyst views data (wrapper for utility)
  const transformAnalystViewsDataForView = transformAnalystViewsDataForTicker;

  // Handle category info click for analyst views
  const handleAnalystViewsCategoryInfoClick = (categoryName: string) => {
    setSelectedAnalystViewsCategory(categoryName);
    setIsAnalystViewsModalOpen(true);
  };
  
  // Category info modal handlers for insiders view
  const handleInsidersCategoryInfoClick = (categoryName: string) => {
    setSelectedInsidersCategory(categoryName);
    setIsInsidersCategoryModalOpen(true);
  };
  
  // Helper functions for insiders ticker view
  const getInsidersTickerValue = (statisticKey: string): any => {
    if (!insidersTickerData) return null;
    
    // Check if it's an aggregated data field
    if (insidersTickerData.aggregatedData && statisticKey in insidersTickerData.aggregatedData) {
      return insidersTickerData.aggregatedData[statisticKey as keyof typeof insidersTickerData.aggregatedData];
    }
    
    // Check if it's a recent transaction field
    if (statisticKey.startsWith('recentTransaction')) {
      const index = parseInt(statisticKey.replace('recentTransaction', ''), 10) - 1;
      if (insidersTickerData.detailedData?.recentTransactions && insidersTickerData.detailedData.recentTransactions[index]) {
        return insidersTickerData.detailedData.recentTransactions[index];
      }
      return null;
    }
    
    return null;
  };
  
  const formatInsidersTickerStatisticValue = (statisticKey: string): React.ReactNode => {
    if (insidersTickerLoading || insidersIsSyncing) return '-';
    const value = getInsidersTickerValue(statisticKey);
    
    // Landing page should always show all indicators (ownerHasProSubscription: true is passed)
    // So we don't need to check for locked status here
    
    if (value === null || value === undefined) return '-';
    
    // Check if this is a transaction detail statistic
    const isTransactionDetail = statisticKey.startsWith('recentTransaction');
    
    if (isTransactionDetail && typeof value === 'object' && value !== null) {
      // Format transaction details as multi-line text with reduced font size
      const transaction = value as any;
      const dateStr = transaction.transactionDate 
        ? new Date(transaction.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '-';
      const nameStr = transaction.name || '-';
      const positionStr = transaction.position || '-';
      
      // For recentTransaction, use Bought/Sold format
      const isPurchase = transaction.transactionCode && transaction.transactionCode.toUpperCase().startsWith('P');
      const sharesStr = transaction.totalShares 
        ? transaction.totalShares.toLocaleString('en-US', { maximumFractionDigits: 0 })
        : '-';
      const priceStr = transaction.weightedPrice !== null && transaction.weightedPrice !== undefined
        ? `$${transaction.weightedPrice.toFixed(2)}`
        : '';
      
      // Format transacted shares percentage
      let percentageStr = '';
      if (transaction.transactedSharesPercentage !== null && transaction.transactedSharesPercentage !== undefined) {
        const percentage = Math.round(transaction.transactedSharesPercentage * 100) / 100;
        percentageStr = ` (${percentage.toFixed(2)}% of holdings)`;
      }
      
      const actionStr = isPurchase 
        ? (priceStr ? `Bought ${sharesStr} at ${priceStr}${percentageStr}` : `Bought ${sharesStr}${percentageStr}`)
        : (priceStr ? `Sold ${sharesStr} at ${priceStr}${percentageStr}` : `Sold ${sharesStr}${percentageStr}`);
      
      // Color purchase transactions green, sales remain default (black)
      const textColorClass = isPurchase ? 'text-green-light dark:text-green-500' : '';
      
      return (
        <div className={`text-xs leading-tight ${textColorClass}`}>
          <div>{dateStr}</div>
          <div>{nameStr}</div>
          <div>{positionStr}</div>
          <div>{actionStr}</div>
        </div>
      );
    }
    
    if (typeof value === 'number') {
      // Format all numeric values with locale string (no decimals for counts/shares)
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return String(value);
  };
  
  const getInsidersTickerStatisticColor = (statisticKey: string): string => {
    if (insidersTickerLoading || insidersIsSyncing) return 'text-text-light-neutral dark:text-text-secondary';
    
    const value = getInsidersTickerValue(statisticKey);
    if (value === null || value === undefined) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // For difference statistics (buyers - sellers, purchases - sales, etc.)
    if (statisticKey === 'buyersMinusSellers' || 
        statisticKey === 'purchasesMinusSales' || 
        statisticKey === 'sharesPurchasedMinusSharesSold') {
      if (typeof value === 'number') {
        if (value > 0) {
          return 'text-green-light dark:text-green-500'; // Positive (more buyers/purchases)
        } else if (value < 0) {
          return 'text-red-light dark:text-red-500'; // Negative (more sellers/sales)
        }
      }
      return 'text-text-light-neutral dark:text-text-secondary'; // Zero or neutral
    }
    
    // Default neutral color for all other statistics
    return 'text-text-light-neutral dark:text-text-secondary';
  };
  
  // Calculate statistics column width for insiders (similar to TickerInsiders)
  const insidersStatisticsColumnWidth = useMemo(() => {
    const isSmallScreen = window.innerWidth < 640;
    return isSmallScreen ? 120 : 140;
  }, []);

  const prosConsEmbeddedDisplayCols = useMemo(() => {
    const s = prosConsHistory.slice(0, 6);
    return s.length > 0
      ? s
      : [{ year: null, month: null, prosCons: null as LandingProsConsHistoryItem['prosCons'] }];
  }, [prosConsHistory]);

  const renderProsConsEmbeddedFooter = () => (
    <div
      className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}
    >
      <div className="flex items-center gap-4">
        {prosConsTickerLoading && (
          <div className="flex items-center gap-2">
            <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full" />
            <span>Loading...</span>
          </div>
        )}
        {prosConsTickerLastUpdated && !prosConsTickerLoading && (
          <span>
            Last updated: {prosConsTickerLastUpdated.toLocaleDateString()}{' '}
            {prosConsTickerLastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );

  const renderProsConsEmbeddedTable = (fullScreenMode: boolean) => {
    const indicatorColWidth = getProsConsIndicatorsColumnWidthPx({ isSmallScreen, isEditingColumns: false });
    const monthColWidth = getProsConsHistoryMonthColumnWidthPx({
      isSmallScreen,
      columnCount: prosConsEmbeddedDisplayCols.length,
      fullScreen: fullScreenMode,
    });
    const actionColWidth = 60;

    const firstColumnCellStyle: React.CSSProperties = {
      width: indicatorColWidth,
      minWidth: indicatorColWidth,
      maxWidth: indicatorColWidth,
      boxSizing: 'border-box',
    };

    return (
    <table className="w-full font-mono" style={{ tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: `${indicatorColWidth}px` }} />
        {prosConsEmbeddedDisplayCols.map((_, idx) => (
          <col key={idx} style={{ width: `${monthColWidth}px`, minWidth: `${monthColWidth}px` }} />
        ))}
        <col style={{ width: '100%', minWidth: 0 }} />
        <col style={{ width: `${actionColWidth}px`, minWidth: `${actionColWidth}px` }} />
      </colgroup>
      <thead
        className="bg-gray-50 dark:bg-background-card sticky-header-border"
        style={{ position: 'sticky', top: 0, zIndex: 50 }}
      >
        <tr className="h-12 sm:h-16">
          <th
            className={`px-4 text-left align-middle text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card`}
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 40,
              ...firstColumnCellStyle,
            }}
          >
            INDICATORS
          </th>
          {prosConsEmbeddedDisplayCols.map((c, idx) => (
            <th
              key={idx}
              className={`px-4 text-left align-middle text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card`}
            >
              {formatLandingProsConsMonthYear(c.year, c.month)}
            </th>
          ))}
          <th
            className="p-0 border-0 align-middle bg-gray-50 dark:bg-background-card"
            aria-hidden
          />
          <th
            className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
            style={{
              position: 'sticky',
              right: 0,
              zIndex: 40,
              width: actionColWidth,
              minWidth: actionColWidth,
              maxWidth: actionColWidth,
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={() => setIsProsConsFullScreen(!fullScreenMode)}
              className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
              title={fullScreenMode ? 'Close full screen' : 'Open in full screen'}
            >
              {fullScreenMode ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
          </th>
        </tr>
      </thead>
      <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
        <tr>
          <td
            className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 30,
              ...firstColumnCellStyle,
            }}
          >
            Pros
          </td>
          {prosConsEmbeddedDisplayCols.map((col, idx) => (
            <td key={idx} className="px-4 py-3 align-top bg-surface-light dark:bg-background-card">
              {prosConsTickerLoading || prosConsIsSyncing ? (
                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" />
              ) : !col.prosCons ? (
                <span className="text-gray-400 dark:text-text-secondary">-</span>
              ) : (() => {
                const prosLines = prosConsLineArrays(col.prosCons).pros;
                return prosLines?.length ? (
                  <div className={`font-semibold text-text-light-neutral dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                    <ProsConsNumberedList lines={prosLines} />
                  </div>
                ) : (
                  <span className="text-gray-400 dark:text-text-secondary">-</span>
                );
              })()}
            </td>
          ))}
          <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
          <td
            className="py-3 px-2 sm:px-4 text-right align-top bg-surface-light dark:bg-background-card"
            style={{
              position: 'sticky',
              right: 0,
              zIndex: 30,
              width: actionColWidth,
              minWidth: actionColWidth,
              maxWidth: actionColWidth,
              boxSizing: 'border-box',
            }}
          />
        </tr>
        <tr>
          <td
            className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 30,
              ...firstColumnCellStyle,
            }}
          >
            Cons
          </td>
          {prosConsEmbeddedDisplayCols.map((col, idx) => (
            <td key={idx} className="px-4 py-3 align-top bg-surface-light dark:bg-background-card">
              {prosConsTickerLoading || prosConsIsSyncing ? (
                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" />
              ) : !col.prosCons ? (
                <span className="text-gray-400 dark:text-text-secondary">-</span>
              ) : (() => {
                const consLines = prosConsLineArrays(col.prosCons).cons;
                return consLines?.length ? (
                  <div className={`font-semibold text-text-light-neutral dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                    <ProsConsNumberedList lines={consLines} />
                  </div>
                ) : (
                  <span className="text-gray-400 dark:text-text-secondary">-</span>
                );
              })()}
            </td>
          ))}
          <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
          <td
            className="py-3 px-2 sm:px-4 text-right align-top bg-surface-light dark:bg-background-card"
            style={{
              position: 'sticky',
              right: 0,
              zIndex: 30,
              width: actionColWidth,
              minWidth: actionColWidth,
              maxWidth: actionColWidth,
              boxSizing: 'border-box',
            }}
          />
        </tr>
      </tbody>
    </table>
    );
  };

  const handleCloseAnalystViewsModal = () => {
    setIsAnalystViewsModalOpen(false);
    setSelectedAnalystViewsCategory('');
  };
  
  const handleCloseInsidersModal = () => {
    setIsInsidersCategoryModalOpen(false);
    setSelectedInsidersCategory('');
  };

  const handleAnalystViewsIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedAnalystViewsIndicatorKey(indicatorKey);
    setIsAnalystViewsIndicatorModalOpen(true);
  };

  const handleCloseAnalystViewsIndicatorModal = () => {
    setIsAnalystViewsIndicatorModalOpen(false);
    setSelectedAnalystViewsIndicatorKey('');
  };

  // Check if ticker is ETF or ETV for analyst views
  const isAnalystViewsEtfOrEtv = analystViewsMarketType && (analystViewsMarketType.toUpperCase() === 'ETF' || analystViewsMarketType.toUpperCase() === 'ETV');
  
  // Timeframe options moved to dailyPriceUtils
  
  // Set up scroll listener for daily table
  useEffect(() => {
    if (!selectedTickerForDailyView || !dailyTableRef.current) {
      return;
    }
    
    const handleScroll = () => {
      if (!dailyTableRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = dailyTableRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100 && hasMoreDailyData && !loadingMoreDailyData) {
        loadMoreDailyData();
      }
    };
    
    dailyTableRef.current.addEventListener('scroll', handleScroll);
    return () => {
      if (dailyTableRef.current) {
        dailyTableRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [selectedTickerForDailyView, hasMoreDailyData, loadingMoreDailyData, loadMoreDailyData]);

  const handleCompareStocksClick = () => {
    navigate('/get-started');
  };

  // Handle tab click without navigation
  const handleTabClick = (e: React.MouseEvent<HTMLAnchorElement>, tabType: TabType) => {
    e.preventDefault();
    
    // Capture the currently selected ticker before any state changes
    const currentTicker = selectedTickerForDailyView || 
                          selectedTickerForTechnicalsView || 
                          selectedTickerForFundamentalsView || 
                          selectedTickerForAnalystViewsView || 
                          selectedTickerForInsidersView ||
                          selectedTickerForProsConsView ||
                          selectedTickerForPerformanceView ||
                          null;
    
    // If clicking the already active tab, scroll the Featured Stocks table container to top (not embedded ticker)
    if (activeTab === tabType) {
      if (!currentTicker) {
        const tableSection = tableSectionRef.current;
        if (tableSection) {
          const scrollableContainer = tableSection.querySelector<HTMLElement>('.overflow-x-auto.overflow-y-auto');
          if (scrollableContainer) {
            scrollableContainer.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }
        }
      }
      return;
    }
    
    // If we're in an embedded view, don't close views - just switch tabs and open the new view
    // This prevents the container from unmounting/remounting which causes flashing
    const isInEmbeddedView = !!currentTicker;
    
    if (isInEmbeddedView) {
      // Change to the new tab first
      setActiveTab(tabType);
      setLoading(true);
      
      // Immediately set the new view's ticker (before closing old views) to prevent unmount
      // Then close old views and fetch data
      if (tabType === 'daily') {
        setSelectedTickerForDailyView(currentTicker);
        // Close other views after setting the new one
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
          // Note: Don't reset technicalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
          // Note: Don't reset fundamentalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
          // Note: Don't reset analystViewsTickerLastUpdated - preserve it
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
          // Note: Don't reset insidersTickerLastUpdated - preserve it
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        // Fetch data (this will set dailyTickerLastUpdated)
        setDailyTickerLoading(true);
        setDailyTickerError(null);
        setAllDailyPrices([]);
        setHasMoreDailyData(true);
        dailyCurrentPageRef.current = 0;
        fetchDailyTickerData(currentTicker, 0, 50, dailyTimeframe, dailySortColumn, dailySortDirection);
      } else if (tabType === 'technicals') {
        setSelectedTickerForTechnicalsView(currentTicker);
        // Close other views after setting the new one
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
          // Note: Don't reset dailyTickerLastUpdated - preserve it
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
          // Note: Don't reset fundamentalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
          // Note: Don't reset analystViewsTickerLastUpdated - preserve it
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
          // Note: Don't reset insidersTickerLastUpdated - preserve it
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        // Fetch data (this will set technicalsTickerLastUpdated)
        setTechnicalsTickerLoading(true);
        setTechnicalsTickerError(null);
        fetchTechnicalsTickerData(currentTicker);
      } else if (tabType === 'fundamentals') {
        setSelectedTickerForFundamentalsView(currentTicker);
        // Close other views after setting the new one
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
          // Note: Don't reset dailyTickerLastUpdated - preserve it
        }
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
          // Note: Don't reset technicalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
          // Note: Don't reset analystViewsTickerLastUpdated - preserve it
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
          // Note: Don't reset insidersTickerLastUpdated - preserve it
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        // Fetch data (this will set fundamentalsTickerLastUpdated)
        setFundamentalsTimeframe(timeframe);
        setFundamentalsTickerLoading(true);
        setFundamentalsTickerError(null);
        fundamentalsPollingStoppedRef.current = false;
        setFundamentalsPollingLimitReached(false);
        setFundamentalsSyncPollingAttempts(0);
        fundamentalsCurrentAttemptRef.current = 1;
        fundamentalsIsCheckingSyncRef.current = false;
        clearFundamentalsPollingInterval();
        checkFundamentalsSyncAndFetchData(currentTicker, 1);
      } else if (tabType === 'analysts') {
        setSelectedTickerForAnalystViewsView(currentTicker);
        // Close other views after setting the new one
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
          // Note: Don't reset dailyTickerLastUpdated - preserve it
        }
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
          // Note: Don't reset technicalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
          // Note: Don't reset fundamentalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
          // Note: Don't reset insidersTickerLastUpdated - preserve it
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        // Fetch data (this will set analystViewsTickerLastUpdated)
        setAnalystViewsTickerLoading(true);
        setAnalystViewsTickerError(null);
        analystViewsPollingStoppedRef.current = false;
        setAnalystViewsPollingLimitReached(false);
        setAnalystViewsSyncPollingAttempts(0);
        analystViewsCurrentAttemptRef.current = 1;
        analystViewsIsCheckingSyncRef.current = false;
        clearAnalystViewsPollingInterval();
        checkAnalystViewsSyncAndFetchData(currentTicker, 1);
      } else if (tabType === 'insiders') {
        setSelectedTickerForInsidersView(currentTicker);
        // Close other views after setting the new one
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
          // Note: Don't reset dailyTickerLastUpdated - preserve it
        }
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
          // Note: Don't reset technicalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
          // Note: Don't reset fundamentalsTickerLastUpdated - preserve it
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
          // Note: Don't reset analystViewsTickerLastUpdated - preserve it
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        // Fetch data (this will set insidersTickerLastUpdated)
        setInsidersTickerLoading(true);
        setInsidersTickerError(null);
        insidersPollingStoppedRef.current = false;
        setInsidersPollingLimitReached(false);
        setInsidersSyncPollingAttempts(0);
        insidersCurrentAttemptRef.current = 1;
        insidersIsCheckingSyncRef.current = false;
        clearInsidersPollingInterval();
        checkInsidersSyncAndFetchData(currentTicker, 1);
      } else if (tabType === 'proscons') {
        setSelectedTickerForProsConsView(currentTicker);
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
        }
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
        }
        if (selectedTickerForPerformanceView) {
          performanceEmbeddedStoppedRef.current = true;
          clearPerformanceEmbeddedPolling();
          setSelectedTickerForPerformanceView(null);
          setPerformanceEmbeddedHistory([]);
          setPerformanceEmbeddedError(null);
          setIsPerformanceFullScreen(false);
          setPerformanceEmbeddedIsSyncing(false);
          setPerformanceEmbeddedPollingLimit(false);
        }
        setProsConsTickerLoading(true);
        setProsConsTickerError(null);
        prosConsPollingStoppedRef.current = false;
        prosConsResolvedIdRef.current = null;
        prosConsCurrentAttemptRef.current = 1;
        prosConsIsCheckingSyncRef.current = false;
        clearProsConsPollingInterval();
        void runProsConsEmbeddedOpen(currentTicker);
      } else if (tabType === 'performance') {
        if (selectedTickerForDailyView) {
          setSelectedTickerForDailyView(null);
          setDailyTickerData(null);
          setAllDailyPrices([]);
        }
        if (selectedTickerForTechnicalsView) {
          setSelectedTickerForTechnicalsView(null);
          setTechnicalsTickerData(null);
          setTechnicalsDates({});
          setTechnicalsDailyPrices({});
        }
        if (selectedTickerForFundamentalsView) {
          setSelectedTickerForFundamentalsView(null);
          setFundamentalsQuarterlyFinancials([]);
          setFundamentalsQuarterlyRatios([]);
          setFundamentalsAnnualFinancials([]);
          setFundamentalsAnnualRatios([]);
          setFundamentalsQuarterlyColumns([]);
          setFundamentalsAnnualColumns([]);
          stopFundamentalsPolling();
        }
        if (selectedTickerForAnalystViewsView) {
          setSelectedTickerForAnalystViewsView(null);
          setAnalystViewsTickerData(null);
          setAnalystViewsTickerPriceData(null);
          stopAnalystViewsPolling();
        }
        if (selectedTickerForInsidersView) {
          setSelectedTickerForInsidersView(null);
          setInsidersTickerData(null);
          stopInsidersPolling();
        }
        if (selectedTickerForProsConsView) {
          setSelectedTickerForProsConsView(null);
          setProsConsHistory([]);
          setProsConsTickerError(null);
          prosConsPollingStoppedRef.current = true;
          clearProsConsPollingInterval();
          setProsConsTickerBasicsId(null);
          prosConsResolvedIdRef.current = null;
          setProsConsIsSyncing(false);
          setIsProsConsFullScreen(false);
        }
        openPerformanceEmbeddedViewRef.current?.(currentTicker);
      }
    } else {
      // Not in embedded view - normal tab switching behavior
      setActiveTab(tabType);
      setLoading(true);
      // Data will be fetched in useEffect based on activeTab
    }
  };
  
  // Helper functions to get current embedded view info
  const getCurrentEmbeddedTicker = () => {
    return selectedTickerForDailyView || 
           selectedTickerForTechnicalsView || 
           selectedTickerForFundamentalsView || 
           selectedTickerForAnalystViewsView ||
           selectedTickerForInsidersView ||
           selectedTickerForProsConsView ||
           selectedTickerForPerformanceView ||
           null;
  };
  
  const getCurrentEmbeddedTickerName = () => {
    // Use cached name if available, otherwise fall back to current view's data
    if (currentEmbeddedTickerName) {
      return currentEmbeddedTickerName;
    }
    // Fallback to current view's data
    if (selectedTickerForDailyView) {
      return dailyTickerData?.ticker_name || selectedTickerForDailyView;
    } else if (selectedTickerForTechnicalsView) {
      return technicalsTickerName || selectedTickerForTechnicalsView;
    } else if (selectedTickerForFundamentalsView) {
      return fundamentalsTickerName || selectedTickerForFundamentalsView;
    } else if (selectedTickerForAnalystViewsView) {
      return analystViewsTickerName || selectedTickerForAnalystViewsView;
    } else if (selectedTickerForProsConsView) {
      return prosConsTickerName || selectedTickerForProsConsView;
    } else if (selectedTickerForInsidersView) {
      return insidersTickerName || selectedTickerForInsidersView;
    } else if (selectedTickerForPerformanceView) {
      return currentEmbeddedTickerName || selectedTickerForPerformanceView;
    }
    return null;
  };
  
  const getCurrentEmbeddedTickerSymbol = () => {
    // Use cached symbol if available, otherwise fall back to current view's data
    if (currentEmbeddedTickerSymbol) {
      return currentEmbeddedTickerSymbol;
    }
    // Fallback to current view's data
    if (selectedTickerForDailyView) {
      return selectedTickerForDailyView;
    } else if (selectedTickerForTechnicalsView) {
      return technicalsTickerSymbol || selectedTickerForTechnicalsView;
    } else if (selectedTickerForFundamentalsView) {
      return fundamentalsTickerSymbol || selectedTickerForFundamentalsView;
    } else if (selectedTickerForAnalystViewsView) {
      return analystViewsTickerSymbol || selectedTickerForAnalystViewsView;
    } else if (selectedTickerForProsConsView) {
      return prosConsTickerSymbol || selectedTickerForProsConsView;
    } else if (selectedTickerForInsidersView) {
      return insidersTickerSymbol || selectedTickerForInsidersView;
    } else if (selectedTickerForPerformanceView) {
      return currentEmbeddedTickerSymbol || selectedTickerForPerformanceView;
    }
    return null;
  };
  
  const getCurrentEmbeddedCloseHandler = () => {
    if (selectedTickerForDailyView) {
      return handleCloseDailyView;
    } else if (selectedTickerForTechnicalsView) {
      return handleCloseTechnicalsView;
    } else if (selectedTickerForFundamentalsView) {
      return handleCloseFundamentalsView;
    } else if (selectedTickerForAnalystViewsView) {
      return handleCloseAnalystViewsView;
    } else if (selectedTickerForProsConsView) {
      return handleCloseProsConsView;
    } else if (selectedTickerForInsidersView) {
      return handleCloseInsidersView;
    } else if (selectedTickerForPerformanceView) {
      return handleClosePerformanceView;
    }
    return null;
  };
  
  const isAnyEmbeddedViewActive = () => {
    return !!(selectedTickerForDailyView || 
              selectedTickerForTechnicalsView || 
              selectedTickerForFundamentalsView || 
              selectedTickerForAnalystViewsView ||
              selectedTickerForProsConsView ||
              selectedTickerForInsidersView ||
              selectedTickerForPerformanceView);
  };
  
  // Category info modal state for technicals view
  const [isTechnicalsModalOpen, setIsTechnicalsModalOpen] = useState<boolean>(false);
  const [selectedTechnicalsCategory, setSelectedTechnicalsCategory] = useState<string>('');
  
  // Category info modal handlers for technicals view
  const handleTechnicalsCategoryInfoClick = (categoryName: string) => {
    setSelectedTechnicalsCategory(categoryName);
    setIsTechnicalsModalOpen(true);
  };

  const handleCloseTechnicalsModal = () => {
    setIsTechnicalsModalOpen(false);
    setSelectedTechnicalsCategory('');
  };
  
  // Category info modal state for fundamentals view
  const [isFundamentalsModalOpen, setIsFundamentalsModalOpen] = useState<boolean>(false);
  const [selectedFundamentalsCategory, setSelectedFundamentalsCategory] = useState<string>('');
  
  // Category info modal handlers for fundamentals view
  const handleFundamentalsCategoryInfoClick = (categoryName: string) => {
    setSelectedFundamentalsCategory(categoryName);
    setIsFundamentalsModalOpen(true);
  };

  const handleCloseFundamentalsModal = () => {
    setIsFundamentalsModalOpen(false);
    setSelectedFundamentalsCategory('');
  };
  
  // Indicator info modal state for fundamentals view
  // Daily indicator info modal state
  const [isDailyIndicatorModalOpen, setIsDailyIndicatorModalOpen] = useState<boolean>(false);
  const [selectedDailyIndicatorKey, setSelectedDailyIndicatorKey] = useState<string>('');
  
  // Handle Daily indicator info click
  const handleDailyIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedDailyIndicatorKey(indicatorKey);
    setIsDailyIndicatorModalOpen(true);
  };
  
  // Handle Daily indicator modal close
  const handleCloseDailyIndicatorModal = () => {
    setIsDailyIndicatorModalOpen(false);
    setSelectedDailyIndicatorKey('');
  };
  
  const [isFundamentalsIndicatorModalOpen, setIsFundamentalsIndicatorModalOpen] = useState<boolean>(false);
  const [selectedFundamentalsIndicatorKey, setSelectedFundamentalsIndicatorKey] = useState<string>('');
  
  // Indicator info modal handlers for fundamentals view
  const handleFundamentalsIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedFundamentalsIndicatorKey(indicatorKey);
    setIsFundamentalsIndicatorModalOpen(true);
  };

  const handleCloseFundamentalsIndicatorModal = () => {
    setIsFundamentalsIndicatorModalOpen(false);
    setSelectedFundamentalsIndicatorKey('');
  };
  
  // Create indicator accessors from technicalsTickerData (using utilities)
  const technicalsIndicatorAccessors = React.useMemo(() => {
    if (!technicalsTickerData) return null;
    
    // Convert technicalsTickerData to TickerTechnicalDataFromAPI format
    const tickerDataFromAPI: TickerTechnicalDataFromAPI = {
      ticker_symbol: selectedTickerForTechnicalsView || '',
      ticker_basics_id: technicalsTickerData.ticker_basics_id,
      indicators: technicalsTickerData.indicators.map((ind: any) => ({
        indicatorKey: ind.indicatorKey,
        indicatorValue: ind.indicatorValue,
        indicatorNonNumericValue: ind.indicatorNonNumericValue || null,
        daysAgo: ind.daysAgo,
        date: ind.date,
        ticker_basics_id: ind.ticker_basics_id || technicalsTickerData.ticker_basics_id,
      })),
    };
    
    return createIndicatorAccessors(tickerDataFromAPI);
  }, [technicalsTickerData, selectedTickerForTechnicalsView]);
  
  // Convert technicalsDailyPrices to DailyPriceData format
  const technicalsDailyPricesFormatted: { [daysAgo: number]: DailyPriceData } = React.useMemo(() => {
    const formatted: { [daysAgo: number]: DailyPriceData } = {};
    Object.keys(technicalsDailyPrices).forEach((daysAgoStr) => {
      const daysAgo = parseInt(daysAgoStr);
      const priceData = technicalsDailyPrices[daysAgo];
      if (priceData) {
        formatted[daysAgo] = {
          date: priceData.date,
          close_price_adjusted: priceData.close_price_adjusted,
        };
      }
    });
    return formatted;
  }, [technicalsDailyPrices]);
  
  // Format statistic value for technicals view (using utilities)
  const formatTechnicalsStatisticValueForView = (statisticKey: string, daysAgo: number): React.ReactNode => {
    if (!technicalsIndicatorAccessors) {
      if (technicalsTickerLoading) {
        return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
      }
      return <span className="text-text-secondary">--</span>;
    }
    
    // Convert technicalsTickerData to TickerTechnicalDataFromAPI format
    const tickerDataFromAPI: TickerTechnicalDataFromAPI | null = technicalsTickerData ? {
      ticker_symbol: selectedTickerForTechnicalsView || '',
      ticker_basics_id: technicalsTickerData.ticker_basics_id,
      indicators: technicalsTickerData.indicators.map((ind: any) => ({
        indicatorKey: ind.indicatorKey,
        indicatorValue: ind.indicatorValue,
        indicatorNonNumericValue: ind.indicatorNonNumericValue || null,
        daysAgo: ind.daysAgo,
        date: ind.date,
        ticker_basics_id: ind.ticker_basics_id || technicalsTickerData.ticker_basics_id,
      })),
    } : null;
    
    return formatTechnicalsStatisticValueFromUtils(
      technicalsIndicatorAccessors,
      technicalsDailyPricesFormatted,
      tickerDataFromAPI,
      selectedTickerForTechnicalsView || '',
      statisticKey,
      daysAgo,
      technicalsTickerLoading
    );
  };
  
  // Get available columns based on data
  const getTechnicalsAvailableColumns = () => {
    if (!technicalsTickerData || technicalsTickerData.indicators.length === 0) {
      return technicalsTickerLoading ? TECHNICALS_DATE_COLUMNS : [];
    }
    return TECHNICALS_DATE_COLUMNS.filter(col => {
      return technicalsTickerData.indicators.some((indicator: any) => indicator.daysAgo === col.daysAgo);
    });
  };
  
  const technicalsAvailableColumns = getTechnicalsAvailableColumns();

  const performanceEmbeddedSortedColumns = useMemo(() => {
    const rows = [...performanceEmbeddedHistory];
    rows.sort((a, b) => {
      const ad = a.date ? new Date(a.date as any).getTime() : 0;
      const bd = b.date ? new Date(b.date as any).getTime() : 0;
      return bd - ad;
    });
    return rows;
  }, [performanceEmbeddedHistory]);

  const performanceEmbeddedHeaderDate = (d: string | Date | null) => {
    if (!d) return '-';
    const date = new Date(d as any);
    if (isNaN(date.getTime())) return String(d);
    return date
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      .toUpperCase();
  };

  const formatPerformanceEmbeddedCell = (statKey: string, col: PerformanceHistoryRow) => {
    const symbol =
      col.tickerSymbol || currentEmbeddedTickerSymbol || selectedTickerForPerformanceView || '';
    const tickerObj: any = { ticker: symbol };
    const performance: any = {
      ticker_basics_id: col.tickerBasicsId,
      date: col.date ? new Date(col.date as any).toISOString().slice(0, 10) : null,
      ytd_return: col.ytdReturn ?? null,
      one_m_return: col.oneMReturn ?? null,
      three_m_return: col.threeMReturn ?? null,
      six_m_return: col.sixMReturn ?? null,
      nine_m_return: col.nineMReturn ?? null,
      one_y_return: col.oneYReturn ?? null,
      two_y_return: col.twoYReturn ?? null,
      three_y_return: col.threeYReturn ?? null,
      four_y_return: col.fourYReturn ?? null,
      five_y_return: col.fiveYReturn ?? null,
    };
    const fake = { [symbol]: { performance } };
    return formatPerformanceStatisticValue(statKey, tickerObj, {
      tickerPerformanceData: fake,
      syncingTickers: performanceEmbeddedIsSyncing ? new Set([symbol]) : new Set(),
      unsyncedTickers: new Set(),
      pollingLimitReached: performanceEmbeddedPollingLimit ? new Set([symbol]) : new Set(),
    } as any);
  };

  const getPerformanceEmbeddedCellColor = (statKey: string, col: PerformanceHistoryRow) => {
    const symbol =
      col.tickerSymbol || currentEmbeddedTickerSymbol || selectedTickerForPerformanceView || '';
    const tickerObj: any = { ticker: symbol };
    const performance: any = {
      ticker_basics_id: col.tickerBasicsId,
      date: col.date ? new Date(col.date as any).toISOString().slice(0, 10) : null,
      ytd_return: col.ytdReturn ?? null,
      one_m_return: col.oneMReturn ?? null,
      three_m_return: col.threeMReturn ?? null,
      six_m_return: col.sixMReturn ?? null,
      nine_m_return: col.nineMReturn ?? null,
      one_y_return: col.oneYReturn ?? null,
      two_y_return: col.twoYReturn ?? null,
      three_y_return: col.threeYReturn ?? null,
      four_y_return: col.fourYReturn ?? null,
      five_y_return: col.fiveYReturn ?? null,
    };
    return getPerformanceStatisticColor(statKey, tickerObj, { tickerPerformanceData: { [symbol]: { performance } } });
  };

  // Custom Tabs component that handles clicks without navigation
  const tabs = (
    <div className="flex overflow-x-auto scrollbar-hide space-x-1 sm:space-x-2 md:space-x-4 lg:space-x-8">
      {[
        { type: 'daily' as TabType, label: 'Daily' },
        { type: 'performance' as TabType, label: 'Performance' },
        { type: 'technicals' as TabType, label: 'Technicals' },
        { type: 'fundamentals' as TabType, label: 'Fundamentals' },
        { type: 'proscons' as TabType, label: 'Pros & Cons' },
        { type: 'analysts' as TabType, label: 'Analysts' },
        { type: 'insiders' as TabType, label: 'Insiders' },
      ].map(({ type, label }) => {
        const isActive = activeTab === type;
        const baseClasses = `px-2 sm:px-4 py-3 font-semibold ${getTextSizeClass(fontSize)} transition-all duration-200 whitespace-nowrap flex-shrink-0`;
        
        return (
          <a
            key={type}
            href="#"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => handleTabClick(e, type)}
            className={
              isActive
                ? `${baseClasses} border-b-[3px] border-highlight-gold text-gray-900 dark:text-highlight-gold -mb-px`
                : `${baseClasses} border-b-[3px] border-transparent text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-highlight-gold hover:border-highlight-gold`
            }
          >
            {label}
          </a>
        );
      })}
    </div>
  );

  // Timeframe toggle component (for Fundamentals tab)
  const timeframeToggle = activeTab === 'fundamentals' ? (
    <div className="flex items-center">
      <div className="relative inline-flex rounded-md bg-gray-200 dark:bg-border-color p-1">
        <button
          onClick={() => setTimeframe('quarterly')}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
            timeframe === 'quarterly'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Quarterly
        </button>
        <button
          onClick={() => setTimeframe('annual')}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
            timeframe === 'annual'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Annual
        </button>
      </div>
    </div>
  ) : undefined;

  // Ref and state for detecting when "Last Closing Price" row is stuck (normal mode - embedded Technicals)
  const lastClosingPriceRowRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuck, setIsLastClosingPriceStuck] = useState(false);
  
  // Ref and state for detecting when "Last Closing Price" row is stuck (full screen mode - embedded Technicals)
  const lastClosingPriceRowFullScreenRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuckFullScreen, setIsLastClosingPriceStuckFullScreen] = useState(false);
  
  // Detect when the sticky row becomes stuck using scroll event (normal mode - embedded Technicals)
  useEffect(() => {
    const row = lastClosingPriceRowRef.current;
    if (!row || isTechnicalsFullScreen || activeTab !== 'technicals' || !selectedTickerForTechnicalsView) return;
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      const stickyTop = window.innerWidth >= 640 ? 64 : 48; // sm:top-[64px] or top-[48px]
      
      // Row is stuck if it's positioned right below the header
      let isStuck = false;
      if (headerRect) {
        const headerBottom = headerRect.bottom;
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - headerBottom) <= tolerance;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
      }
      
      setIsLastClosingPriceStuck(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: `-${window.innerWidth >= 640 ? 64 : 48}px 0px 0px 0px`,
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [isTechnicalsFullScreen, activeTab, selectedTickerForTechnicalsView]);
  
  // Detect when the sticky row becomes stuck using scroll event (full screen mode - embedded Technicals)
  useEffect(() => {
    const row = lastClosingPriceRowFullScreenRef.current;
    if (!row || !isTechnicalsFullScreen || activeTab !== 'technicals' || !selectedTickerForTechnicalsView) return;
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      // In full screen mode, header is h-10 (40px) and row sticks at top-[40px]
      const stickyTop = 40;
      
      // Row is stuck if:
      // 1. The row's top is at or very close to the sticky top position (40px)
      // 2. AND the row is actually visible (not scrolled above viewport)
      let isStuck = false;
      
      // First check if row is visible (top should be >= 0 or close to sticky position)
      const isRowVisible = rowRect.top >= -10; // Allow small negative for edge cases
      
      if (isRowVisible && headerRect) {
        // Check if row is positioned right below the header
        const headerBottom = headerRect.bottom;
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - headerBottom) <= tolerance;
        
        // Also check if row is at the sticky top position as a fallback
        if (!isStuck) {
          isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
        }
        
      } else if (!isRowVisible) {
        // Row is scrolled above viewport, definitely not stuck
        isStuck = false;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3 && rowRect.top >= 0;
      }
      
      setIsLastClosingPriceStuckFullScreen(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: '-40px 0px 0px 0px',
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.h-full.overflow-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [isTechnicalsFullScreen, activeTab, selectedTickerForTechnicalsView]);

  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Ensure sticky header has solid background to prevent content showing through */
        table thead.sticky-header-border {
          background-color: rgb(249, 250, 251) !important;
          position: relative;
        }
        
        .dark table thead.sticky-header-border {
          background-color: rgb(17, 24, 39) !important;
        }
        
        /* Sticky header top cover - extends upward to prevent see-through */
        table thead.sticky-header-border::before {
          content: '';
          position: absolute;
          top: -100px;
          left: 0;
          right: 0;
          height: 100px;
          background-color: rgb(249, 250, 251);
          z-index: 49;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::before {
          background-color: rgb(17, 24, 39);
        }
        
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Alternative: target thead with position sticky using attribute selector */
        table thead[style*="sticky"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead[style*="sticky"]::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Sticky "Last Closing Price" row bottom border - using box-shadow, only when stuck */
        table tbody tr.sticky-last-closing-price.is-stuck {
          box-shadow: 0 1px 0 0 rgb(229, 231, 235) !important;
        }
        
        .dark table tbody tr.sticky-last-closing-price.is-stuck {
          box-shadow: 0 1px 0 0 rgb(55, 65, 81) !important;
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!(isDailyFullScreen || isTechnicalsFullScreen || isFundamentalsFullScreen || isAnalystViewsFullScreen || isProsConsFullScreen || isPerformanceFullScreen) && <Header />}
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section
          className="pt-20 sm:pt-24 lg:pt-32 pb-8 sm:pb-10 lg:pb-12"
          aria-labelledby="landing-hero-heading"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              id="landing-hero-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white leading-tight mb-6"
            >
              Instant Side-by-Side Stock Evaluation
            </h1>
            <p className="max-w-3xl mx-auto text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-10">
              Stock Table presents clear, tabular stock data, making it easier than ever to compare stocks and make informed decisions.
            </p>
            <button
              onClick={handleCompareStocksClick}
              className={`bg-primary text-slate-900 font-bold ${getTextSizeClass(fontSize)} py-3 px-8 rounded-lg hover:opacity-90 transition-opacity inline-block`}
            >
              Get Started
            </button>
          </div>
        </section>

        {/* Table Section */}
        <section ref={tableSectionRef} className="pb-20 sm:pb-24 lg:pb-32">
          <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Show embedded view if any ticker is selected */}
            {isAnyEmbeddedViewActive() ? (
              <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-lg shadow-lg">
                {/* Back button */}
                <div className="p-4 border-b border-gray-200 dark:border-border-color">
                  <button
                    onClick={getCurrentEmbeddedCloseHandler() || (() => {})}
                    className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Table
                  </button>
                </div>
                
                {/* Title */}
                <div className="px-4 pb-4 pt-4">
                  <h2 className={`${getTextSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-dark-primary`}>
                    {getCurrentEmbeddedTickerName()} ({getCurrentEmbeddedTickerSymbol()})
                  </h2>
                </div>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-border-color">
                  <nav className="px-4">
                    {tabs}
                  </nav>
                </div>
                
                {/* Content area - conditionally rendered based on activeTab */}
                {activeTab === 'technicals' && selectedTickerForTechnicalsView ? (
                  <>
                    {/* Technical Data Table */}
                {technicalsTickerError ? (
                  <div className="p-8 text-center">
                    <p className="text-red-600 dark:text-red-400">{technicalsTickerError}</p>
                  </div>
                ) : (
                  <>
                    <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
                      <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={embeddedIndicatorsColgroupStyle} />
                        {technicalsAvailableColumns.map((_, index) => (
                          <col key={index} style={{ width: '150px' }} />
                        ))}
                      </colgroup>
                      
                      <thead className="bg-gray-50 dark:bg-background-card border-t border-gray-200 dark:border-border-color sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                        <tr>
                          <th 
                            className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 40,
                              ...embeddedIndicatorsColStyle,
                            }}
                          >
                            <div className="flex items-center">
                              <span>INDICATORS</span>
                            </div>
                          </th>
                          {technicalsAvailableColumns.map((column) => (
                            <th
                              key={column.daysAgo}
                              className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                            >
                              <div className="flex flex-col items-start">
                                <span>{column.label}</span>
                                <span className={`${getSmallTextSizeClass(fontSize)} text-gray-500 dark:text-text-secondary font-normal`}>
                                  {technicalsDates[column.daysAgo] || '--'}
                                </span>
                              </div>
                            </th>
                          ))}
                          
                          {/* Full screen button column */}
                          <th 
                            className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                            style={{ position: 'sticky', right: 0, zIndex: 40 }}
                          >
                            <button
                              onClick={() => setIsTechnicalsFullScreen(true)}
                              className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                              title="Open in full screen"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-border-color">
                        {(() => {
                          // Track which section headers have already been shown to avoid duplicates
                          const shownHeaders = new Set<string>();
                          
                          return TECHNICAL_STATISTICS.map((statistic) => {
                            // Find the original index of this statistic in the full statistics array
                            const originalIndex = TECHNICAL_STATISTICS.findIndex(s => s.key === statistic.key);
                            
                            // Check if we need to render a category header before this statistic
                            const categoryHeader = CATEGORY_HEADERS.find(header => {
                              // Skip if this header has already been shown
                              if (shownHeaders.has(header.label)) {
                                return false;
                              }
                              
                              // Check if this statistic belongs to this section (is at or after startIndex)
                              const belongsToSection = originalIndex >= header.startIndex;
                              
                              // Check if this is the first visible statistic in this section
                              const isFirstVisibleInSection = !TECHNICAL_STATISTICS
                                .slice(header.startIndex, originalIndex)
                                .some(s => false); // No hidden statistics in embedded view
                              
                              // If this is the first visible statistic in the section, mark the header as shown
                              if (belongsToSection && isFirstVisibleInSection) {
                                shownHeaders.add(header.label);
                              }
                              
                              return belongsToSection && isFirstVisibleInSection;
                            });
                          
                            return (
                              <React.Fragment key={statistic.key}>
                                {/* Category header row */}
                                {categoryHeader && (
                                  <tr className="bg-gray-100 dark:bg-border-color">
                                    <td 
                                      className={`py-1 sm:py-2 px-2 sm:px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}
                                      style={{ 
                                        position: 'sticky', 
                                        left: 0, 
                                        zIndex: 30,
                                        ...embeddedIndicatorsColStyle,
                                      }}
                                      title={categoryHeader.label}
                                    >
                                      <div 
                                        className="whitespace-nowrap"
                                        style={{ overflow: 'visible', position: 'relative' }}
                                      >
                                        <span className="inline">{categoryHeader.label}</span>
                                        <CategoryInfoButton
                                          categoryName={categoryHeader.label}
                                          onCategoryInfoClick={handleTechnicalsCategoryInfoClick}
                                        />
                                      </div>
                                    </td>
                                    <td 
                                      colSpan={technicalsAvailableColumns.length}
                                      className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color`}
                                    >
                                      {/* Empty cell for the rest of the columns */}
                                    </td>
                                    
                                    {/* Full screen button column for category header */}
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                                      {/* Empty cell for full screen button column */}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Statistic row */}
                                <tr 
                                  ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowRef : null}
                                  className={`h-12 sm:h-16 ${statistic.key === 'last_closing_price' ? `sticky top-[47px] sm:top-[63px] z-40 sticky-last-closing-price ${isLastClosingPriceStuck ? 'is-stuck' : ''}` : ''}`}
                                >
                                  <td 
                                    className={`px-2 sm:px-4 text-gray-900 dark:text-text-primary font-medium ${getTextSizeClass(fontSize)} ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                                    style={{ 
                                      position: 'sticky', 
                                      left: 0, 
                                      zIndex: 30,
                                      ...embeddedIndicatorsColStyle,
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                      hyphens: 'auto'
                                    }}
                                  >
                                    <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                  </td>
                                  {technicalsAvailableColumns.map((column) => (
                                    <td key={column.daysAgo} className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}>
                                      {formatTechnicalsStatisticValueForView(statistic.key, column.daysAgo)}
                                    </td>
                                  ))}
                                  
                                  {/* Empty cell for full screen button column */}
                                  <td className={`py-1 sm:py-2 px-2 sm:px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}>
                                    {/* Empty cell for full screen button column */}
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                      </table>
                    </div>
                    
                    {/* Status Bar */}
                    <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                      <div className="flex items-center gap-4">
                        {technicalsTickerLoading && (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                            <span>Loading...</span>
                          </div>
                        )}
                        {technicalsTickerLastUpdated && !technicalsTickerLoading && (
                          <span>Last updated: {technicalsTickerLastUpdated.toLocaleDateString()} {technicalsTickerLastUpdated.toLocaleTimeString()}</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {/* Full Screen Overlay */}
                {isTechnicalsFullScreen && (
                  <div
                    className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div
                      ref={technicalsFullScreenTableRef}
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                    >
                        <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={embeddedIndicatorsColgroupStyle} />
                            {technicalsAvailableColumns.map((_, index) => (
                              <col key={index} style={{ width: '150px' }} />
                            ))}
                          </colgroup>
                          
                          <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                            <tr>
                              <th 
                                className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                style={{ 
                                  position: 'sticky', 
                                  left: 0, 
                                  zIndex: 40
                                }}
                              >
                                <div className="flex items-center">
                                  <span>Indicators</span>
                                </div>
                              </th>
                              
                              {technicalsAvailableColumns.map((column) => (
                                <th 
                                  key={column.daysAgo} 
                                  className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                >
                                  <div className="flex flex-col">
                                    <span>{column.label}</span>
                                    <span className={`${getSmallTextSizeClass(fontSize)} text-gray-500 dark:text-text-secondary font-normal`}>
                                      {technicalsDates[column.daysAgo] || '--'}
                                    </span>
                                  </div>
                                </th>
                              ))}
                              
                              {/* Close button - sticky right */}
                              <th 
                                className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                                style={{ position: 'sticky', right: 0, zIndex: 40 }}
                              >
                                <button
                                  onClick={() => setIsTechnicalsFullScreen(false)}
                                  className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                  title="Close full screen"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </th>
                            </tr>
                          </thead>
                          
                          <tbody className="divide-y divide-gray-200 dark:divide-border-color">
                            {(() => {
                              const shownHeaders = new Set<string>();
                              
                              return TECHNICAL_STATISTICS.map((statistic) => {
                                const originalIndex = TECHNICAL_STATISTICS.findIndex(s => s.key === statistic.key);
                                
                                const categoryHeader = CATEGORY_HEADERS.find(header => {
                                  if (shownHeaders.has(header.label)) {
                                    return false;
                                  }
                                  
                                  const belongsToSection = originalIndex >= header.startIndex;
                                  const isFirstVisibleInSection = !TECHNICAL_STATISTICS
                                    .slice(header.startIndex, originalIndex)
                                    .some(s => false);
                                  
                                  if (belongsToSection && isFirstVisibleInSection) {
                                    shownHeaders.add(header.label);
                                  }
                                  
                                  return belongsToSection && isFirstVisibleInSection;
                                });

                                return (
                                  <React.Fragment key={statistic.key}>
                                    {categoryHeader && (
                                      <tr className="bg-gray-100 dark:bg-border-color">
                                        <td 
                                          className={`px-2 sm:px-4 py-2 text-left font-bold text-gray-800 dark:text-text-primary ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}
                                          style={{ 
                                            position: 'sticky', 
                                            left: 0, 
                                            zIndex: 20,
                                            ...embeddedIndicatorsColStyle,
                                          }}
                                        >
                                          <div 
                                            className="whitespace-nowrap"
                                            style={{ overflow: 'visible', position: 'relative' }}
                                          >
                                            <span className="inline">{categoryHeader.label}</span>
                                            <CategoryInfoButton
                                              categoryName={categoryHeader.label}
                                              onCategoryInfoClick={handleTechnicalsCategoryInfoClick}
                                            />
                                          </div>
                                        </td>
                                        {technicalsAvailableColumns.map((column) => (
                                          <td key={column.daysAgo} className={`px-2 sm:px-4 lg:px-6 py-2 text-left font-bold text-gray-800 dark:text-text-primary ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}>
                                            {/* Empty cell - category name only shown in first column */}
                                          </td>
                                        ))}
                                        
                                        {/* Empty cell for close button column */}
                                        <td className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-bold text-gray-800 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                      </tr>
                                    )}
                                    
                                    <tr 
                                      ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowFullScreenRef : null}
                                      className={`hover:bg-gray-50 dark:hover:bg-background-card transition-colors duration-200 ${statistic.key === 'last_closing_price' ? `sticky top-[39px] z-40 sticky-last-closing-price ${isLastClosingPriceStuckFullScreen ? 'is-stuck' : ''}` : ''}`}
                                    >
                                      <td 
                                        className={`px-2 sm:px-4 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                                        style={{ 
                                          position: 'sticky', 
                                          left: 0, 
                                          zIndex: statistic.key === 'last_closing_price' ? 50 : 30,
                                          top: statistic.key === 'last_closing_price' ? '39px' : undefined,
                                          ...embeddedIndicatorsColStyle,
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          hyphens: 'auto'
                                        }}
                                      >
                                        <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                      </td>
                                      {technicalsAvailableColumns.map((column) => (
                                        <td 
                                          key={column.daysAgo} 
                                          className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                                          style={statistic.key === 'last_closing_price' ? { position: 'sticky', top: '39px', zIndex: 40 } : undefined}
                                        >
                                          {formatTechnicalsStatisticValueForView(statistic.key, column.daysAgo)}
                                        </td>
                                      ))}
                                      
                                      {/* Empty cell for close button column */}
                                      <td 
                                        className={`px-4 py-1 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'}`}
                                        style={statistic.key === 'last_closing_price' ? { position: 'sticky', top: '39px', zIndex: 40 } : undefined}
                                      ></td>
                                    </tr>
                                  </React.Fragment>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                    </div>
                      
                      {/* Status Bar */}
                      <div className={`flex-shrink-0 px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                        <div className="flex items-center gap-4">
                          {technicalsTickerLoading && (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                              <span>Loading...</span>
                            </div>
                          )}
                          {technicalsTickerLastUpdated && !technicalsTickerLoading && (
                            <span>Last updated: {technicalsTickerLastUpdated.toLocaleDateString()} {technicalsTickerLastUpdated.toLocaleTimeString()}</span>
                          )}
                        </div>
                      </div>
                  </div>
                )}
                  </>
                ) : activeTab === 'performance' && selectedTickerForPerformanceView ? (
                  <>
                    {performanceEmbeddedError ? (
                      <div className="p-8 text-center">
                        <p className="text-red-600 dark:text-red-400">{performanceEmbeddedError}</p>
                      </div>
                    ) : (
                      <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
                        <table className="min-w-full font-mono" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                          <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                            <tr>
                              <th
                                className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                                style={{ position: 'sticky', left: 0, zIndex: 40 }}
                              >
                                <div className="flex items-center">
                                  <span>INDICATORS</span>
                                </div>
                              </th>
                              {performanceEmbeddedSortedColumns.map((c) => (
                                <th
                                  key={String(c.date ?? '') + String(c.tickerBasicsId ?? '')}
                                  className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{performanceEmbeddedHeaderDate(c.date)}</span>
                                  </div>
                                </th>
                              ))}
                              <th
                                className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                                style={{ position: 'sticky', right: 0, zIndex: 40 }}
                              >
                                <button
                                  onClick={() => setIsPerformanceFullScreen(true)}
                                  className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                  title="Open in full screen"
                                  type="button"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                  </svg>
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                            {PERFORMANCE_CATEGORY_HEADERS.map((cat) => (
                              <tr key={cat.label} className="bg-gray-100 dark:bg-border-color">
                                <td
                                  className="py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold text-sm bg-gray-100 dark:bg-border-color"
                                  style={{ position: 'sticky', left: 0, zIndex: 30 }}
                                >
                                  {cat.label}
                                </td>
                                {performanceEmbeddedSortedColumns.map((c) => (
                                  <td
                                    key={`${cat.label}-h-${String(c.date)}`}
                                    className="px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color"
                                  />
                                ))}
                                <td className="px-2 sm:px-4 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color" />
                              </tr>
                            ))}
                            {PERFORMANCE_STATISTICS.map((stat) => (
                              <tr key={stat.key} className="h-12 sm:h-16">
                                <td
                                  className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                  style={{ position: 'sticky', left: 0, zIndex: 30 }}
                                >
                                  <span className={getStatisticLabelSizeClass(fontSize)}>{stat.label}</span>
                                </td>
                                {performanceEmbeddedSortedColumns.map((c) => (
                                  <td
                                    key={`${stat.key}-${String(c.date)}`}
                                    className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 bg-surface-light dark:bg-background-card ${getPerformanceEmbeddedCellColor(stat.key, c)}`}
                                  >
                                    {formatPerformanceEmbeddedCell(stat.key, c)}
                                  </td>
                                ))}
                                <td className="px-2 sm:px-4 h-12 sm:h-16 bg-surface-light dark:bg-background-card" />
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                      <div className="flex items-center gap-4">
                        {(performanceEmbeddedLoading || performanceEmbeddedIsSyncing) && (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full" />
                            <span>Loading...</span>
                          </div>
                        )}
                        {performanceEmbeddedLastUpdated && !performanceEmbeddedLoading && !performanceEmbeddedIsSyncing && (
                          <span>
                            Last updated: {performanceEmbeddedLastUpdated.toLocaleDateString()}{' '}
                            {performanceEmbeddedLastUpdated.toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {isPerformanceFullScreen && (
                      <div
                        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                        role="dialog"
                        aria-modal="true"
                      >
                        <div
                          ref={performanceFullScreenTableRef}
                          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                        >
                          <table className="min-w-full font-mono" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <thead className="bg-gray-50 dark:bg-background-card sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                              <tr>
                                <th
                                  className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                  style={{ position: 'sticky', left: 0, zIndex: 40 }}
                                >
                                  <div className="flex items-center">
                                    <span>INDICATORS</span>
                                  </div>
                                </th>
                                {performanceEmbeddedSortedColumns.map((c) => (
                                  <th
                                    key={`fs-h-${String(c.date)}`}
                                    className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                  >
                                    <div className="flex items-center">
                                      <span>{performanceEmbeddedHeaderDate(c.date)}</span>
                                    </div>
                                  </th>
                                ))}
                                <th
                                  className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                                  style={{ position: 'sticky', right: 0, zIndex: 40 }}
                                >
                                  <button
                                    onClick={() => setIsPerformanceFullScreen(false)}
                                    className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                    title="Close full screen"
                                    type="button"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                              {PERFORMANCE_CATEGORY_HEADERS.map((cat) => (
                                <tr key={`fs-${cat.label}`} className="bg-gray-100 dark:bg-border-color">
                                  <td
                                    className="py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold text-sm bg-gray-100 dark:bg-border-color"
                                    style={{ position: 'sticky', left: 0, zIndex: 30 }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{cat.label}</span>
                                    </div>
                                  </td>
                                  {performanceEmbeddedSortedColumns.map((c) => (
                                    <td
                                      key={`fs-ch-${String(c.date)}`}
                                      className="px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color"
                                    />
                                  ))}
                                  <td className="px-4 py-1 sm:py-2 text-sm font-semibold text-gray-700 dark:text-text-primary border-b border-gray-200 dark:border-border-color bg-gray-100 dark:bg-border-color" />
                                </tr>
                              ))}
                              {PERFORMANCE_STATISTICS.map((stat) => (
                                <tr key={`fs-${stat.key}`} className="h-12 sm:h-16">
                                  <td
                                    className="px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                    style={{ position: 'sticky', left: 0, zIndex: 30 }}
                                  >
                                    <span className={getStatisticLabelSizeClass(fontSize)}>{stat.label}</span>
                                  </td>
                                  {performanceEmbeddedSortedColumns.map((c) => (
                                    <td
                                      key={`fs-${stat.key}-${String(c.date)}`}
                                      className={`px-1 sm:px-2 md:px-4 lg:px-6 font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 bg-surface-light dark:bg-background-card ${getPerformanceEmbeddedCellColor(stat.key, c)}`}
                                      style={{ paddingTop: 0, paddingBottom: 0 }}
                                    >
                                      {formatPerformanceEmbeddedCell(stat.key, c)}
                                    </td>
                                  ))}
                                  <td className="px-4 h-12 sm:h-16 bg-surface-light dark:bg-background-card" />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : activeTab === 'daily' && selectedTickerForDailyView ? (
                  <>
                    {/* Timeframe Dropdown */}
                <div className="px-4 h-16 flex items-center border-b border-gray-200 dark:border-border-color">
                  <TimeframeDropdown
                    timeframe={dailyTimeframe}
                    onTimeframeChange={handleDailyTimeframeChange}
                    isOpen={isDailyDropdownOpen}
                    onToggle={() => setIsDailyDropdownOpen(!isDailyDropdownOpen)}
                    onClose={() => setIsDailyDropdownOpen(false)}
                  />
                </div>
                
                {/* Daily Price Table - using shared component */}
                {dailyTickerError ? (
                  <div className="p-8 text-center">
                    <p className="text-red-600 dark:text-red-400">{dailyTickerError}</p>
                  </div>
                ) : (
                  <DailyPriceTable
                    data={allDailyPrices}
                    loading={dailyTickerLoading}
                    loadingMore={loadingMoreDailyData}
                    sortColumn={dailySortColumn}
                    sortDirection={dailySortDirection}
                    onSort={handleDailySort}
                    onFullScreenClick={() => setIsDailyFullScreen(true)}
                    tableRef={dailyTableRef}
                    isSyncing={false}
                  />
                )}
                
                {/* Status Bar */}
                <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                  <div className="flex items-center gap-4">
                    {dailyTickerLoading && (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                        <span>Loading...</span>
                      </div>
                    )}
                    {dailyTickerLastUpdated && !dailyTickerLoading && (
                      <span>Last updated: {dailyTickerLastUpdated.toLocaleDateString()} {dailyTickerLastUpdated.toLocaleTimeString()}</span>
                    )}
                  </div>
                </div>
                
                {/* Full Screen Overlay - using shared component */}
                {isDailyFullScreen && (
                  <DailyPriceTableFullScreen
                    data={allDailyPrices}
                    loading={dailyTickerLoading}
                    loadingMore={loadingMoreDailyData}
                    sortColumn={dailySortColumn}
                    sortDirection={dailySortDirection}
                    onSort={handleDailySort}
                    onClose={() => setIsDailyFullScreen(false)}
                    tableRef={dailyFullScreenTableRef}
                    isSyncing={false}
                  />
                )}
                  </>
                ) : activeTab === 'fundamentals' && selectedTickerForFundamentalsView ? (
                  <>
                    {/* Timeframe Toggle */}
                <div className="px-4 py-4 border-t border-b border-gray-200 dark:border-border-color">
                  <div className="flex items-center">
                    <div className="relative inline-flex rounded-md bg-gray-200 dark:bg-border-color p-1">
                      <button
                        onClick={() => {
                          setFundamentalsTimeframe('quarterly');
                        }}
                        className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
                          fundamentalsTimeframe === 'quarterly'
                            ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
                            : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
                        }`}
                      >
                        Quarterly
                      </button>
                      <button
                        onClick={() => {
                          setFundamentalsTimeframe('annual');
                        }}
                        className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
                          fundamentalsTimeframe === 'annual'
                            ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
                            : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
                        }`}
                      >
                        Annual
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Fundamentals Data Table */}
                {fundamentalsTickerError ? (
                  <div className="p-8 text-center">
                    <p className="text-red-600 dark:text-red-400">{fundamentalsTickerError}</p>
                  </div>
                ) : (
                  <>
                    <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
                      <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={embeddedIndicatorsColgroupStyle} />
                        {(() => {
                          const currentColumns = fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns;
                          if ((fundamentalsTickerLoading || fundamentalsIsSyncing) && currentColumns.length === 0) {
                            return Array.from({ length: 6 }).map((_, index) => (
                              <col key={index} style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }} />
                            ));
                          }
                          return currentColumns.map((_, index) => (
                            <col key={index} style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }} />
                          ));
                        })()}
                        <col style={{ width: '60px' }} />
                      </colgroup>
                      
                      <thead className="bg-gray-50 dark:bg-background-card border-t border-gray-200 dark:border-border-color sticky-header-border" style={{ position: 'sticky', top: -1, zIndex: 50 }}>
                        <tr>
                          <th 
                            className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 40
                            }}
                          >
                            INDICATORS
                          </th>
                          {(fundamentalsTickerLoading || fundamentalsIsSyncing) && (fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns).length === 0 ? (
                            Array.from({ length: 6 }).map((_, index) => (
                              <th
                                key={`loading-header-${index}`}
                                className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                              >
                              </th>
                            ))
                          ) : (
                            (fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns).map((column, index) => (
                              <th
                                key={index}
                                className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                                style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}
                              >
                                {column.label}
                              </th>
                            ))
                          )}
                          
                          {/* Full screen button column */}
                          <th 
                            className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                            style={{ position: 'sticky', right: 0, zIndex: 40 }}
                          >
                            <button
                              onClick={() => setIsFundamentalsFullScreen(true)}
                              className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                              title="Open in full screen"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      
                      <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                        {(() => {
                          const currentColumns = fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns;
                          const allStatistics = fundamentalsTimeframe === 'quarterly' ? FUNDAMENTAL_STATISTICS : ANNUAL_FUNDAMENTAL_STATISTICS;
                          const baseCategoryHeaders = fundamentalsTimeframe === 'quarterly' ? FUNDAMENTAL_CATEGORY_HEADERS : ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS;
                          const currentCategoryHeaders = baseCategoryHeaders;
                          
                          if ((fundamentalsTickerLoading || fundamentalsIsSyncing) && currentColumns.length === 0) {
                            return allStatistics.slice(0, 20).map((statistic) => (
                              <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                                <td 
                                  className="px-2 sm:px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                  style={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 30
                                  }}
                                >
                                  <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                </td>
                                {Array.from({ length: 6 }).map((_, colIndex) => (
                                  <td
                                    key={`loading-stat-col-${colIndex}`}
                                    className={`${colIndex === 0 ? 'pl-1 sm:pl-2 pr-1 sm:pr-2 md:pr-4 lg:pr-6' : 'px-1 sm:px-2 md:px-4 lg:px-6'} py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}
                                    style={colIndex === 0 ? {} : { width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}
                                  >
                                    <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                  </td>
                                ))}
                                <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                                </td>
                              </tr>
                            ));
                          }
                          
                          if (currentCategoryHeaders.length > 0) {
                            return currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                              <React.Fragment key={categoryHeader.label}>
                                {/* Category Header Row */}
                                <tr className="bg-gray-100 dark:bg-border-color">
                                  <td
                                    className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                    style={{
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 30,
                                      ...embeddedIndicatorsColStyle,
                                    }}
                                  >
                                    <div 
                                      className="whitespace-nowrap"
                                      style={{ overflow: 'visible', position: 'relative' }}
                                    >
                                      <span className="inline">{categoryHeader.label}</span>
                                      <CategoryInfoButton
                                        categoryName={categoryHeader.label}
                                        onCategoryInfoClick={handleFundamentalsCategoryInfoClick}
                                      />
                                    </div>
                                  </td>
                                  {currentColumns.map((_, colIndex) => (
                                    <td
                                      key={colIndex}
                                      className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                      style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}
                                    >
                                    </td>
                                  ))}
                                  <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                                  </td>
                                </tr>
                                
                                {/* Statistics Rows */}
                                {allStatistics
                                  .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : allStatistics.length)
                                  .map((statistic) => {
                                    // Split label to add info button after last word
                                    const labelWords = statistic.label.split(' ');
                                    const lastWord = labelWords[labelWords.length - 1];
                                    const otherWords = labelWords.slice(0, -1);
                                    
                                    return (
                                      <tr key={statistic.key} className="h-12 sm:h-16">
                                        <td 
                                          className="px-2 sm:px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                          style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 30,
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            hyphens: 'auto'
                                          }}
                                        >
                                          <span className={getStatisticLabelSizeClass(fontSize)}>
                                            {otherWords.length > 0 && `${otherWords.join(' ')} `}
                                            <span className="whitespace-nowrap">
                                              {lastWord}
                                              <IndicatorInfoButton
                                                indicatorKey={statistic.key}
                                                indicatorLabel={statistic.label}
                                                onIndicatorInfoClick={handleFundamentalsIndicatorInfoClick}
                                                size="small"
                                              />
                                            </span>
                                          </span>
                                        </td>
                                        {currentColumns.map((column, colIndex) => {
                                          const value = getFundamentalsValue(statistic.key, column);
                                          const previousValue = colIndex < currentColumns.length - 1 ? getFundamentalsValue(statistic.key, currentColumns[colIndex + 1]) : null;
                                          
                                          const arrowInfo = getFundamentalsArrowInfo(statistic.key, value, previousValue);
                                          const textColor = 'text-text-light-neutral dark:text-text-secondary';
                                          const tooltipText = getFundamentalsArrowTooltip(statistic.key, value, previousValue);
                                          
                                          return (
                                            <td
                                              key={colIndex}
                                              className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${textColor} bg-surface-light dark:bg-background-card`}
                                              style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}
                                            >
                                              <span className="inline-flex items-center gap-1">
                                                {formatFundamentalsValue(value, statistic.type || 'ratio', statistic.key)}
                                                {arrowInfo.direction === 'up' && (
                                                  <div className="relative group/arrow">
                                                    <UpArrowIcon 
                                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                                    />
                                                    {tooltipText && (
                                                      <div className={`absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary z-[60] font-sans`}>
                                                        {tooltipText}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                                {arrowInfo.direction === 'down' && (
                                                  <div className="relative group/arrow">
                                                    <DownArrowIcon 
                                                      className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                                    />
                                                    {tooltipText && (
                                                      <div className={`absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary z-[60] font-sans`}>
                                                        {tooltipText}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </span>
                                            </td>
                                          );
                                        })}
                                        <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </React.Fragment>
                            ));
                          }
                          
                          return null;
                        })()}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Status Bar */}
                  <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                    <div className="flex items-center gap-4">
                      {fundamentalsTickerLoading && (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                          <span>Loading...</span>
                        </div>
                      )}
                      {fundamentalsTickerLastUpdated && !fundamentalsTickerLoading && (
                        <span>Last updated: {fundamentalsTickerLastUpdated.toLocaleDateString()} {fundamentalsTickerLastUpdated.toLocaleTimeString()}</span>
                      )}
                    </div>
                  </div>
                    </>
                )}
                
                {/* Full Screen Overlay */}
                {isFundamentalsFullScreen && (
                  <div
                    className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div
                      ref={fundamentalsFullScreenTableRef}
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                    >
                        <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={embeddedIndicatorsColgroupStyle} />
                            {(fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns).map((_, index) => (
                              <col key={index} style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }} />
                            ))}
                          </colgroup>
                          
                          <thead className="sticky-header-border" style={{ position: 'sticky', top: -1, zIndex: 50 }}>
                            <tr>
                              <th 
                                className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                style={{ 
                                  position: 'sticky', 
                                  left: 0, 
                                  zIndex: 40
                                }}
                              >
                                <div className="flex items-center">
                                  <span>Indicators</span>
                                </div>
                              </th>
                              
                              {(fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns).map((column) => (
                                <th 
                                  key={column.label} 
                                  className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                  style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}
                                >
                                  {column.label}
                                </th>
                              ))}
                              
                              {/* Close button - sticky right */}
                              <th 
                                className="py-1 px-4 text-right bg-gray-50 dark:bg-background-card"
                                style={{ position: 'sticky', right: 0, zIndex: 40 }}
                              >
                                <button
                                  onClick={() => setIsFundamentalsFullScreen(false)}
                                  className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                  title="Close full screen"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </th>
                            </tr>
                          </thead>
                          
                          <tbody className="divide-y divide-gray-200 dark:divide-border-color">
                            {(() => {
                              const currentColumns = fundamentalsTimeframe === 'quarterly' ? fundamentalsQuarterlyColumns : fundamentalsAnnualColumns;
                              const allStatistics = fundamentalsTimeframe === 'quarterly' ? FUNDAMENTAL_STATISTICS : ANNUAL_FUNDAMENTAL_STATISTICS;
                              const baseCategoryHeaders = fundamentalsTimeframe === 'quarterly' ? FUNDAMENTAL_CATEGORY_HEADERS : ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS;
                              const currentCategoryHeaders = baseCategoryHeaders;
                              
                              if (currentCategoryHeaders.length > 0) {
                                return currentCategoryHeaders.map((categoryHeader, categoryIndex) => (
                                  <React.Fragment key={categoryHeader.label}>
                                    {categoryHeader && (
                                      <tr className="bg-gray-100 dark:bg-border-color">
                                        <td 
                                          className={`px-2 sm:px-4 py-2 text-left font-bold text-gray-800 dark:text-text-primary ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}
                                          style={{ 
                                            position: 'sticky', 
                                            left: 0, 
                                            zIndex: 20,
                                            ...embeddedIndicatorsColStyle,
                                          }}
                                        >
                                          <div 
                                            className="whitespace-nowrap"
                                            style={{ overflow: 'visible', position: 'relative' }}
                                          >
                                            <span className="inline">{categoryHeader.label}</span>
                                            <CategoryInfoButton
                                              categoryName={categoryHeader.label}
                                              onCategoryInfoClick={handleFundamentalsCategoryInfoClick}
                                            />
                                          </div>
                                        </td>
                                        {currentColumns.map((column) => (
                                          <td key={column.label} className={`px-2 sm:px-4 lg:px-6 py-2 text-left font-bold text-gray-800 dark:text-text-primary ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`} style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}>
                                          {/* Empty cell - category name only shown in first column */}
                                          </td>
                                        ))}
                                        <td className={`px-4 py-2 ${getTextSizeClass(fontSize)} font-bold text-gray-800 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                      </tr>
                                    )}
                                    
                                    {allStatistics
                                      .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : allStatistics.length)
                                      .map((statistic) => {
                                        // Split label to add info button after last word
                                        const labelWords = statistic.label.split(' ');
                                        const lastWord = labelWords[labelWords.length - 1];
                                        const otherWords = labelWords.slice(0, -1);
                                        
                                        return (
                                          <tr key={statistic.key} className="hover:bg-gray-50 dark:hover:bg-background-card transition-colors duration-200">
                                            <td 
                                              className={`px-2 sm:px-4 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}
                                              style={{ 
                                                position: 'sticky', 
                                                left: 0, 
                                                zIndex: 20,
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                hyphens: 'auto'
                                              }}
                                            >
                                              <span className={getStatisticLabelSizeClass(fontSize)}>
                                                {otherWords.length > 0 && `${otherWords.join(' ')} `}
                                                <span className="whitespace-nowrap">
                                                  {lastWord}
                                                  <IndicatorInfoButton
                                                    indicatorKey={statistic.key}
                                                    indicatorLabel={statistic.label}
                                                    onIndicatorInfoClick={handleFundamentalsIndicatorInfoClick}
                                                    size="small"
                                                  />
                                                </span>
                                              </span>
                                            </td>
                                            {currentColumns.map((column, colIndex) => {
                                              const value = getFundamentalsValue(statistic.key, column);
                                              const previousValue = colIndex < currentColumns.length - 1 ? getFundamentalsValue(statistic.key, currentColumns[colIndex + 1]) : null;
                                              
                                              const arrowInfo = getFundamentalsArrowInfo(statistic.key, value, previousValue);
                                              const textColor = 'text-text-light-neutral dark:text-text-secondary';
                                              const tooltipText = getFundamentalsArrowTooltip(statistic.key, value, previousValue);
                                              
                                              return (
                                                <td key={colIndex} className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`} style={{ width: `${fundamentalsDateColumnWidth}px`, minWidth: `${fundamentalsDateColumnWidth}px`, maxWidth: `${fundamentalsDateColumnWidth}px` }}>
                                                  <span className="inline-flex items-center gap-1">
                                                    {formatFundamentalsValue(value, statistic.type || 'ratio', statistic.key)}
                                                    {arrowInfo.direction === 'up' && (
                                                      <div className="relative group/arrow">
                                                        <UpArrowIcon 
                                                          className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                                        />
                                                        {tooltipText && (
                                                          <div className={`absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary z-[60] font-sans`}>
                                                            {tooltipText}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    {arrowInfo.direction === 'down' && (
                                                      <div className="relative group/arrow">
                                                        <DownArrowIcon 
                                                          className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
                                                        />
                                                        {tooltipText && (
                                                          <div className={`absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary z-[60] font-sans`}>
                                                            {tooltipText}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                  </span>
                                                </td>
                                              );
                                            })}
                                            <td className={`px-4 py-1 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}></td>
                                          </tr>
                                        );
                                      })}
                                  </React.Fragment>
                                ));
                              }
                              
                              return null;
                            })()}
                          </tbody>
                        </table>
                    </div>
                      
                      {/* Status Bar */}
                      <div className={`flex-shrink-0 px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                        <div className="flex items-center gap-4">
                          {fundamentalsTickerLoading && (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                              <span>Loading...</span>
                            </div>
                          )}
                          {fundamentalsTickerLastUpdated && !fundamentalsTickerLoading && (
                            <span>Last updated: {fundamentalsTickerLastUpdated.toLocaleDateString()} {fundamentalsTickerLastUpdated.toLocaleTimeString()}</span>
                          )}
                        </div>
                      </div>
                  </div>
                )}
                  </>
                ) : activeTab === 'analysts' && selectedTickerForAnalystViewsView ? (
                  <>
                    {/* Analysts Data Table */}
                {analystViewsTickerError ? (
                  <div className="p-8 text-center">
                    <p className="text-red-600 dark:text-red-400">{analystViewsTickerError}</p>
                  </div>
                ) : isAnalystViewsEtfOrEtv ? (
                  <div className="p-8 text-center">
                    <p className={`text-gray-600 dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                      No analyst estimates or recommendations are available for this ticker.
                    </p>
                  </div>
                ) : (
                  <>
                  <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
                    <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={embeddedIndicatorsColgroupStyle} />
                        {(analystViewsIsSyncing && (!analystViewsTickerData || !analystViewsTickerData.quarters || analystViewsTickerData.quarters.length === 0) ? Array.from({ length: 4 }) : analystViewsTickerData?.quarters || []).map((_, index) => (
                          <col key={index} style={{ width: '150px' }} />
                        ))}
                      </colgroup>
                      
                      <thead className="bg-gray-50 dark:bg-background-card border-t border-gray-200 dark:border-border-color sticky-header-border" style={{ position: 'sticky', top: -1, zIndex: 50 }}>
                        <tr>
                          <th 
                            className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 40,
                              ...embeddedIndicatorsColStyle,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                          >
                            INDICATORS
                          </th>
                          {analystViewsIsSyncing && (!analystViewsTickerData || !analystViewsTickerData.quarters || analystViewsTickerData.quarters.length === 0) ? (
                            Array.from({ length: 4 }).map((_, index) => (
                              <th
                                key={`loading-header-${index}`}
                                className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                              >
                                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-20 rounded"></div>
                              </th>
                            ))
                          ) : (
                            analystViewsTickerData?.quarters?.map((quarter, index) => (
                              <th
                                key={index}
                                className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                              >
                                <div className="flex flex-col items-start">
                                  <span>{index === 0 ? 'Latest' : formatQuarterHeader(quarter)}</span>
                                  {index === 0 && analystViewsLatestDate && (
                                    <span className={`${getSmallTextSizeClass(fontSize)} text-gray-500 dark:text-text-secondary font-normal mt-1`}>
                                      {formatAnalystViewsDate(analystViewsLatestDate)}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))
                          )}
                          
                          {/* Full screen button column */}
                          <th 
                            className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                            style={{ position: 'sticky', right: 0, zIndex: 40 }}
                          >
                            <button
                              onClick={() => setIsAnalystViewsFullScreen(true)}
                              className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                              title="Open in full screen"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      
                      <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                        {analystViewsIsSyncing && (!analystViewsTickerData || !analystViewsTickerData.quarters || analystViewsTickerData.quarters.length === 0) ? (
                          ANALYST_VIEWS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => {
                            const categoryStats = ANALYST_VIEWS_STATISTICS.slice(
                              categoryHeader.startIndex,
                              categoryIndex < ANALYST_VIEWS_CATEGORY_HEADERS.length - 1 ? ANALYST_VIEWS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : ANALYST_VIEWS_STATISTICS.length
                            );
                            
                            return (
                              <React.Fragment key={`loading-${categoryHeader.label}`}>
                                {/* Category Header Row */}
                                <tr className="bg-gray-100 dark:bg-border-color">
                                  <td
                                    className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                    style={{
                                      position: 'sticky',
                                      left: 0,
                                      zIndex: 30,
                                      ...embeddedIndicatorsColStyle,
                                    }}
                                  >
                                    <div 
                                      className="whitespace-nowrap"
                                      style={{ overflow: 'visible', position: 'relative' }}
                                    >
                                      <span className="inline">{categoryHeader.label}</span>
                                      <CategoryInfoButton
                                        categoryName={categoryHeader.label}
                                        onCategoryInfoClick={handleTechnicalsCategoryInfoClick}
                                      />
                                    </div>
                                  </td>
                                  {Array.from({ length: 4 }).map((_, colIndex) => (
                                    <td
                                      key={`loading-header-col-${colIndex}`}
                                      className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                    >
                                    </td>
                                  ))}
                                  <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                                  </td>
                                </tr>
                                
                                {/* Statistics Rows */}
                                {categoryStats.map((statistic) => (
                                  <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                                    <td 
                                      className="px-2 sm:px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                      style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 30,
                                        ...embeddedIndicatorsColStyle,
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        hyphens: 'auto'
                                      }}
                                    >
                                      <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                    </td>
                                    {Array.from({ length: 4 }).map((_, quarterIndex) => (
                                      <td key={`loading-quarter-${quarterIndex}`} className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} bg-surface-light dark:bg-background-card`}>
                                        <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                      </td>
                                    ))}
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          ANALYST_VIEWS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => (
                            <React.Fragment key={categoryHeader.label}>
                              {/* Category Header Row */}
                              <tr className="bg-gray-100 dark:bg-border-color">
                                <td
                                  className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                  style={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 30,
                                    ...embeddedIndicatorsColStyle,
                                  }}
                                >
                                  <div 
                                    className="whitespace-nowrap"
                                    style={{ overflow: 'visible', position: 'relative' }}
                                  >
                                    <span className="inline">{categoryHeader.label}</span>
                                    <CategoryInfoButton
                                      categoryName={categoryHeader.label}
                                      onCategoryInfoClick={handleAnalystViewsCategoryInfoClick}
                                    />
                                  </div>
                                </td>
                                {analystViewsTickerData?.quarters?.map((_, colIndex) => (
                                  <td
                                    key={colIndex}
                                    className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                  >
                                  </td>
                                ))}
                                <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                                </td>
                              </tr>
                              
                              {/* Statistics Rows */}
                              {ANALYST_VIEWS_STATISTICS
                                .slice(categoryHeader.startIndex, categoryIndex < ANALYST_VIEWS_CATEGORY_HEADERS.length - 1 ? ANALYST_VIEWS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : ANALYST_VIEWS_STATISTICS.length)
                                .map((statistic) => {
                                  return (
                                    <tr key={statistic.key} className="h-12 sm:h-16">
                                      <td 
                                        className="px-2 sm:px-4 text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                        style={{
                                          position: 'sticky',
                                          left: 0,
                                          zIndex: 30,
                                          ...embeddedIndicatorsColStyle,
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          hyphens: 'auto'
                                        }}
                                      >
                                        <div className={`flex items-start ${getTextSizeClass(fontSize)}`}>
                                          {statistic.label.split('\n').map((line, lineIndex) => {
                                            const words = line.split(' ');
                                            const lastWordIndex = words.length - 1;
                                            return (
                                              <span key={lineIndex} className={lineIndex > 0 ? 'block' : ''}>
                                                {words.map((word, wordIndex) => (
                                                  <span key={wordIndex}>
                                                    {wordIndex > 0 && ' '}
                                                    {wordIndex === lastWordIndex ? (
                                                      <span className="whitespace-nowrap">
                                                        {word}
                                                        {lineIndex === statistic.label.split('\n').length - 1 && wordIndex === lastWordIndex && (statistic.key === 'priceTargetDifference' || statistic.key === 'averageRecommendation') && (
                                                          <IndicatorInfoButton
                                                            indicatorKey={statistic.key}
                                                            indicatorLabel={statistic.label}
                                                            onIndicatorInfoClick={handleAnalystViewsIndicatorInfoClick}
                                                            size="small"
                                                          />
                                                        )}
                                                      </span>
                                                    ) : (
                                                      word
                                                    )}
                                                  </span>
                                                ))}
                                                {lineIndex < statistic.label.split('\n').length - 1 && <br />}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </td>
                                      {analystViewsTickerData?.quarters?.map((quarter, quarterIndex) => {
                                        const quarterData = getAnalystViewsQuarterDataForView(quarter, quarterIndex);
                                        const value = quarterData[statistic.key];
                                        const formattedValue = formatAnalystViewsValue(value, statistic.key);
                                        const color = getAnalystViewsTickerStatisticColor(statistic.key, value);
                                        
                                        return (
                                          <td 
                                            key={quarterIndex} 
                                            className={`px-1 sm:px-2 md:px-4 lg:px-6 py-1 sm:py-2 text-left font-semibold ${getTextSizeClass(fontSize)} ${color}`}
                                          >
                                            {analystViewsTickerLoading || analystViewsIsSyncing ? (
                                              <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                            ) : (
                                              formattedValue
                                            )}
                                          </td>
                                        );
                                      })}
                                      
                                      {/* Empty cell for full screen button column */}
                                      <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary`}>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Status Bar */}
                  <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                    <div className="flex items-center gap-4">
                      {analystViewsTickerLoading && (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                          <span>Loading...</span>
                        </div>
                      )}
                      {analystViewsTickerLastUpdated && !analystViewsTickerLoading && (
                        <span>Last updated: {analystViewsTickerLastUpdated.toLocaleDateString()} {analystViewsTickerLastUpdated.toLocaleTimeString()}</span>
                      )}
                    </div>
                  </div>
                  </>
                )}
                
                {/* Full Screen Overlay */}
                {isAnalystViewsFullScreen && (
                  <div
                    className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div
                      ref={analystViewsFullScreenTableRef}
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                    >
                        {isAnalystViewsEtfOrEtv ? (
                          <div className="h-full flex items-center justify-center">
                            <p className={`text-gray-600 dark:text-text-secondary ${getTextSizeClass(fontSize)}`}>
                              No analyst estimates or recommendations are available for this ticker.
                            </p>
                          </div>
                        ) : (
                          <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={embeddedIndicatorsColgroupStyle} />
                              {analystViewsTickerData?.quarters?.map((_, index) => (
                                <col key={index} style={{ width: '150px' }} />
                              )) || []}
                            </colgroup>
                            
                            <thead className="sticky-header-border" style={{ position: 'sticky', top: -1, zIndex: 50 }}>
                              <tr>
                                <th 
                                  className={`py-1 px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                  style={{ 
                                    ...embeddedIndicatorsColStyle,
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 40
                                  }}
                                >
                                  <div className="flex items-center">
                                    <span>INDICATORS</span>
                                  </div>
                                </th>
                                
                                {analystViewsTickerData?.quarters?.map((quarter, index) => (
                                  <th
                                    key={index}
                                    className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                  >
                                    <div className="flex flex-col items-start">
                                      <span>{index === 0 ? 'Latest' : formatQuarterHeader(quarter)}</span>
                                      {index === 0 && analystViewsLatestDate && (
                                        <span className={`${getSmallTextSizeClass(fontSize)} text-gray-500 dark:text-text-secondary font-normal mt-1`}>
                                          {formatAnalystViewsDate(analystViewsLatestDate)}
                                        </span>
                                      )}
                                    </div>
                                  </th>
                                ))}
                                
                                <th 
                                  className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                                  style={{ position: 'sticky', right: 0, zIndex: 40 }}
                                >
                                  <button
                                    onClick={() => setIsAnalystViewsFullScreen(false)}
                                    className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                    title="Close full screen"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </th>
                              </tr>
                            </thead>
                            
                            <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                              {ANALYST_VIEWS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => (
                                <React.Fragment key={categoryHeader.label}>
                                  {/* Category Header Row */}
                                  <tr className="bg-gray-100 dark:bg-border-color">
                                    <td
                                      className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                      style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 30,
                                        ...embeddedIndicatorsColStyle,
                                      }}
                                    >
                                      <div 
                                        className="whitespace-nowrap"
                                        style={{ overflow: 'visible', position: 'relative' }}
                                      >
                                        <span className="inline">{categoryHeader.label}</span>
                                        <CategoryInfoButton
                                          categoryName={categoryHeader.label}
                                          onCategoryInfoClick={handleAnalystViewsCategoryInfoClick}
                                        />
                                      </div>
                                    </td>
                                    {analystViewsTickerData?.quarters?.map((_, colIndex) => (
                                      <td
                                        key={colIndex}
                                        className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                      >
                                      </td>
                                    ))}
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                                    </td>
                                  </tr>
                                  
                                  {/* Statistics Rows */}
                                  {ANALYST_VIEWS_STATISTICS
                                    .slice(categoryHeader.startIndex, categoryIndex < ANALYST_VIEWS_CATEGORY_HEADERS.length - 1 ? ANALYST_VIEWS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : ANALYST_VIEWS_STATISTICS.length)
                                    .map((statistic) => {
                                      return (
                                        <tr key={statistic.key} className="h-10">
                                          <td 
                                            className={`px-2 sm:px-4 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}
                                            style={{
                                              position: 'sticky',
                                              left: 0,
                                              zIndex: 20,
                                    ...embeddedIndicatorsColStyle,
                                              wordWrap: 'break-word',
                                              overflowWrap: 'break-word',
                                              hyphens: 'auto'
                                            }}
                                          >
                                            <div className={`flex items-start ${getTextSizeClass(fontSize)}`}>
                                              {statistic.label.split('\n').map((line, lineIndex) => {
                                                const words = line.split(' ');
                                                const lastWordIndex = words.length - 1;
                                                return (
                                                  <span key={lineIndex} className={lineIndex > 0 ? 'block' : ''}>
                                                    {words.map((word, wordIndex) => (
                                                      <span key={wordIndex}>
                                                        {wordIndex > 0 && ' '}
                                                        {wordIndex === lastWordIndex ? (
                                                          <span className="whitespace-nowrap">
                                                            {word}
                                                            {lineIndex === statistic.label.split('\n').length - 1 && wordIndex === lastWordIndex && (statistic.key === 'priceTargetDifference' || statistic.key === 'averageRecommendation') && (
                                                              <IndicatorInfoButton
                                                                indicatorKey={statistic.key}
                                                                indicatorLabel={statistic.label}
                                                                onIndicatorInfoClick={handleAnalystViewsIndicatorInfoClick}
                                                                size="small"
                                                              />
                                                            )}
                                                          </span>
                                                        ) : (
                                                          word
                                                        )}
                                                      </span>
                                                    ))}
                                                    {lineIndex < statistic.label.split('\n').length - 1 && <br />}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </td>
                                          {analystViewsTickerData?.quarters?.map((quarter, quarterIndex) => {
                                            const quarterData = getAnalystViewsQuarterDataForView(quarter, quarterIndex);
                                            const value = quarterData[statistic.key];
                                            const formattedValue = formatAnalystViewsValue(value, statistic.key);
                                            const color = getAnalystViewsTickerStatisticColor(statistic.key, value);
                                            
                                            return (
                                              <td key={quarterIndex} className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold bg-white dark:bg-background ${color}`}>
                                                {analystViewsTickerLoading || analystViewsIsSyncing ? (
                                                  <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                                ) : (
                                                  formattedValue
                                                )}
                                              </td>
                                            );
                                          })}
                                          
                                          <td className={`px-4 py-1 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-white dark:bg-background`}>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  </div>
                )}
                  </>
                ) : activeTab === 'proscons' && selectedTickerForProsConsView ? (
                  <>
                    {prosConsTickerError ? (
                      <div className="p-8 text-center">
                        <p className="text-red-600 dark:text-red-400">{prosConsTickerError}</p>
                      </div>
                    ) : (
                      <>
                        <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]" ref={prosConsTableScrollRef}>
                          {renderProsConsEmbeddedTable(false)}
                        </div>
                        {renderProsConsEmbeddedFooter()}
                      </>
                    )}
                    {isProsConsFullScreen && (
                      <div
                        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                        role="dialog"
                        aria-modal="true"
                      >
                        <div
                          ref={prosConsFullScreenTableRef}
                          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                        >
                          {renderProsConsEmbeddedTable(true)}
                        </div>
                      </div>
                    )}
                  </>
                ) : activeTab === 'insiders' && selectedTickerForInsidersView ? (
                  <>
                    {/* Insiders Data Table */}
                {insidersTickerError ? (
                  <div className="p-8 text-center">
                    <p className="text-red-600 dark:text-red-400">{insidersTickerError}</p>
                  </div>
                ) : (
                  <>
                    <div className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
                      <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ 
                            width: `${insidersStatisticsColumnWidth}px`, 
                            minWidth: '120px', 
                            maxWidth: '180px'
                          }} />
                          <col style={{ width: '200px', minWidth: '150px' }} />
                          <col style={{ width: '60px' }} />
                        </colgroup>
                        
                        <thead 
                          className="bg-gray-50 dark:bg-background-card sticky-header-border" 
                          style={{ 
                            position: 'sticky', 
                            top: 0, 
                            zIndex: 50
                          }}
                        >
                          <tr>
                            <th 
                              className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 40
                              }}
                            >
                              <div className="flex items-center">
                                <span>INDICATORS</span>
                              </div>
                            </th>
                            <th 
                              className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                            >
                              LATEST
                            </th>
                            <th 
                              className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                              style={{ position: 'sticky', right: 0, zIndex: 40 }}
                            >
                              <button
                                onClick={() => setIsInsidersFullScreen(true)}
                                className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                title="Open in full screen"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                              </button>
                            </th>
                          </tr>
                        </thead>
                        
                        <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                          {insidersTickerLoading || insidersIsSyncing ? (
                            INSIDER_TRANSACTIONS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => {
                              const categoryStats = INSIDER_TRANSACTIONS_STATISTICS.slice(
                                categoryHeader.startIndex,
                                categoryIndex < INSIDER_TRANSACTIONS_CATEGORY_HEADERS.length - 1 ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : INSIDER_TRANSACTIONS_STATISTICS.length
                              );
                              
                              return (
                                <React.Fragment key={`loading-${categoryHeader.label}`}>
                                  <tr className="bg-gray-100 dark:bg-border-color">
                                    <td
                                      className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                      style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 30,
                                        width: `${insidersStatisticsColumnWidth}px`,
                                        maxWidth: `${insidersStatisticsColumnWidth}px`
                                      }}
                                    >
                                      <div className="whitespace-nowrap">
                                        <span className="inline">{categoryHeader.label}</span>
                                      </div>
                                    </td>
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                  </tr>
                                  {categoryStats.map((statistic) => (
                                    <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                                      <td 
                                        className="px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                        style={{
                                          position: 'sticky',
                                          left: 0,
                                          zIndex: 30
                                        }}
                                      >
                                        <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                      </td>
                                      <td className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}>
                                        <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                      </td>
                                      <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })
                          ) : insidersTickerData ? (
                            INSIDER_TRANSACTIONS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => {
                              const categoryStats = INSIDER_TRANSACTIONS_STATISTICS.slice(
                                categoryHeader.startIndex,
                                categoryIndex < INSIDER_TRANSACTIONS_CATEGORY_HEADERS.length - 1 ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : INSIDER_TRANSACTIONS_STATISTICS.length
                              );
                              
                              return (
                                <React.Fragment key={categoryHeader.label}>
                                  <tr className="bg-gray-100 dark:bg-border-color">
                                    <td
                                      className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                      style={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 30,
                                        width: `${insidersStatisticsColumnWidth}px`,
                                        maxWidth: `${insidersStatisticsColumnWidth}px`
                                      }}
                                    >
                                      <div className="whitespace-nowrap" style={{ overflow: 'visible', position: 'relative' }}>
                                        <span className="inline">{categoryHeader.label}</span>
                                        <button
                                          onClick={() => handleInsidersCategoryInfoClick(categoryHeader.label)}
                                          className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                          title={`Learn more about ${categoryHeader.label}`}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                            <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                          </svg>
                                        </button>
                                      </div>
                                    </td>
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                    <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                  </tr>
                                  {categoryStats.map((statistic) => {
                                    const value = getInsidersTickerValue(statistic.key);
                                    const formattedValue = formatInsidersTickerStatisticValue(statistic.key);
                                    const color = getInsidersTickerStatisticColor(statistic.key);
                                    const isTransactionDetail = statistic.key.startsWith('recentTransaction') && typeof formattedValue === 'object' && formattedValue !== null;
                                    
                                    return (
                                      <tr key={statistic.key} className="h-10">
                                        <td 
                                          className={`px-4 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                                          style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 20,
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            hyphens: 'auto'
                                          }}
                                        >
                                          <span className={getStatisticLabelSizeClass(fontSize)}>
                                            {statistic.label}
                                          </span>
                                        </td>
                                        <td 
                                          className={`px-2 sm:px-4 lg:px-6 py-1 text-left font-semibold ${getTextSizeClass(fontSize)} ${color} bg-surface-light dark:bg-background-card ${
                                            isTransactionDetail ? 'align-top' : ''
                                          }`}
                                          style={{
                                            ...(isTransactionDetail ? { paddingTop: '0.25rem', paddingBottom: '0.25rem' } : {})
                                          }}
                                        >
                                          {formattedValue}
                                        </td>
                                        <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card"></td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
                      <div className="flex items-center gap-4">
                        {insidersTickerLoading && (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                            <span>Loading...</span>
                          </div>
                        )}
                        {insidersTickerLastUpdated && !insidersTickerLoading && (
                          <span>Last updated: {insidersTickerLastUpdated.toLocaleDateString()} {insidersTickerLastUpdated.toLocaleTimeString()}</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {isInsidersFullScreen && (
                  <div
                    className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div
                      ref={insidersFullScreenTableRef}
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain"
                    >
                        <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ 
                              width: `${insidersStatisticsColumnWidth}px`, 
                              minWidth: '120px', 
                              maxWidth: '180px'
                            }} />
                            <col style={{ width: '200px', minWidth: '150px' }} />
                            <col style={{ width: '60px' }} />
                          </colgroup>
                          
                          <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                            <tr>
                              <th 
                                className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                                style={{ 
                                  width: `${insidersStatisticsColumnWidth}px`,
                                  minWidth: '120px',
                                  maxWidth: '180px',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 40
                                }}
                              >
                                <div className="flex items-center">
                                  <span>INDICATORS</span>
                                </div>
                              </th>
                              <th 
                                className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                              >
                                LATEST
                              </th>
                              
                              <th 
                                className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                                style={{ position: 'sticky', right: 0, zIndex: 40 }}
                              >
                                <button
                                  onClick={() => setIsInsidersFullScreen(false)}
                                  className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                                  title="Close full screen"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </th>
                            </tr>
                          </thead>
                          
                          <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                            {insidersTickerLoading || insidersIsSyncing ? (
                              INSIDER_TRANSACTIONS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => {
                                const categoryStats = INSIDER_TRANSACTIONS_STATISTICS.slice(
                                  categoryHeader.startIndex,
                                  categoryIndex < INSIDER_TRANSACTIONS_CATEGORY_HEADERS.length - 1 ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : INSIDER_TRANSACTIONS_STATISTICS.length
                                );
                                
                                return (
                                  <React.Fragment key={`loading-${categoryHeader.label}`}>
                                    <tr className="bg-gray-100 dark:bg-border-color">
                                      <td
                                        className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                        style={{
                                          position: 'sticky',
                                          left: 0,
                                          zIndex: 30,
                                          width: `${insidersStatisticsColumnWidth}px`,
                                          maxWidth: `${insidersStatisticsColumnWidth}px`
                                        }}
                                      >
                                        <div className="whitespace-nowrap">
                                          <span className="inline">{categoryHeader.label}</span>
                                        </div>
                                      </td>
                                      <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                      <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                    </tr>
                                    {categoryStats.map((statistic) => (
                                      <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                                        <td 
                                          className="px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                          style={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 30
                                          }}
                                        >
                                          <span className={getStatisticLabelSizeClass(fontSize)}>{statistic.label}</span>
                                        </td>
                                        <td className={`px-2 sm:px-4 lg:px-6 py-1 text-left ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}>
                                          <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>
                                        </td>
                                        <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card"></td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })
                            ) : insidersTickerData ? (
                              INSIDER_TRANSACTIONS_CATEGORY_HEADERS.map((categoryHeader, categoryIndex) => {
                                const categoryStats = INSIDER_TRANSACTIONS_STATISTICS.slice(
                                  categoryHeader.startIndex,
                                  categoryIndex < INSIDER_TRANSACTIONS_CATEGORY_HEADERS.length - 1 ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS[categoryIndex + 1].startIndex : INSIDER_TRANSACTIONS_STATISTICS.length
                                );
                                
                                return (
                                  <React.Fragment key={categoryHeader.label}>
                                    <tr className="bg-gray-100 dark:bg-border-color">
                                      <td
                                        className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                        style={{
                                          position: 'sticky',
                                          left: 0,
                                          zIndex: 30,
                                          width: `${insidersStatisticsColumnWidth}px`,
                                          maxWidth: `${insidersStatisticsColumnWidth}px`
                                        }}
                                      >
                                        <div className="whitespace-nowrap" style={{ overflow: 'visible', position: 'relative' }}>
                                          <span className="inline">{categoryHeader.label}</span>
                                          <button
                                            onClick={() => handleInsidersCategoryInfoClick(categoryHeader.label)}
                                            className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                            title={`Learn more about ${categoryHeader.label}`}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                              <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                              <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                          </button>
                                        </div>
                                      </td>
                                      <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                      <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                                    </tr>
                                    {categoryStats.map((statistic) => {
                                      const value = getInsidersTickerValue(statistic.key);
                                      const formattedValue = formatInsidersTickerStatisticValue(statistic.key);
                                      const color = getInsidersTickerStatisticColor(statistic.key);
                                      const isTransactionDetail = statistic.key.startsWith('recentTransaction') && typeof formattedValue === 'object' && formattedValue !== null;
                                      
                                      return (
                                        <tr key={statistic.key} className="h-10">
                                          <td 
                                            className={`px-4 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                                            style={{
                                              position: 'sticky',
                                              left: 0,
                                              zIndex: 20,
                                              wordWrap: 'break-word',
                                              overflowWrap: 'break-word',
                                              hyphens: 'auto'
                                            }}
                                          >
                                            <span className={getStatisticLabelSizeClass(fontSize)}>
                                              {statistic.label}
                                            </span>
                                          </td>
                                          <td 
                                            className={`px-2 sm:px-4 lg:px-6 py-1 text-left font-semibold ${getTextSizeClass(fontSize)} ${color} bg-surface-light dark:bg-background-card ${
                                              isTransactionDetail ? 'align-top' : ''
                                            }`}
                                            style={{
                                              ...(isTransactionDetail ? { paddingTop: '0.25rem', paddingBottom: '0.25rem' } : {})
                                            }}
                                          >
                                            {formattedValue}
                                          </td>
                                          <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card"></td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })
                            ) : null}
                          </tbody>
                        </table>
                    </div>
                  </div>
                )}
                  </>
                ) : null}
              </div>
            ) : (
              <StockTableLayout
              tickerList={tickerList}
              tickerData={
                activeTab === 'daily' ? (snapshotData || []) :
                activeTab === 'fundamentals' ? tickerList.map(symbol => getTickerData(symbol)).filter(Boolean) as TickerDisplayData[] :
                tickerList.map(symbol => getTickerData(symbol)).filter(Boolean) as TickerDisplayData[]
              }
              loading={activeTab === 'daily' ? snapshotLoading : loading}
              error={activeTab === 'daily' ? (snapshotError || null) : error}
              lastUpdated={activeTab === 'daily' ? snapshotLastUpdated : lastUpdated}
              isEditingTickers={false}
              isPublicList={true}
              hiddenStatistics={hiddenStatistics}
              draggedTicker={null}
              dragOverTicker={null}
              droppedTicker={null}
              statistics={
                activeTab === 'daily' ? (visibleStatistics || DAILY_STATISTICS) :
                activeTab === 'performance' ? PERFORMANCE_STATISTICS :
                activeTab === 'fundamentals' ? (timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_STATISTICS : FUNDAMENTAL_STATISTICS) :
                activeTab === 'proscons' ? PROS_CONS_STATISTICS :
                activeTab === 'analysts' ? ANALYST_VIEWS_STATISTICS :
                activeTab === 'insiders' ? INSIDER_TRANSACTIONS_STATISTICS :
                TECHNICAL_STATISTICS
              }
              categoryHeaders={
                activeTab === 'fundamentals' ? (timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS : FUNDAMENTAL_CATEGORY_HEADERS) :
                activeTab === 'proscons' ? [] :
                activeTab === 'analysts' ? ANALYST_VIEWS_CATEGORY_HEADERS :
                activeTab === 'insiders' ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS :
                activeTab === 'daily' ? undefined :
                activeTab === 'performance' ? PERFORMANCE_CATEGORY_HEADERS :
                CATEGORY_HEADERS
              }
              getTickerData={getTickerData}
              formatStatisticValue={formatStatisticValue}
              getStatisticColor={getStatisticColor}
              onRefetch={handleRefetch}
              onTickerClick={handleTickerClick}
              onRemoveTicker={() => {}}
              onDragStart={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onDragEnd={() => {}}
              onHideStatistic={() => {}}
              onShowStatistic={() => {}}
              onCategoryInfoClick={
                activeTab === 'technicals' ? handleTechnicalsCategoryInfoClick :
                activeTab === 'fundamentals' ? handleFundamentalsCategoryInfoClick :
                activeTab === 'analysts' ? handleAnalystViewsCategoryInfoClick :
                undefined
              }
              onIndicatorInfoClick={
                activeTab === 'daily' ? handleDailyIndicatorInfoClick :
                activeTab === 'fundamentals' ? handleFundamentalsIndicatorInfoClick :
                activeTab === 'analysts' ? handleAnalystViewsIndicatorInfoClick :
                undefined
              }
              indicatorKeysWithInfo={
                activeTab === 'daily' ? ['volume'] :
                activeTab === 'analysts' ? ['priceTargetDifference', 'averageRecommendation'] :
                undefined
              }
              lockedStatistics={[]}
              proOnlyStatistics={[]}
              lockedTickers={[]}
              listName="Featured Stocks"
              onStartEdit={() => {}}
              onCancelEdit={() => {}}
              onDoneEdit={() => {}}
              onAddTicker={() => {}}
              onSave={() => {}}
              tabs={tabs}
              timeframeToggle={timeframeToggle}
              showRefreshButton={false}
              showDelayedPricesText={activeTab === 'daily'}
              showLastUpdatedDate={activeTab !== 'daily'}
              tickerColumnWidth={activeTab === 'proscons' ? "216px" : undefined}
              useListDailySmallScreenColumnWidth={
                activeTab === 'technicals' ||
                activeTab === 'fundamentals' ||
                activeTab === 'proscons' ||
                activeTab === 'analysts' ||
                activeTab === 'insiders'
              }
              hideHeader={true}
              maxHeight="90vh"
            />
            )}
          </div>
        </section>
      </main>

      <Footer />
      
      {/* Category Info Modal for Technicals View */}
      <CategoryInfoModal
        isOpen={isTechnicalsModalOpen}
        onClose={handleCloseTechnicalsModal}
        categoryName={selectedTechnicalsCategory}
      />
      
      {/* Category Info Modal for Fundamentals View */}
      <CategoryInfoModal
        isOpen={isFundamentalsModalOpen}
        onClose={handleCloseFundamentalsModal}
        categoryName={selectedFundamentalsCategory}
      />
      
      {/* Indicator Info Modal for Fundamentals View */}
      <IndicatorInfoModal
        isOpen={isFundamentalsIndicatorModalOpen}
        onClose={handleCloseFundamentalsIndicatorModal}
        indicatorKey={selectedFundamentalsIndicatorKey}
      />
      
      {/* Category Info Modal for Analysts View */}
      <CategoryInfoModal
        isOpen={isAnalystViewsModalOpen}
        onClose={handleCloseAnalystViewsModal}
        categoryName={selectedAnalystViewsCategory}
      />
      
      {/* Category Info Modal for Insiders View */}
      <CategoryInfoModal
        isOpen={isInsidersCategoryModalOpen}
        onClose={handleCloseInsidersModal}
        categoryName={selectedInsidersCategory}
      />
      
      {/* Indicator Info Modal for Analysts View */}
      <IndicatorInfoModal
        isOpen={isAnalystViewsIndicatorModalOpen}
        onClose={handleCloseAnalystViewsIndicatorModal}
        indicatorKey={selectedAnalystViewsIndicatorKey}
      />
      
      {/* Indicator Info Modal for Daily View */}
      <IndicatorInfoModal
        isOpen={isDailyIndicatorModalOpen}
        onClose={handleCloseDailyIndicatorModal}
        indicatorKey={selectedDailyIndicatorKey}
      />
    </div>
    </>
  );
};

export default Landing;

